import type {
  Backrest,
  BenchTier as BenchLeg,
  GlassSegment,
  HeaterWall,
  InnerWall,
  ProductInstance,
  SaunaDoor,
  SculptedBench,
  SculptEnd,
  ShapeRelation,
  Vec2,
} from '../shared/types';
import { skirtingOf } from '../shared/benchPresets';
import { wallThicknessOf } from './dent';
import { roomDoors } from './door';
import {
  lightsOnHostSplit,
  lightsOnWallRemoved,
  lightsOnWallSplit,
  remintLights,
} from './lightLife';
import {
  autoTierRank,
  benchOverlap,
  benchSlatSpans,
  type BenchTier,
  cumulativeTuck,
} from './benches';
import {
  benchThickness,
  doorTop,
  glassPaneBase,
  glassPaneTop,
  openingArchitrave,
  openingSill,
  roomBase,
} from './heights';
import {
  CUT_FACE_TOL,
  CUT_SQUARE,
  cutBand,
  type InWallPart,
} from './wallCuts';
import {
  arcPoints,
  cornerFillet,
  pointInPolygon,
} from './geometry';
import { profileStep } from './benchProfile';
import { polygonCentroid } from './flooring';

/** Glass pane thickness in mm (mirrors GLASS_THICKNESS in glass.ts). */
const GLASS_THICKNESS = 10;
const EPS = 1e-3;

/** Whether an instance is a polygonal (free-form) room. */
export function isPolyRoom(inst: ProductInstance): boolean {
  return !!inst.polygon && inst.polygon.length >= 3;
}

/** Twice the signed area of a polygon; positive when wound counter-clockwise. */
export function polygonArea(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a;
}

/** Return the polygon wound counter-clockwise (interior on the left of each edge). */
export function toCCW(poly: Vec2[]): Vec2[] {
  return polygonArea(poly) < 0 ? [...poly].reverse() : poly.map((p) => ({ x: p.x, y: p.y }));
}

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const mul = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
const len2 = (a: Vec2): number => Math.hypot(a.x, a.y);
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Intersect line (p1 + t·d1) with line (p2 + s·d2); midpoint fallback if parallel. */
function lineIntersect(p1: Vec2, d1: Vec2, p2: Vec2, d2: Vec2): Vec2 {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 1e-9) return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/** The mitre at a vertex of two offset lines given by their START points: the
 *  previous edge's (`pPrev`) and this edge's (`pV`, the vertex's own offset). Where
 *  the two run straight on — a jamb, or any in-line vertex — the vertex's offset is
 *  the answer; `lineIntersect`'s parallel fallback would give the midpoint of the
 *  previous edge, which moved a jamb half a segment back along its wall. */
export function mitreAt(pPrev: Vec2, dPrev: Vec2, pV: Vec2, dV: Vec2): Vec2 {
  if (Math.abs(dPrev.x * dV.y - dPrev.y * dV.x) < 1e-9) return { x: pV.x, y: pV.y };
  return lineIntersect(pPrev, dPrev, pV, dV);
}

export interface PolyEdge {
  /** Outer endpoints (the drawn polyline vertices). */
  a: Vec2;
  b: Vec2;
  /** Inner (room-side) endpoints, mitred so adjacent walls join seamlessly. */
  ia: Vec2;
  ib: Vec2;
  /** Unit direction a→b. */
  dir: Vec2;
  /** Unit inward normal (toward the room interior). */
  normal: Vec2;
  /** Edge length (mm). */
  len: number;
}

/**
 * The room's wall edges, each carrying its outer endpoints (on the drawn
 * perimeter) and inner endpoints offset inward by the wall thickness with
 * mitred corners, so the walls meet seamlessly at every vertex.
 */
export function polyEdges(inst: ProductInstance, extraInset?: (edge: number) => number): PolyEdge[] {
  if (!isPolyRoom(inst)) return [];
  // Keep the polygon in its stored (draw) order so vertex indices are stable;
  // pick the inward normal from the winding so walls always offset into the room.
  const poly = inst.polygon!;
  const n = poly.length;
  const ccw = polygonArea(poly) > 0;
  const wall = wallThicknessOf(inst);
  const dir: Vec2[] = [];
  const normal: Vec2[] = [];
  const length: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = poly[i], b = poly[(i + 1) % n];
    const d = sub(b, a);
    const L = len2(d) || 1;
    const u = { x: d.x / L, y: d.y / L };
    dir.push(u);
    // Inward normal: the left normal for a CCW polygon, the right normal for CW.
    normal.push(ccw ? { x: -u.y, y: u.x } : { x: u.y, y: -u.x });
    length.push(L);
  }
  // Inner vertex i = intersection of edges (i-1) and i, both shifted inward.
  // `extraInset` pushes individual edges further in (a hidden heater wall on that
  // edge); the intersection then mitres the pushed faces automatically, whether
  // the neighbour is pushed or not.
  const insetOf = (i: number): number => wall + (extraInset ? extraInset(i) : 0);
  const inner: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const prev = (i - 1 + n) % n;
    const p1 = add(poly[i], mul(normal[prev], insetOf(prev)));
    const p2 = add(poly[i], mul(normal[i], insetOf(i)));
    inner.push(lineIntersect(p1, dir[prev], p2, dir[i]));
  }
  const edges: PolyEdge[] = [];
  for (let i = 0; i < n; i++) {
    edges.push({
      a: poly[i],
      b: poly[(i + 1) % n],
      ia: inner[i],
      ib: inner[(i + 1) % n],
      dir: dir[i],
      normal: normal[i],
      len: length[i],
    });
  }
  return edges;
}

// ---- Walls vs segments ---------------------------------------------------
// A pane or opening owns a whole polygon edge ("segment"): its two ends are JAMB
// vertices (`polygonJoints`) on a straight wall. So a room is numbered two ways:
// by SEGMENT (polygon edges — glass, openings, doors, wall bands, the outer
// dimension chain, the solver) and by WALL (corner to corner — benches, heater
// walls, partitions, interior dimensions). `wallView` is the one bridge.

/** Whether vertex `v` is a jamb rather than a corner. */
export function isJoint(inst: ProductInstance, v: number): boolean {
  return !!inst.polygonJoints?.[v];
}

/** Whether the room has any jamb vertex (and still at least three corners). */
export function hasJoints(inst: ProductInstance): boolean {
  const j = inst.polygonJoints, n = inst.polygon?.length ?? 0;
  if (!j || n < 3) return false;
  let any = false, corners = 0;
  for (let v = 0; v < n; v++) { if (j[v]) any = true; else corners++; }
  return any && corners >= 3;
}

/** One wall: the segments between two consecutive corners. `starts[k]` is where
 *  segment `segs[k]` begins, measured along the wall from corner `a`. */
export interface WallRun { a: number; b: number; segs: number[]; starts: number[]; len: number; }

/** The room's walls in wall order. With no jambs, wall `i` is segment `i`. */
export function wallRuns(inst: ProductInstance): WallRun[] {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return [];
  const n = poly.length;
  const segLen = (i: number): number => len2(sub(poly[(i + 1) % n], poly[i]));
  if (!hasJoints(inst)) return poly.map((_, i) => ({ a: i, b: (i + 1) % n, segs: [i], starts: [0], len: segLen(i) }));
  const corners: number[] = [];
  for (let v = 0; v < n; v++) if (!isJoint(inst, v)) corners.push(v);
  return corners.map((a, k) => {
    const b = corners[(k + 1) % corners.length];
    const segs: number[] = [], starts: number[] = [];
    let at = 0;
    for (let v = a; ; v = (v + 1) % n) {
      segs.push(v); starts.push(at);
      at += segLen(v);
      if ((v + 1) % n === b) break;
    }
    // The wall's length is corner to corner. The segments sum to the same while
    // the jambs sit on the line between the corners — which the solver keeps them
    // to — but a candidate reshape can fold them back, and then only this is true.
    return { a, b, segs, starts, len: len2(sub(poly[b], poly[a])) };
  });
}

/** The wall segment `seg` belongs to, and where along that wall it starts. */
export function segToWall(inst: ProductInstance, seg: number): { wall: number; offset: number } | null {
  const runs = wallRuns(inst);
  for (let w = 0; w < runs.length; w++) {
    const k = runs[w].segs.indexOf(seg);
    if (k >= 0) return { wall: w, offset: runs[w].starts[k] };
  }
  return null;
}

/** The segment holding station `s` along wall `wall`, and the station within it.
 *  A station exactly on a jamb goes to the segment that STARTS there, unless
 *  `atEnd` asks for the one that ends there. */
export function wallToSeg(inst: ProductInstance, wall: number, s: number, atEnd = false): { seg: number; s: number } | null {
  const run = wallRuns(inst)[wall];
  if (!run) return null;
  const poly = inst.polygon!, n = poly.length;
  for (let k = 0; k < run.segs.length; k++) {
    const seg = run.segs[k], s0 = run.starts[k];
    const s1 = s0 + len2(sub(poly[(seg + 1) % n], poly[seg]));
    const last = k === run.segs.length - 1;
    if (last || (atEnd ? s <= s1 + EPS : s < s1 - EPS)) return { seg, s: clamp(s - s0, 0, s1 - s0) };
  }
  return null;
}

/**
 * The room as the WALL-numbered features see it: the jamb vertices dropped, so
 * edge `i` of the view is wall `i`. Benches, their corner joins, heater walls and
 * partitions are passed through by reference (they are stored by wall already),
 * while outer doors, panes and openings are converted COPIES re-based onto their
 * wall — read-only, for the wall-numbered rules that need to see them (bench
 * carving, corner radii, the heater-wall/door exclusion). Anything that changes
 * the room must write to the real instance, never to a view.
 *
 * A room with no jamb IS its own view (the same object), so every wall-numbered
 * function can call this unconditionally at no cost.
 */
export function wallView(inst: ProductInstance): ProductInstance {
  if (!hasJoints(inst)) return inst;
  const runs = wallRuns(inst);
  const poly = inst.polygon!;
  const wallOf = new Map<number, { wall: number; offset: number }>();
  runs.forEach((r, w) => r.segs.forEach((s, k) => wallOf.set(s, { wall: w, offset: r.starts[k] })));
  const pick = <T>(arr: T[] | undefined): T[] | undefined => (arr ? runs.map((r) => arr[r.a]) : undefined);
  const rebase = (g: GlassSegment): GlassSegment => {
    if (g.innerWallId != null || g.edge == null) return g;
    const at = wallOf.get(g.edge);
    return at ? { ...g, edge: at.wall, start: g.start + at.offset, end: g.end + at.offset } : g;
  };
  const doors = roomDoors(inst).map((d) => {
    if (d.innerWallId != null) return d;
    const e = d.edge ?? 0, at = wallOf.get(e);
    if (!at) return d;
    const segLen = len2(sub(poly[(e + 1) % poly.length], poly[e]));
    const mid = at.offset + segLen / 2 + (d.offset || 0);
    return { ...d, edge: at.wall, offset: mid - runs[at.wall].len / 2 };
  });
  return {
    ...inst,
    polygon: runs.map((r) => poly[r.a]),
    polygonJoints: undefined,
    polygonRadii: pick(inst.polygonRadii),
    polygonCornerSlats: pick(inst.polygonCornerSlats),
    polygonConstraints: inst.polygonConstraints ? runs.map((r) => inst.polygonConstraints![r.segs[0]] ?? null) : undefined,
    polygonEdgeLengths: undefined,
    polygonRelations: undefined,
    doors,
    door: undefined,
    glass: inst.glass?.map(rebase),
    openings: inst.openings?.map(rebase),
  };
}

/** A pane or opening on a wall, in stations along that wall. `seg` is the REAL
 *  object stored on the room (not a view copy), so a layout can move it. */
export interface WallFeature { kind: 'glass' | 'opening'; seg: GlassSegment; s: number; t: number; }

/** The panes and openings hosted on wall `w`, in order along it. */
export function wallFeatures(inst: ProductInstance, w: number): WallFeature[] {
  const run = wallRuns(inst)[w];
  if (!run) return [];
  const out: WallFeature[] = [];
  const collect = (kind: WallFeature['kind'], arr: GlassSegment[] | undefined): void => {
    for (const g of arr || []) {
      if (g.innerWallId != null || g.edge == null) continue;
      const k = run.segs.indexOf(g.edge);
      if (k < 0) continue;
      const o = run.starts[k];
      out.push({ kind, seg: g, s: o + Math.min(g.start, g.end), t: o + Math.max(g.start, g.end) });
    }
  };
  collect('glass', inst.glass);
  collect('opening', inst.openings);
  return out.sort((a, b) => a.s - b.s);
}

/** Two stations closer than this are the same jamb. */
const JAMB_EPS = 0.5;

/** Orientation locks for a freshly drawn ring: every segment that is already
 *  (within ~2°) horizontal or vertical gets an 'h' / 'v' lock, so squared shapes
 *  come out constrained. Returns undefined when nothing is axis-aligned. */
export function autoAxisLocks(pts: Vec2[]): Array<'h' | 'v' | null> | undefined {
  const n = pts.length;
  const arr = pts.map((_, i) => {
    const a = pts[i], b = pts[(i + 1) % n];
    const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy);
    if (L < 1) return null;
    if (Math.abs(dy) / L < 0.035) return 'h' as const;   // < ~2° from horizontal
    if (Math.abs(dx) / L < 0.035) return 'v' as const;    // < ~2° from vertical
    return null;
  }) as Array<'h' | 'v' | null>;
  return arr.some(Boolean) ? arr : undefined;
}

/** Where each old vertex went after a layout: its new index, or null for a jamb
 *  that was removed. Anything that points at a room vertex BY INDEX (a dimension
 *  anchored to a corner) follows it through this. */
export type VertexRemap = (v: number) => number | null;
const identityRemap: VertexRemap = (v) => v;
const composeRemap = (a: VertexRemap, b: VertexRemap): VertexRemap => (v) => { const x = a(v); return x == null ? null : b(x); };

/**
 * Lay wall `w` out so each of `feats` owns exactly one whole segment: the wall's
 * jambs are replaced by vertices at the features' ends, each pane/opening is
 * re-homed onto its segment (`start` 0, `end` its length), and each door on the
 * wall moves to the segment holding its centre. This is the ONE writer of a
 * wall's segmentation — adding, deleting, converting an old drawing and every
 * typed edit go through it — and it re-indexes everything numbered by vertex or
 * segment (per-vertex arrays, per-segment locks, relations, features on other
 * walls). Wall-numbered features (benches, heater walls, partitions) are not
 * touched: the corners, and so the walls, never change here.
 *
 * `feats` must be the room's own objects, in wall stations; overlaps are clipped
 * (the earlier one wins) and slivers under `JAMB_EPS` dropped from the list.
 */
export function layoutWall(inst: ProductInstance, w: number, feats: WallFeature[]): VertexRemap {
  const runs = wallRuns(inst);
  const run = runs[w];
  const poly = inst.polygon;
  if (!run || !poly) return identityRemap;
  const n = poly.length, L = run.len;
  const segLen = (i: number): number => len2(sub(poly[(i + 1) % n], poly[i]));
  const A = poly[run.a], B = poly[run.b];
  const dir = L > 0 ? mul(sub(B, A), 1 / L) : { x: 0, y: 0 };
  const onWall = (s: number): Vec2 => add(A, mul(dir, s));
  const inRun = new Set(run.segs);

  // Doors on this wall, by the station of their centre (they follow their spot).
  const doorsHere = roomDoors(inst)
    .filter((d) => d.innerWallId == null && inRun.has(d.edge ?? 0))
    .map((d) => {
      const e = d.edge ?? 0, k = run.segs.indexOf(e);
      return { d, mid: run.starts[k] + segLen(e) / 2 + (d.offset || 0) };
    });

  // Clean feature list: clamped, ordered, non-overlapping, ends snapped to the corners.
  const snapEnd = (x: number): number => (x < JAMB_EPS ? 0 : x > L - JAMB_EPS ? L : x);
  const clean: WallFeature[] = [];
  for (const f of [...feats].sort((p, q) => p.s - q.s)) {
    const prevT = clean.length ? clean[clean.length - 1].t : 0;
    const s = snapEnd(Math.max(clamp(f.s, 0, L), prevT)), t = snapEnd(clamp(f.t, 0, L));
    if (t - s >= JAMB_EPS) clean.push({ ...f, s, t });
  }
  const cuts: number[] = [];
  for (const f of clean) for (const x of [f.s, f.t]) {
    if (x > JAMB_EPS && x < L - JAMB_EPS && !cuts.some((c) => Math.abs(c - x) < JAMB_EPS)) cuts.push(x);
  }
  cuts.sort((p, q) => p - q);

  // New ring: every vertex except this wall's old jambs, with the new jambs laid in
  // straight after the wall's first corner.
  const interior = new Set(run.segs.slice(1));
  const vMap = new Map<number, number>();
  const pts: Vec2[] = [], joints: boolean[] = [];
  for (let v = 0; v < n; v++) {
    if (interior.has(v)) continue;
    vMap.set(v, pts.length);
    pts.push(poly[v]); joints.push(!!inst.polygonJoints?.[v]);
    if (v === run.a) for (const c of cuts) { pts.push(onWall(c)); joints.push(true); }
  }
  const N = pts.length;
  const first = vMap.get(run.a)!;
  const bounds = [0, ...cuts, L];
  const wallSeg = (k: number): number => (first + k) % N;
  const segAt = (x: number): number => {
    let k = 0;
    while (k < cuts.length && x >= cuts[k] - JAMB_EPS) k++;
    return k;
  };
  // Any old segment → its new index: off this wall it keeps its start vertex; on
  // it, the new segment holding its midpoint.
  const segMap = (i: number): number => {
    if (!inRun.has(i)) return vMap.get(i)!;
    const k = run.segs.indexOf(i);
    return wallSeg(segAt(run.starts[k] + segLen(i) / 2));
  };

  const perVertex = <T>(arr: Array<T | null> | undefined): Array<T | null> | undefined => {
    if (!arr) return undefined;
    const out: Array<T | null> = new Array(N).fill(null);
    for (const [v, nv] of vMap) out[nv] = arr[v] ?? null;
    return out.some((x) => x != null && (x as unknown) !== 0) ? out : undefined;
  };
  const oldCons = inst.polygonConstraints, oldLens = inst.polygonEdgeLengths;
  let cons: Array<'h' | 'v' | null> | undefined;
  if (oldCons) {
    cons = new Array(N).fill(null);
    for (let i = 0; i < n; i++) if (!inRun.has(i)) cons[segMap(i)] = oldCons[i] ?? null;
    for (let k = 0; k <= cuts.length; k++) cons[wallSeg(k)] = oldCons[run.segs[0]] ?? null;
    if (!cons.some(Boolean)) cons = undefined;
  }
  let relations = inst.polygonRelations ? [...inst.polygonRelations] : undefined;
  let lens: Array<number | null> | undefined;
  if (oldLens) {
    lens = new Array(N).fill(null);
    for (let i = 0; i < n; i++) if (!inRun.has(i)) lens[segMap(i)] = oldLens[i] ?? null;
    // A whole-wall length pin survives the wall being cut up as the distance
    // between its two corners (vertex-numbered, so later layouts keep it).
    const pin = run.segs.length === 1 ? oldLens[run.segs[0]] : null;
    if (pin != null && cuts.length) {
      (relations ??= []).push({ type: 'distancePP', verts: [run.a, run.b], value: pin });
    } else if (pin != null) lens[wallSeg(0)] = pin;
    if (!lens.some((x) => x != null)) lens = undefined;
  }
  if (relations) {
    const vm = (v: number): number | null => vMap.get(v) ?? null;
    relations = relations.flatMap((r): ShapeRelation[] => {
      if ('edges' in r) return [{ ...r, edges: [segMap(r.edges[0]), segMap(r.edges[1])] }];
      if ('verts' in r) {
        const a = vm(r.verts[0]), b = vm(r.verts[1]);
        return a == null || b == null ? [] : [{ ...r, verts: [a, b] }];
      }
      const v = vm(r.vert);
      return v == null ? [] : [{ ...r, vert: v }];
    });
    if (!relations.length) relations = undefined;
  }

  // Features on OTHER walls follow their segment's new index.
  const reindex = (g: GlassSegment): void => { if (g.innerWallId == null && g.edge != null && !inRun.has(g.edge)) g.edge = segMap(g.edge); };
  for (const g of inst.glass || []) reindex(g);
  for (const o of inst.openings || []) reindex(o);
  for (const d of roomDoors(inst)) if (d.innerWallId == null && !inRun.has(d.edge ?? 0)) d.edge = segMap(d.edge ?? 0);

  inst.polygon = pts;
  inst.polygonJoints = joints.some(Boolean) ? joints : undefined;
  inst.polygonRadii = perVertex(inst.polygonRadii);
  inst.polygonCornerSlats = perVertex(inst.polygonCornerSlats);
  inst.polygonConstraints = cons;
  inst.polygonEdgeLengths = lens;
  inst.polygonRelations = relations;

  // This wall's own features: one whole segment each.
  const kept = new Set(clean.map((f) => f.seg));
  const dropped = feats.filter((f) => !kept.has(f.seg)).map((f) => f.seg);
  if (dropped.length) {
    inst.glass = inst.glass?.filter((g) => !dropped.includes(g));
    inst.openings = inst.openings?.filter((o) => !dropped.includes(o));
  }
  for (const f of clean) {
    const k = segAt((f.s + f.t) / 2);
    f.seg.edge = wallSeg(k);
    f.seg.start = 0;
    f.seg.end = bounds[k + 1] - bounds[k];
  }
  for (const { d, mid } of doorsHere) {
    const k = segAt(clamp(mid, 0, L));
    d.edge = wallSeg(k);
    d.offset = mid - (bounds[k] + bounds[k + 1]) / 2;
  }
  return (v) => vMap.get(v) ?? null;
}

/**
 * Bring every wall of a room into the one-segment-per-feature layout: a wall
 * whose jambs don't match its panes/openings exactly (an old drawing, a pane a
 * door just trimmed, a jamb left over from a deleted pane) is re-laid; one that
 * already matches just has its features' `start`/`end` snapped to the exact
 * segment length. Cheap when there is nothing to do, so it runs on every
 * regenerate — which is also what converts an old drawing on load and heals a
 * wall after any pane is removed, by whatever path.
 */
export function ensureWallSegments(inst: ProductInstance): VertexRemap | null {
  if (!isPolyRoom(inst)) return null;
  let remap: VertexRemap | null = null;
  const walls = wallRuns(inst).length;
  for (let w = 0; w < walls; w++) {
    const run = wallRuns(inst)[w];
    const poly = inst.polygon!, n = poly.length;
    const feats = wallFeatures(inst, w);
    const segLen = (i: number): number => len2(sub(poly[(i + 1) % n], poly[i]));
    const bySeg = new Map<number, number>();
    for (const f of feats) bySeg.set(f.seg.edge!, (bySeg.get(f.seg.edge!) ?? 0) + 1);
    const want = new Set<number>();
    for (const f of feats) for (const x of [f.s, f.t]) if (x > JAMB_EPS && x < run.len - JAMB_EPS) want.add(Math.round(x * 2));
    const have = run.starts.slice(1).map((x) => Math.round(x * 2));
    const aligned = feats.every((f) => bySeg.get(f.seg.edge!) === 1
      && Math.min(f.seg.start, f.seg.end) <= JAMB_EPS && Math.max(f.seg.start, f.seg.end) >= segLen(f.seg.edge!) - JAMB_EPS);
    const sameJambs = have.length === want.size && have.every((x) => [...want].some((y) => Math.abs(x - y) <= 2));
    if (!aligned || !sameJambs) { remap = composeRemap(remap ?? identityRemap, layoutWall(inst, w, feats)); continue; }
    for (const f of feats) { f.seg.start = 0; f.seg.end = segLen(f.seg.edge!); }
  }
  return remap;
}

/**
 * Add a pane or opening spanning `[s, t]` along wall `w` — the glass/opening
 * tool's commit. Ends within `GLASS_MIN` of a corner or an existing jamb snap onto
 * it; the span is cut around the wall's doors (a door is glazed already); a pane
 * it overlaps or touches of the SAME kind is extended rather than stacked (the
 * first one keeps its id and settings), and one of the other kind is clipped back.
 * Then the wall is re-laid so every feature owns its own segment. Returns the
 * added (or extended) features, empty when nothing long enough was left.
 */
export function addWallFeature(
  inst: ProductInstance, kind: WallFeature['kind'], w: number, s: number, t: number, minLen: number,
): { added: GlassSegment[]; remap: VertexRemap } {
  const none = { added: [], remap: identityRemap };
  const run = wallRuns(inst)[w];
  if (!run) return none;
  const L = run.len;
  const stops = [...run.starts, L];
  const snap = (x: number): number => {
    let best = x, bd = GLASS_MIN;
    for (const p of stops) { const d = Math.abs(p - x); if (d < bd) { bd = d; best = p; } }
    return best;
  };
  const a = snap(clamp(Math.min(s, t), 0, L)), b = snap(clamp(Math.max(s, t), 0, L));
  // Cut around the doors on this wall.
  const view = wallView(inst);
  let pieces: Array<[number, number]> = [[a, b]];
  for (const d of outerDoorSpans(view)) {
    if (d.edge !== w) continue;
    pieces = pieces.flatMap(([p, q]): Array<[number, number]> =>
      q <= d.start + EPS || p >= d.end - EPS ? [[p, q]] : [[p, Math.min(q, d.start)], [Math.max(p, d.end), q]]);
  }
  pieces = pieces.filter(([p, q]) => q - p >= minLen);
  if (!pieces.length) return none;

  let feats = wallFeatures(inst, w);
  const added: GlassSegment[] = [];
  const listOf = (k: WallFeature['kind']): GlassSegment[] => (k === 'glass' ? (inst.glass ??= []) : (inst.openings ??= []));
  for (const [p0, q0] of pieces) {
    let p = p0, q = q0, keeper: GlassSegment | null = null;
    const absorbed = new Set<GlassSegment>();
    for (const f of feats) {
      if (f.kind !== kind || f.t < p - EPS || f.s > q + EPS) continue;
      p = Math.min(p, f.s); q = Math.max(q, f.t);
      if (keeper) absorbed.add(f.seg); else keeper = f.seg;
    }
    const seg: GlassSegment = keeper ?? { wall: 'front', edge: run.segs[0], start: 0, end: 0 };
    if (!keeper) listOf(kind).push(seg);
    if (absorbed.size) {
      if (kind === 'glass') inst.glass = inst.glass!.filter((g) => !absorbed.has(g));
      else inst.openings = inst.openings!.filter((o) => !absorbed.has(o));
    }
    // The other kind gives way: clipped back, split in two if the new span lands inside it.
    const next: WallFeature[] = [];
    for (const f of feats) {
      if (absorbed.has(f.seg) || f.seg === seg) continue;
      if (f.kind === kind || f.t <= p + EPS || f.s >= q - EPS) { next.push(f); continue; }
      const parts = ([[f.s, p], [q, f.t]] as Array<[number, number]>).filter(([x, y]) => y - x >= GLASS_MIN);
      if (!parts.length) {
        if (f.kind === 'glass') inst.glass = inst.glass!.filter((g) => g !== f.seg);
        else inst.openings = inst.openings!.filter((o) => o !== f.seg);
        continue;
      }
      parts.forEach(([x, y], i) => {
        // The far piece of a split is a NEW feature: no id, so the two halves are
        // not one selection / one delete (as clampGlassToDoor does for a door).
        const piece = i === 0 ? f.seg : { ...f.seg, id: undefined };
        if (i > 0) listOf(f.kind).push(piece);
        next.push({ ...f, seg: piece, s: x, t: y });
      });
    }
    next.push({ kind, seg, s: p, t: q });
    feats = next;
    added.push(seg);
  }
  return { added, remap: layoutWall(inst, w, feats) };
}

// ---- Rounded (curved) wall corners ---------------------------------------
// A corner stores only its outer-face radius; the arc, every concentric inner
// face and the bench corner pieces are derived. The effective radius is
// re-clamped on every read, so shrinking a wall tightens the curve and undoing
// it lets the stored value spring back.

/** Minimum arc radius worth drawing; below this a face is treated as sharp. */
export const RADIUS_EPS = 0.5;

/** Stored (unclamped) corner radius at vertex `v`. */
export function cornerRadiusRaw(inst: ProductInstance, v: number): number {
  return Math.max(0, inst.polygonRadii?.[v] ?? 0);
}

/** Interior angle (radians) at vertex `v`, or null when degenerate. */
function cornerAngleAt(poly: Vec2[], v: number): number | null {
  const n = poly.length;
  const C = poly[v], p = poly[(v - 1 + n) % n], q = poly[(v + 1) % n];
  const a = sub(p, C), b = sub(q, C);
  const la = len2(a), lb = len2(b);
  if (la < 1e-6 || lb < 1e-6) return null;
  const t = Math.acos(clamp((a.x * b.x + a.y * b.y) / (la * lb), -1, 1));
  return t < 1e-3 || Math.PI - t < 1e-3 ? null : t;
}

/** Distance along an edge that a corner radius consumes (`r / tan(theta/2)`). */
const stationOf = (r: number, theta: number): number => r / Math.tan(theta / 2);

/** Spans along `edge` occupied by anything anchored to it — doors, glass,
 *  openings and interior partitions. A corner radius may not eat into these. */
function edgeFeatureSpans(inst: ProductInstance, edge: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const g of inst.glass || []) if (g.edge === edge) out.push([Math.min(g.start, g.end), Math.max(g.start, g.end)]);
  for (const o of inst.openings || []) if (o.edge === edge) out.push([Math.min(o.start, o.end), Math.max(o.start, o.end)]);
  for (const d of outerDoorSpans(inst)) if (d.edge === edge) out.push([d.start, d.end]);
  for (const iw of inst.innerWalls || []) {
    if ((iw.edge ?? -1) !== edge) continue;
    const sp = innerWallSpan(inst, iw);
    if (sp) out.push([sp.s, sp.t]);
  }
  return out;
}

/** How far a corner may reach along one of its edges before hitting a feature.
 *  `fromStart` picks which end of the edge the corner sits at. */
function featureLimit(inst: ProductInstance, edge: number, edgeLen: number, fromStart: boolean): number {
  let lim = edgeLen;
  for (const [s, e] of edgeFeatureSpans(inst, edge)) {
    lim = Math.min(lim, fromStart ? Math.max(0, s) : Math.max(0, edgeLen - e));
  }
  return lim;
}

/** The effective radius of every corner, clamped to what the room can give:
 *  the two adjacent edges must fit both their corners' tangent runs, no corner
 *  may eat into a door / glass / opening / partition, and an edge carrying a
 *  hidden heater wall (which spans its whole edge and mitres into both
 *  neighbours) forces its corners sharp. */
export function effectiveCornerRadii(inst: ProductInstance): number[] {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return [];
  const n = poly.length;
  // Every room without a single rounded corner takes this exit: the clamps below
  // scan each wall's doors, panes and partitions, and polyBenchBands asks for
  // these on every render.
  if (!poly.some((_, i) => cornerRadiusRaw(inst, i) > 0)) return poly.map(() => 0);
  // The clamps are wall rules (a heater wall is looked up by wall; a pane may not
  // be eaten into along the WALL), so a room with jambs resolves on its view and
  // hands each corner its wall corner's radius. A jamb is straight: always 0.
  if (hasJoints(inst)) {
    const byWall = effectiveCornerRadii(wallView(inst));
    const out = poly.map(() => 0);
    wallRuns(inst).forEach((r, w) => { out[r.a] = byWall[w] ?? 0; });
    return out;
  }
  const lens = poly.map((p, i) => len2(sub(poly[(i + 1) % n], p)));
  const thetas = poly.map((_, i) => cornerAngleAt(poly, i));
  // Raw station each corner wants, then per-corner hard limits.
  const raw = poly.map((_, i) => {
    const th = thetas[i];
    return th == null ? 0 : stationOf(cornerRadiusRaw(inst, i), th);
  });
  const cap = poly.map((_, i) => {
    const prev = (i - 1 + n) % n;
    // Corner i is the END of edge `prev` and the START of edge `i`.
    if (heaterWallOn(inst, prev) || heaterWallOn(inst, i)) return 0;
    return Math.min(
      featureLimit(inst, prev, lens[prev], false),
      featureLimit(inst, i, lens[i], true),
    );
  });
  let station = raw.map((s, i) => Math.min(s, cap[i]));
  // Two corners sharing an edge must fit on it; if they don't, scale both back
  // proportionally so neither swallows the other's run. Each pass reads a
  // SNAPSHOT and writes to a fresh array, so the result doesn't depend on which
  // edge is visited first — otherwise a symmetric room comes out lopsided.
  // Passes only ever shrink, so this settles; the cap is a safety net.
  for (let pass = 0; pass < 8; pass++) {
    const next = station.slice();
    let changed = false;
    for (let e = 0; e < n; e++) {
      const i = e, j = (e + 1) % n;
      const sum = station[i] + station[j];
      if (sum > lens[e] + EPS && sum > 1e-9) {
        const f = lens[e] / sum;
        next[i] = Math.min(next[i], station[i] * f);
        next[j] = Math.min(next[j], station[j] * f);
        changed = true;
      }
    }
    station = next;
    if (!changed) break;
  }
  return station.map((s, i) => {
    const th = thetas[i];
    if (th == null || s <= 0) return 0;
    const r = s * Math.tan(th / 2);
    return r < RADIUS_EPS ? 0 : r;
  });
}

/** Effective (clamped) radius of a single corner. */
export function effectiveCornerRadius(inst: ProductInstance, v: number): number {
  return effectiveCornerRadii(inst)[v] ?? 0;
}

/** Largest radius corner `v` could take with the room as it stands — what the
 *  properties panel clamps a typed value to. */
export function maxCornerRadius(inst: ProductInstance, v: number): number {
  const poly = inst.polygon;
  if (!poly || v < 0 || v >= poly.length) return 0;
  // The largest radius this corner can KEEP — i.e. that the clamps hand back
  // unchanged. Anything past it makes the corner share its wall with a
  // neighbour, which would deliver less than asked AND shrink the neighbour, so
  // it is not a value the panel should accept.
  const held = (r: number): boolean => {
    const probe = { ...inst, polygonRadii: poly.map((_, i) => (i === v ? r : cornerRadiusRaw(inst, i))) } as ProductInstance;
    return (effectiveCornerRadii(probe)[v] ?? 0) >= r - 0.5;
  };
  let lo = 0, hi = 0;
  for (const e of poly.map((p, i) => len2(sub(poly[(i + 1) % poly.length], p)))) hi = Math.max(hi, e);
  hi *= 2; // a corner can never hold more than its walls, so this brackets it
  if (held(hi)) return hi;
  for (let i = 0; i < 40 && hi - lo > 0.25; i++) {
    const mid = (lo + hi) / 2;
    if (held(mid)) lo = mid; else hi = mid;
  }
  return lo;
}

/** Derived arc of every corner (null where the corner stays sharp). */
export interface RoomCorner {
  /** Corner vertex index. */
  v: number;
  /** Effective outer-face radius. */
  radius: number;
  /** The angle between the two edges (radians, ≤ π): the interior angle at a
   *  convex corner, 2π − interior at a reflex one (`reflex`). */
  theta: number;
  /** Distance along each adjacent edge to the tangent point — shared by every
   *  concentric face (see `cornerFillet`). */
  station: number;
  /** Arc centre, shared by every concentric face. */
  center: Vec2;
  /** A REFLEX corner (the notch of an L): its centre lies outside the room, so
   *  the faces inset from the outer one grow around it instead of shrinking. */
  reflex: boolean;
}

export function roomCorners(inst: ProductInstance): Array<RoomCorner | null> {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return [];
  const n = poly.length;
  const radii = effectiveCornerRadii(inst);
  const ccw = polygonArea(poly) > 0;
  return poly.map((C, v) => {
    const r = radii[v];
    if (!(r > 0)) return null;
    const P = poly[(v - 1 + n) % n], N = poly[(v + 1) % n];
    const f = cornerFillet(P, C, N, r);
    const turn = (C.x - P.x) * (N.y - C.y) - (C.y - P.y) * (N.x - C.x);
    return f ? { v, radius: r, theta: f.theta, station: f.station, center: f.center, reflex: ccw ? turn < 0 : turn > 0 } : null;
  });
}

/** Radius of the face `inset` mm in from the outer one at a rounded corner — the
 *  one rule for every concentric face (wall, bench rings, corner pieces): R − inset
 *  at a convex corner, R + inset at a reflex one. Past 0 the face is a sharp mitre. */
export function faceRadius(c: RoomCorner, inset: number): number {
  return c.reflex ? c.radius + inset : c.radius - inset;
}

/** How far a rounded corner's `R<n>` label sits beyond its arc (mm). */
const CORNER_LEADER_MM = 260;

/** A rounded corner's radius leader in the room's own frame: `on` at the middle
 *  of the outer arc, `far` the label end, along the bisector AWAY from the room —
 *  past the vertex at a convex corner, back toward the arc centre (into the
 *  notch, which is outside the room) at a reflex one. */
export function cornerRadiusLeaderLocal(inst: ProductInstance, c: RoomCorner): { on: Vec2; far: Vec2 } {
  const C = inst.polygon![c.v];
  const bx = C.x - c.center.x, by = C.y - c.center.y;
  const bl = Math.hypot(bx, by) || 1;
  const ux = bx / bl, uy = by / bl;
  const r = c.radius + (c.reflex ? -CORNER_LEADER_MM : CORNER_LEADER_MM);
  return {
    on: { x: c.center.x + ux * c.radius, y: c.center.y + uy * c.radius },
    far: { x: c.center.x + ux * r, y: c.center.y + uy * r },
  };
}

/** Whether any corner of the room is rounded. */
export function hasRoundedCorners(inst: ProductInstance): boolean {
  return roomCorners(inst).some(Boolean);
}

/**
 * The room outline at a given inset from the outer face, with rounded corners
 * expanded into arc points — the single source every consumer (wall bands,
 * floor, benches, hit-testing, 3D, sections, DXF) reads, so none of them can
 * disagree about where the walls run. `inset` 0 is the outer perimeter, the
 * wall thickness gives the inner face, and deeper values give bench rings; a
 * corner whose radius has been eaten by the inset falls back to a sharp mitre.
 */
export function resolvedOutline(inst: ProductInstance, inset = 0): Vec2[] {
  return resolvedOutlinePaths(inst, inset).flat();
}

/** `resolvedOutline`'s points, one path per corner: a rounded corner's arc
 *  points, else its single mitre point. The run from a path's last point to the
 *  next path's first is the straight face of wall `v`; the runs inside a path
 *  are its arc's chords. */
export function resolvedOutlinePaths(inst: ProductInstance, inset = 0): Vec2[][] {
  const f = offsetFrame(inst, inset);
  if (!f) return [];
  const out: Vec2[][] = [];
  for (let v = 0; v < f.n; v++) out.push(cornerFacePath(f, v));
  return out;
}

/** Offset lines of every wall at a given inset, plus the derived corners — the
 *  shared basis for the outline, the wall bands and the corner pieces. */
interface OffsetFrame {
  n: number;
  poly: Vec2[];
  inset: number;
  dir: Vec2[];
  nrm: Vec2[];
  base: Vec2[];
  corners: Array<RoomCorner | null>;
}

function offsetFrame(inst: ProductInstance, inset: number): OffsetFrame | null {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return null;
  const n = poly.length;
  const ccw = polygonArea(poly) > 0;
  const dir: Vec2[] = [], nrm: Vec2[] = [], base: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const d = sub(poly[(i + 1) % n], poly[i]);
    const L = len2(d) || 1;
    const u = { x: d.x / L, y: d.y / L };
    const nv = ccw ? { x: -u.y, y: u.x } : { x: u.y, y: -u.x };
    dir.push(u);
    nrm.push(nv);
    base.push(add(poly[i], mul(nv, inset)));
  }
  return { n, poly, inset, dir, nrm, base, corners: roomCorners(inst) };
}

/**
 * The face path around corner `v` at the frame's inset: the arc between the two
 * tangent points, or a single mitre point once the inset has eaten the radius.
 * Concentric with the outer face — same centre, same tangent stations, radius
 * `faceRadius` — which is what holds the wall thickness through a curve.
 */
function cornerFacePath(f: OffsetFrame, v: number): Vec2[] {
  const prev = (v - 1 + f.n) % f.n;
  const c = f.corners[v];
  const r = c ? faceRadius(c, f.inset) : 0;
  if (!c || r <= RADIUS_EPS) return [mitreAt(f.base[prev], f.dir[prev], f.base[v], f.dir[v])];
  const p1 = add(add(f.poly[v], mul(f.dir[prev], -c.station)), mul(f.nrm[prev], f.inset));
  const p2 = add(add(f.poly[v], mul(f.dir[v], c.station)), mul(f.nrm[v], f.inset));
  return arcPoints(c.center, p1, p2, r);
}

/**
 * The straight stretch of one edge's face at a given inset, as distances along
 * the OUTER edge direction from `polygon[edge]`. Stations are measured on the
 * outer line because every concentric face projects onto the same stations, so
 * a bench ring and the wall face agree about where the curve begins.
 *
 * A corner that is still curved at this inset gives back its (shared) tangent
 * station; one whose radius the inset has eaten falls back to the sharp mitre,
 * which reaches `inset / tan(theta/2)` along the edge.
 */
export function edgeStraightRun(inst: ProductInstance, edge: number, inset = 0): [number, number] {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return [0, 0];
  const n = poly.length;
  const len = len2(sub(poly[(edge + 1) % n], poly[edge]));
  const corners = roomCorners(inst);
  const stationAt = (v: number): number => {
    const c = corners[v];
    if (c && faceRadius(c, inset) > RADIUS_EPS) return c.station;
    const th = c ? c.theta : cornerAngleAt(poly, v);
    return th == null ? 0 : stationOf(inset, th); // sharp: the mitre's reach
  };
  const s = stationAt(edge);
  const e = len - stationAt((edge + 1) % n);
  return [Math.min(s, len), Math.max(0, e)];
}

// ---- Hidden heater walls -------------------------------------------------
// A heater wall is stored as parameters only; its band is derived here from the
// room polygon, so resizing or reshaping the room re-mitres it for free.

/** The room's hidden heater walls (empty when it has none). */
/** Give every pane, opening and partition of a room that has none its id (a
 *  file saved before they had them, a pane `addWallFeature` just laid): what a
 *  selection, a Parts row and the plan's highlight address it by. Ids already
 *  there are kept. */
export function mintFeatureIds(inst: ProductInstance, uid: (prefix: string) => string): void {
  for (const iw of inst.innerWalls ?? []) if (!iw.id) iw.id = uid('iw');
  for (const g of inst.glass ?? []) if (!g.id) g.id = uid('gl');
  for (const o of inst.openings ?? []) if (!o.id) o.id = uid('op');
}

/** Every station a sculpted bench keeps on a face — its two ends, then its
 *  trestles — so a room edit carries them all alike. */
export function sculptStations(sb: SculptedBench): SculptEnd[] {
  return [sb.start, sb.end, ...(sb.trestles ?? [])];
}

/**
 * Give an instance its own ids for every room feature that carries one. A paste
 * `structuredClone`s the source, so the copy would otherwise share its panes'/
 * doors'/partitions' ids with the original — and because every selection halo and
 * Parts row addresses a feature BY id (not by which instance owns it), selecting
 * one pane lit up the twin on every copy. Inner walls are re-minted first so the
 * `innerWallId` a hosted door / pane / opening points at is remapped to the copy's
 * own partition. `uid(prefix)` supplies the fresh ids (kept caller-side so the
 * module stays free of Date/counter globals).
 */
export function remintFeatureIds(inst: ProductInstance, uid: (prefix: string) => string): Map<string, string> {
  const featMap = new Map<string, string>();
  const iwMap = new Map<string, string>();
  for (const iw of inst.innerWalls ?? []) {
    const nid = uid('iw');
    if (iw.id) iwMap.set(iw.id, nid);
    iw.id = nid;
    // Its benches too: a copy's partition bench selected must not light the original's.
    for (const leg of [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])]) {
      if (leg.id == null) continue;
      const nid = uid('bench');
      featMap.set(leg.id, nid);
      leg.id = nid;
    }
  }
  const reIw = (id?: string): string | undefined => (id != null ? iwMap.get(id) ?? id : id);
  for (const g of inst.glass ?? []) { g.id = uid('gl'); g.innerWallId = reIw(g.innerWallId); }
  for (const o of inst.openings ?? []) { o.id = uid('op'); o.innerWallId = reIw(o.innerWallId); }
  for (const d of inst.doors ?? []) {
    if (d.id != null) { const nid = uid('door'); featMap.set(d.id, nid); d.id = nid; }
    d.innerWallId = reIw(d.innerWallId);
  }
  for (const b of inst.polyBenches ?? []) { const nid = uid('bench'); featMap.set(b.id, nid); b.id = nid; }
  for (const h of inst.heaterWalls ?? []) { const nid = uid('hw'); if (h.id) featMap.set(h.id, nid); h.id = nid; }
  for (const s of inst.benchSupports ?? []) {
    s.id = uid('bs');
    s.innerWallId = reIw(s.innerWallId);
    if (s.roundBench != null) s.roundBench = featMap.get(s.roundBench) ?? s.roundBench;
  }
  for (const br of inst.backrests ?? []) {
    const nid = uid('br');
    featMap.set(br.id, nid);
    br.id = nid;
    const h = br.host;
    if (br.bench) br.bench = featMap.get(br.bench) ?? br.bench;
    if ('heaterWall' in h) br.host = { heaterWall: featMap.get(h.heaterWall) ?? h.heaterWall };
    else if ('partition' in h) br.host = { partition: reIw(h.partition) ?? h.partition, face: h.face };
  }
  // Its sculpted benches: new ids, their partition ends on the copy's partitions.
  for (const sb of inst.sculptedBenches ?? []) {
    const nid = uid('sb');
    featMap.set(sb.id, nid);
    sb.id = nid;
    for (const t of sb.trestles ?? []) t.id = uid('st');
    for (const end of sculptStations(sb)) {
      if ('partition' in end.host) end.host = { partition: reIw(end.host.partition) ?? end.host.partition, face: end.host.face };
    }
  }
  // Its lights: fixed to the copy's own features.
  remintLights(inst, featMap, uid);
  return featMap;
}

export function heaterWallsOf(inst: ProductInstance): HeaterWall[] {
  return inst.heaterWalls ?? [];
}

/** The heater wall on `edge`, or null. At most one per edge. */
export function heaterWallOn(inst: ProductInstance, edge: number): HeaterWall | null {
  return heaterWallsOf(inst).find((h) => h.edge === edge) ?? null;
}

/** Stored (unclamped) gap + thickness of the heater wall on `edge`; 0 without one. */
function heaterWallInsetRaw(inst: ProductInstance, edge: number): number {
  const h = heaterWallOn(inst, edge);
  return h ? Math.max(0, h.distance) + Math.max(0, h.thickness) : 0;
}

/**
 * Largest total inset (air gap + thickness) `edge` can take while every wall edge
 * keeps at least `clearance` mm of inner-face run — so the false wall can never
 * cross the opposite side of the room or invert the carved interior, no matter
 * what is typed or how far the room is shrunk. Other edges hold their own raw
 * insets. Binary search over the derived edges; validity is "no edge collapses".
 */
export function maxHeaterWallInset(inst: ProductInstance, edge: number, clearance = 250): number {
  inst = wallView(inst);
  if (!isPolyRoom(inst)) return 0;
  const poly = inst.polygon!;
  let span = 0;
  for (const p of poly) for (const q of poly) span = Math.max(span, Math.abs(p.x - q.x) + Math.abs(p.y - q.y));
  const ok = (E: number): boolean =>
    polyEdges(inst, (i) => (i === edge ? E : heaterWallInsetRaw(inst, i)))
      .every((e) => (e.ib.x - e.ia.x) * e.dir.x + (e.ib.y - e.ia.y) * e.dir.y >= clearance);
  if (!ok(0)) return 0;
  let lo = 0, hi = span;
  for (let k = 0; k < 40; k++) {
    const mid = (lo + hi) / 2;
    if (ok(mid)) lo = mid; else hi = mid;
  }
  return Math.floor(lo);
}

/** The stored gap/thickness capped to `maxHeaterWallInset`, so derived geometry is
 *  always valid even when the room has been shrunk under an existing wall. The
 *  thickness is preserved where possible; the air gap absorbs the clamp. */
export function effectiveHeaterWall(inst: ProductInstance, h: HeaterWall): { distance: number; thickness: number } {
  const distance = Math.max(0, h.distance), thickness = Math.max(0, h.thickness);
  const cap = maxHeaterWallInset(inst, h.edge);
  if (distance + thickness <= cap) return { distance, thickness };
  const t = Math.min(thickness, cap);
  return { distance: Math.max(0, cap - t), thickness: t };
}

/** How far a heater wall pushes its edge's inner face into the room (gap + wall
 *  thickness, clamped to keep the interior valid); 0 on edges without one. */
export function heaterWallInset(inst: ProductInstance, edge: number): number {
  const h = heaterWallOn(inst, edge);
  if (!h) return 0;
  const eff = effectiveHeaterWall(inst, h);
  return eff.distance + eff.thickness;
}

/**
 * The room's edges as the BENCHES and the interior floor see them: identical to
 * `polyEdges` except that an edge carrying a heater wall has its inner face
 * pushed in past the air gap and the false wall. Bench offsets are measured from
 * `ia`, so benches sit against the heater wall and neighbouring benches mitre
 * against it exactly as they do against a room wall. Wall bands, doors, glass and
 * inner walls deliberately keep the un-pushed `polyEdges`.
 */
export function polyBenchEdges(inst: ProductInstance): PolyEdge[] {
  inst = wallView(inst);
  if (!heaterWallsOf(inst).length) return polyEdges(inst);
  return polyEdges(inst, (i) => heaterWallInset(inst, i));
}

/** The interior polygon carved by any heater walls — the floor tint stops at a
 *  heater wall's inner face, leaving the air gap behind it an empty void. */
export function polyInnerCarved(inst: ProductInstance): Vec2[] {
  inst = wallView(inst);
  // Heater walls force their corners sharp, so a room can be curved or carved at
  // any given corner but never both — the curved ring is safe to use as-is.
  if (hasRoundedCorners(inst) && !heaterWallsOf(inst).length) return polyInner(inst);
  return polyBenchEdges(inst).map((e) => e.ia);
}

/** Derived band polygon of each hidden heater wall, mitred into its neighbours:
 *  the quad between the air-gap face and the wall's inner face. Ends land on the
 *  adjacent wall's inner face, or mitre with an adjacent heater wall when the
 *  neighbour has one too. */
export function heaterWallBands(inst: ProductInstance): Array<{ wall: HeaterWall; poly: Vec2[] }> {
  inst = wallView(inst);
  const walls = heaterWallsOf(inst);
  if (!walls.length) return [];
  // Two offset skeletons: the gap face (wall + distance) and the inner face
  // (wall + distance + thickness). Corner vertices of each give clean mitres.
  const gapEdges = polyEdges(inst, (i) => {
    const h = heaterWallOn(inst, i);
    return h ? effectiveHeaterWall(inst, h).distance : 0;
  });
  const faceEdges = polyBenchEdges(inst);
  const out: Array<{ wall: HeaterWall; poly: Vec2[] }> = [];
  for (const h of walls) {
    const g = gapEdges[h.edge], f = faceEdges[h.edge];
    if (!g || !f) continue;
    out.push({ wall: h, poly: [g.ia, g.ib, f.ib, f.ia] });
  }
  return out;
}

/** A point on the outer edge at distance `d` from its first vertex. */
export const outerAt = (e: PolyEdge, d: number): Vec2 => add(e.a, mul(e.dir, d));
/** The inner-edge point opposite outer distance `d` (mitred vertices at the ends). */
export function innerAt(e: PolyEdge, d: number, wall: number): Vec2 {
  if (d <= EPS) return e.ia;
  if (d >= e.len - EPS) return e.ib;
  return add(outerAt(e, d), mul(e.normal, wall));
}

type Span = [number, number];

/** Subtract `gaps` from [0,len], returning the remaining solid spans. */
function solidSpans(len: number, gaps: Span[]): Span[] {
  const sorted = gaps
    .map((g) => [Math.max(0, Math.min(g[0], g[1])), Math.min(len, Math.max(g[0], g[1]))] as Span)
    .filter((g) => g[1] > g[0] + EPS)
    .sort((p, q) => p[0] - q[0]);
  const out: Span[] = [];
  let cursor = 0;
  for (const [s, e] of sorted) {
    if (s - cursor > EPS) out.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (len - cursor > EPS) out.push([cursor, len]);
  return out;
}

/**
 * The wall run a door sits on, in the room's local frame. `base` is the along=0
 * point on the face the door is drawn on, `along` the unit direction its
 * offset/width run along, `swing` the unit direction its leaf opens toward, and
 * `len` the run length. For an OUTER edge the face is the outer wall face and the
 * leaf swings outward; for an INNER partition the face is the partition
 * centreline and the leaf swings sideways across the room.
 */
export interface DoorRun { base: Vec2; along: Vec2; swing: Vec2; len: number; }

/** The run for a given door (outer edge or inner partition), or null. */
export function doorRun(inst: ProductInstance, door: SaunaDoor): DoorRun | null {
  // A partition is hosted by WALL, so its door's run is found on the wall view.
  if (door.innerWallId != null) inst = wallView(inst);
  const edges = polyEdges(inst);
  if (!edges.length) return null;
  if (door.innerWallId != null) {
    const iw = (inst.innerWalls || []).find((w) => w.id === door.innerWallId);
    if (!iw || iw.edge == null) return null;
    const e = edges[iw.edge];
    if (!e) return null;
    const wall = wallThicknessOf(inst);
    // Partition centreline: from the host edge's inner face at iw.pos, running
    // inward along the edge normal for iw.length; the leaf swings along the edge.
    const base = innerAt(e, innerWallSpan(inst, iw)?.mid ?? clamp(iw.pos, 0, e.len), wall);
    // Both sides of a partition are interior, so the leaf can swing either way
    // (push / pull) — `swingBack` mirrors it. An outer wall has no such choice.
    const swing = door.swingBack ? { x: -e.dir.x, y: -e.dir.y } : e.dir;
    return { base, along: e.normal, swing, len: Math.max(0, iw.length) };
  }
  const e = edges[door.edge ?? 0];
  if (!e) return null;
  return { base: e.a, along: e.dir, swing: { x: -e.normal.x, y: -e.normal.y }, len: e.len };
}

/** The door's opening span [start,end] measured along its run. */
export function doorSpanOn(run: DoorRun, door: SaunaDoor): { start: number; end: number } {
  const mid = run.len / 2 + (door.offset || 0);
  const half = door.width / 2;
  return { start: clamp(mid - half, 0, run.len), end: clamp(mid + half, 0, run.len) };
}

/** Outer-wall door openings, one per door hosted on an outer polygon edge. */
export function outerDoorSpans(inst: ProductInstance): Array<{ door: SaunaDoor; edge: number; start: number; end: number }> {
  const out: Array<{ door: SaunaDoor; edge: number; start: number; end: number }> = [];
  for (const d of roomDoors(inst)) {
    if (d.innerWallId != null) continue;
    const run = doorRun(inst, d);
    if (!run) continue;
    const s = doorSpanOn(run, d);
    out.push({ door: d, edge: d.edge ?? 0, start: s.start, end: s.end });
  }
  return out;
}

/** Door openings (along the partition's inward axis) on a given inner wall. */
export function innerWallDoorSpans(inst: ProductInstance, iwId: string | undefined): Array<{ door: SaunaDoor; start: number; end: number }> {
  if (iwId == null) return [];
  const out: Array<{ door: SaunaDoor; start: number; end: number }> = [];
  for (const d of roomDoors(inst)) {
    if (d.innerWallId !== iwId) continue;
    const run = doorRun(inst, d);
    if (!run) continue;
    const s = doorSpanOn(run, d);
    out.push({ door: d, start: s.start, end: s.end });
  }
  return out;
}

/**
 * Glazed spans on one interior partition, each `[start,end]` along the partition's
 * INWARD length — the same measure its doors use, so both cut the same fill.
 */
export function innerWallGlassSpans(
  inst: ProductInstance, iwId: string | undefined,
): Array<{ seg: GlassSegment; start: number; end: number }> {
  if (iwId == null) return [];
  const iw = (inst.innerWalls || []).find((w) => w.id === iwId);
  const len = Math.max(0, iw?.length ?? 0);
  const out: Array<{ seg: GlassSegment; start: number; end: number }> = [];
  for (const g of inst.glass || []) {
    if (g.innerWallId !== iwId) continue;
    const a = clamp(Math.min(g.start, g.end), 0, len), b = clamp(Math.max(g.start, g.end), 0, len);
    if (b - a > 1) out.push({ seg: g, start: a, end: b });
  }
  return out;
}

/** Back-compat: the primary (first outer) door's span, or null. */
export function polyDoorSpan(inst: ProductInstance): { edge: number; start: number; end: number } | null {
  const first = outerDoorSpans(inst)[0];
  return first ? { edge: first.edge, start: first.start, end: first.end } : null;
}

/** Where a steam room's door drain channel lies (local frame): centred on the
 *  main door, `off` mm outside the wall SEGMENT that door is on — a pane on the
 *  same wall splits it, so the door's segment need not be edge 0 — and running
 *  along it. Null with no outer door. */
export function polyDoorChannelAt(inst: ProductInstance, off: number): { at: Vec2; dir: Vec2 } | null {
  const span = polyDoorSpan(inst);
  const e = span ? polyEdges(inst)[span.edge] : undefined;
  if (!span || !e) return null;
  return { at: sub(outerAt(e, (span.start + span.end) / 2), mul(e.normal, off)), dir: e.dir };
}

/** The centre of a room's inner floor (local frame), by area: the corners a pane
 *  or an opening adds along a wall leave it where it is. */
export function polyFloorCentre(inst: ProductInstance): Vec2 {
  return polygonCentroid(polyInner(inst));
}

/** A partition's band across its whole thickness between two stations along its
 *  inward length (a pane's or a door's `start` / `end`, clamped to it), in the
 *  room's local frame — exactly the gap `innerWallGaps` cuts out of its fill —;
 *  null when it is empty. */
export function innerWallSpanBand(inst: ProductInstance, iw: InnerWall, s0: number, s1: number): Vec2[] | null {
  const band = polyInnerWallBand(inst, iw), len = Math.max(0, iw.length);
  if (band.length < 4 || !(len > 0)) return null;
  const a = clamp(Math.min(s0, s1), 0, len), b = clamp(Math.max(s0, s1), 0, len);
  if (!(b - a > 1e-6)) return null;
  const [oS, oE, iE, iS] = band;
  const at = (p: Vec2, q: Vec2, d: number): Vec2 => ({ x: p.x + ((q.x - p.x) * d) / len, y: p.y + ((q.y - p.y) * d) / len });
  return [at(oS, iS, a), at(oE, iE, a), at(oE, iE, b), at(oS, iS, b)];
}

/** Glass + door gaps on a given edge (these cut the wall). Openings cut only
 *  where asked: the plan draws an opening's wall faded over it, 3D leaves a hole.
 *  `cutAt`: only what a level plane that high (mm above the floor) passes
 *  through, openings too — the wall stands solid over a door and under a pane
 *  (a reflected ceiling plan). */
function wallGaps(inst: ProductInstance, edgeIndex: number, openings = false, cutAt?: number): Span[] {
  const at = (lo: number, hi: number): boolean => cutAt == null || (lo <= cutAt && cutAt < hi);
  const gaps: Span[] = [];
  for (const g of inst.glass || []) if (g.edge === edgeIndex && at(glassPaneBase(g), glassPaneTop(inst, g))) gaps.push([g.start, g.end]);
  for (const d of outerDoorSpans(inst)) if (d.edge === edgeIndex && at(0, doorTop(inst, d.door))) gaps.push([d.start, d.end]);
  if (openings || cutAt != null) {
    for (const o of inst.openings || []) if (o.innerWallId == null && o.edge === edgeIndex && at(openingSill(o), openingArchitrave(inst, o))) gaps.push([o.start, o.end]);
  }
  return gaps;
}

/** The id prefix of a part's hole among a room's openings (`roomWallCuts`). */
export const PART_CUT_PREFIX = 'cut:';
/** Whether an opening is a part's hole (`roomWallCuts`), not one of the room's. */
export const isPartCut = (seg: Pick<GlassSegment, 'id'>): boolean => !!seg.id?.startsWith(PART_CUT_PREFIX);

/** The holes the parts set into a room's walls cut right through them
 *  (`roomWallCut`, wallCuts.ts: a control unit's display in the sauna's wood),
 *  as openings: on the segment whose outer or inner face the part stands out of
 *  (within `CUT_FACE_TOL`, square to it), clamped into that face's straight run,
 *  from the cut's bottom to its top over the room's floor; each one's id is
 *  `PART_CUT_PREFIX` + the part's. Derived, never stored. One clashing with a
 *  door, a pane, an opening or a hole before it is left out. */
/** The face of a room's wall a part set into a wall stands out of (`at`, its
 *  middle on that face, and `into`, the direction into the wall: world, from
 *  `inWallFace`): the segment whose outer or inner face it lies on (within
 *  `CUT_FACE_TOL`, square to it), inside that face's straight run (its own
 *  mitred ends inside, the corners' arcs), with how far along the segment it
 *  stands and the run's ends. Null when it is set into none of the room's. */
export function roomFaceAt(inst: ProductInstance, at: Vec2, into: Vec2): { edge: number; inner: boolean; t: number; lo: number; hi: number } | null {
  if (!isPolyRoom(inst) || !inst.autoBenches) return null;
  const edges = polyEdges(inst), wall = wallThicknessOf(inst);
  const r = (-inst.rotation * Math.PI) / 180, c = Math.cos(r), sn = Math.sin(r);
  const local = (v: Vec2): Vec2 => ({ x: v.x * c - v.y * sn, y: v.x * sn + v.y * c });
  const p = local({ x: at.x - inst.x, y: at.y - inst.y }), dir = local(into);
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i], rel = sub(p, e.a);
    const t = rel.x * e.dir.x + rel.y * e.dir.y, d = rel.x * e.normal.x + rel.y * e.normal.y;
    const dot = dir.x * e.normal.x + dir.y * e.normal.y;
    const outer = dot > CUT_SQUARE && Math.abs(d) <= CUT_FACE_TOL, inner = dot < -CUT_SQUARE && Math.abs(d - wall) <= CUT_FACE_TOL;
    if (!outer && !inner) continue;
    const [runS, runE] = edgeStraightRun(inst, i, 0);
    const along = (q: Vec2): number => (q.x - e.a.x) * e.dir.x + (q.y - e.a.y) * e.dir.y;
    const lo = inner ? Math.max(runS, along(e.ia)) : runS, hi = inner ? Math.min(runE, along(e.ib)) : runE;
    return t < lo || t > hi ? null : { edge: i, inner, t, lo, hi };
  }
  return null;
}

export function roomWallCuts(inst: ProductInstance, parts: readonly InWallPart[]): GlassSegment[] {
  if (!isPolyRoom(inst) || !inst.autoBenches || !parts.length) return [];
  const wall = wallThicknessOf(inst), base = roomBase(inst);
  const out: GlassSegment[] = [];
  for (const part of parts) {
    const cut = part.roomWallCut;
    if (!cut || !(cut.width > 0) || !(cut.height > 0)) continue;
    const face = roomFaceAt(inst, part.at, part.into);
    if (face) {
      const { edge: i, inner, t, lo, hi } = face;
      const s = Math.max(lo, t - cut.width / 2), en = Math.min(hi, t + cut.width / 2);
      const [c0, c1] = cutBand(part, cut);
      const sill = Math.max(0, c0 - base), top = c1 - base;
      const clash = [...wallGaps(inst, i, true), ...out.filter((o) => o.edge === i).map((o): Span => [o.start, o.end])]
        .some(([a, b]) => Math.min(a, b) < en && s < Math.max(a, b));
      if (en - s >= 1 && top - sill >= 1 && !clash) {
        // A cut shallower than the wall is a pocket in the face it stands out of.
        const deep = cut.depth != null && cut.depth > 0 && cut.depth < wall - 0.5 ? cut.depth : null;
        const pocket = deep == null ? undefined : inner ? { from: wall - deep, to: wall } : { from: 0, to: deep };
        out.push({ wall: 'front', edge: i, start: s, end: en, id: `${PART_CUT_PREFIX}${part.src}`, heightFromGround: sill, architraveHeight: top, ...(pocket ? { pocket } : {}) });
      }
    }
  }
  return out;
}

/** A room with its parts' holes among its openings (`roomWallCuts`), for the
 *  views that cut them through its walls (3D, sections); the room itself when
 *  none is cut. */
export function withWallCuts(inst: ProductInstance, parts: readonly InWallPart[]): ProductInstance {
  const cuts = roomWallCuts(inst, parts);
  return cuts.length ? { ...inst, openings: [...(inst.openings ?? []), ...cuts] } : inst;
}

export interface PolyWall {
  /** Solid wall band quads (outer→inner), gaps for door/glass already removed. */
  fill: Vec2[][];
  /** The edge each `fill` band belongs to, parallel to it; -1 for the arc band of
   *  a rounded corner, which belongs to two edges. Lets a caller repaint exactly
   *  the bands of one wall (see {@link polyWallBand}) instead of re-deriving them. */
  fillEdge: number[];
  /** Outer perimeter segments (gapped at doors/glass). */
  outer: Array<[Vec2, Vec2]>;
  /** Inner perimeter segments (gapped at doors/glass). */
  inner: Array<[Vec2, Vec2]>;
  /** Jamb faces across the wall thickness at each door/glass opening. */
  jambs: Array<[Vec2, Vec2]>;
  /** Rounded-corner face arcs, kept as true arcs (not folded into `outer` /
   *  `inner`) so the DXF export can emit ARC entities rather than a tessellated
   *  polyline. Callers that only stroke can expand them with `arcPoints`. */
  cornerArcs: Array<{ center: Vec2; radius: number; from: Vec2; to: Vec2 }>;
}

/**
 * Build the polygonal room's wall geometry: filled bands plus the stroke loops.
 * Corners stay seamless because the outer/inner loops run continuously through
 * the mitred vertices and the internal radial seams are never stroked.
 */
/** `openings`: cut the openings too (3D, which builds the wall below the sill
 *  and above the architrave itself); the plan keeps them in the band. `cutAt`:
 *  the wall a level plane that high cuts (`wallGaps`). */
export function polyWall(inst: ProductInstance, opts: { openings?: boolean; cutAt?: number } = {}): PolyWall {
  const edges = polyEdges(inst);
  const wall = wallThicknessOf(inst);
  const fill: Vec2[][] = [];
  const fillEdge: number[] = [];
  const outer: Array<[Vec2, Vec2]> = [];
  const inner: Array<[Vec2, Vec2]> = [];
  const jambs: Array<[Vec2, Vec2]> = [];
  const cornerArcs: PolyWall['cornerArcs'] = [];
  const n = edges.length;
  // At a jamb the opening is the NEIGHBOURING segment, so the jamb face between a
  // solid run and a pane/door sits on the segment boundary, not inside one edge.
  const gapAt = (k: number, atEnd: boolean): boolean =>
    wallGaps(inst, k, opts.openings, opts.cutAt).some(([a, b]) => (atEnd ? Math.max(a, b) >= edges[k].len - EPS : Math.min(a, b) <= EPS));
  edges.forEach((e, i) => {
    // A rounded corner takes the ends of the wall; the straight band runs
    // between the tangent points and the arc bands below close the corners.
    const [runS, runE] = edgeStraightRun(inst, i, 0);
    const jambBefore = isJoint(inst, i) && gapAt((i - 1 + n) % n, true);
    const jambAfter = isJoint(inst, (i + 1) % n) && gapAt((i + 1) % n, false);
    for (const [s0, t0] of solidSpans(e.len, wallGaps(inst, i, opts.openings, opts.cutAt))) {
      const s = Math.max(s0, runS), t = Math.min(t0, runE);
      if (t - s <= EPS) continue;
      const oS = outerAt(e, s), oE = outerAt(e, t);
      const iS = innerAt(e, s, wall), iE = innerAt(e, t, wall);
      fill.push([oS, oE, iE, iS]);
      fillEdge.push(i);
      outer.push([oS, oE]);
      inner.push([iS, iE]);
      // Only a real opening edge gets a jamb — not a corner, and not the seam
      // where the straight band hands over to an arc band.
      if ((s0 > EPS || jambBefore) && s <= s0 + EPS) jambs.push([oS, iS]);
      if ((t0 < e.len - EPS || jambAfter) && t >= t0 - EPS) jambs.push([oE, iE]);
    }
  });
  // Curved corners: the band between the outer arc and the wall's inner face.
  // A radius may never eat into a door or pane, so these are always solid.
  const fOut = offsetFrame(inst, 0), fIn = offsetFrame(inst, wall);
  if (fOut && fIn) {
    for (let v = 0; v < fOut.n; v++) {
      if (!fOut.corners[v]) continue;
      const oPath = cornerFacePath(fOut, v), iPath = cornerFacePath(fIn, v);
      fill.push([...oPath, ...[...iPath].reverse()]);
      fillEdge.push(-1);
      const c = fOut.corners[v]!;
      if (oPath.length > 1) cornerArcs.push({ center: c.center, radius: c.radius, from: oPath[0], to: oPath[oPath.length - 1] });
      if (iPath.length > 1) cornerArcs.push({ center: c.center, radius: faceRadius(c, wall), from: iPath[0], to: iPath[iPath.length - 1] });
    }
  }
  return { fill, fillEdge, outer, inner, jambs, cornerArcs };
}

/**
 * The filled bands of ONE wall — literally the bands `polyWall` painted for that
 * edge, so a highlight lands exactly on the wall instead of on a re-derivation of
 * it (doors and glass are cut out of both, mitres are part of both). A rounded
 * corner's arc band is shared by two edges and belongs to neither, so it is left
 * to the corner.
 */
export function polyWallBand(inst: ProductInstance, edge: number): Vec2[][] {
  const w = polyWall(inst);
  return w.fill.filter((_, i) => w.fillEdge[i] === edge);
}

/** The inner (room-side) polygon, for clipping the floor/benches. Follows the
 *  arc at every rounded corner, so the floor and the benches clipped to it stop
 *  at the curved wall face rather than a phantom mitre. */
export function polyInner(inst: ProductInstance): Vec2[] {
  return hasRoundedCorners(inst)
    ? resolvedOutline(inst, wallThicknessOf(inst))
    : polyEdges(inst).map((e) => e.ia);
}

/** Interior partitions inherit the room's wall thickness unless the partition
 *  overrides it; only the override is stored, so a room-wide change still
 *  carries through. (Colour is resolved the same way, at the draw sites that
 *  already know the room's wall colour.) */
export function innerWallThickness(inst: ProductInstance, iw: InnerWall): number {
  return Math.max(1, iw.thickness ?? wallThicknessOf(inst));
}

/**
 * The span an interior partition occupies along its host edge — the ONE resolver
 * every consumer (band, fill, dimensions, bench free-spans, the door run) reads,
 * so none of them can disagree about where the partition is.
 *
 * `pos` is clamped so the partition always keeps its full thickness inside the
 * wall. Clamping the two FACES instead collapses them onto each other whenever
 * `pos` has run off the end — which is how a partition left on a stale edge index
 * came out zero-thick. Resolved on read, so a stale value heals by itself.
 */
export function innerWallSpan(inst: ProductInstance, iw: InnerWall): { s: number; t: number; mid: number } | null {
  inst = wallView(inst);
  const e = polyEdges(inst)[iw.edge ?? -1];
  if (!e) return null;
  const h = innerWallThickness(inst, iw) / 2;
  const mid = e.len >= 2 * h ? clamp(iw.pos, h, e.len - h) : e.len / 2;
  return { s: Math.max(0, mid - h), t: Math.min(e.len, mid + h), mid };
}

/**
 * Local-frame band quad of a polygonal room's interior partition wall: a
 * wall-thick strip centred at distance `iw.pos` along its host edge, starting at
 * that edge's inner face and running inward along the edge normal by `iw.length`.
 * Empty when the wall isn't anchored to a valid edge.
 */
export function polyInnerWallBand(inst: ProductInstance, iw: InnerWall): Vec2[] {
  inst = wallView(inst);
  const edges = polyEdges(inst);
  const e = edges[iw.edge ?? -1];
  const span = innerWallSpan(inst, iw);
  if (!e || !span) return [];
  const wall = wallThicknessOf(inst); // host edge inset — where the partition starts
  const len = Math.max(0, iw.length);
  const { s, t } = span;
  const oS = innerAt(e, s, wall), oE = innerAt(e, t, wall);
  const iS = add(oS, mul(e.normal, len)), iE = add(oE, mul(e.normal, len));
  return [oS, oE, iE, iS];
}

/** The partitions' footprints (local frame) that cut a room's WALL benches
 *  where the plan and the DXF draw them (`carvedSeat`'s `clips`): a wall bench
 *  stops at an inner wall — one from another wall running into it, one its
 *  mitre reaches. `pending`: a partition being drawn, cut by too. A partition's
 *  own benches are not cut by them. */
export function partitionClips(inst: ProductInstance, pending?: InnerWall | null): Vec2[][] {
  const out: Vec2[][] = [];
  for (const iw of [...(inst.innerWalls || []), ...(pending ? [pending] : [])]) {
    if (iw.edge == null) continue;
    const band = polyInnerWallBand(inst, iw);
    if (band.length >= 3) out.push(band);
  }
  return out;
}

/**
 * Fill quads of an interior partition wall with its door and glass openings cut
 * out (band minus the passages). Each door or pane on the partition removes a span
 * along its inward length; the remaining solid pieces are returned as quads. Glass
 * cuts the fill for the same reason a door does — the pane is drawn in that gap, so
 * leaving the wall solid behind it would hide it.
 */
export function polyInnerWallFill(inst: ProductInstance, iw: InnerWall): Vec2[][] {
  inst = wallView(inst);
  const band = polyInnerWallBand(inst, iw);
  if (band.length < 4) return [];
  const gaps = innerWallGaps(inst, iw);
  const len = Math.max(0, iw.length);
  if (!gaps.length) return [band];
  const edges = polyEdges(inst);
  const e = edges[iw.edge ?? -1];
  if (!e) return [band];
  const wall = wallThicknessOf(inst);
  const sp = innerWallSpan(inst, iw);
  if (!sp) return [band];
  const { s, t } = sp;
  // The two long faces of the partition (at pos∓h across the host edge), as lines
  // running inward; a solid span [a,b] along the inward length gives one quad.
  const faceA = (d: number): Vec2 => add(innerAt(e, s, wall), mul(e.normal, d));
  const faceB = (d: number): Vec2 => add(innerAt(e, t, wall), mul(e.normal, d));
  return solidSpans(len, gaps).map(([a, b]) => [faceA(a), faceB(a), faceB(b), faceA(b)]);
}

/** A bench band on one face of a polygonal room's interior partition wall. */
export interface PolyInnerBenchBand {
  iwIndex: number;
  face: 'a' | 'b';
  index: number;
  id: string;
  leg: BenchLeg;
  level: BenchTier;
  /** Filled local-frame quad of the band (tucked under the tier behind it). */
  poly: Vec2[];
  /** The same quad as a sweep reads it: `[backStart, backEnd, frontEnd,
   *  frontStart]`, its back on the partition face (or the tier behind). */
  sweep: Vec2[];
  /** How far a profiled tier rises onto the one in front (`profileStep`). */
  lift: number;
  /** The run's length: the partition's inward length. */
  len: number;
  /** The plan point `s` mm along the run (inward from the host wall's inner
   *  face) and `d` mm from the band's back toward its front — the slat frame,
   *  from where its FRAME starts. */
  at: (s: number, d: number) => Vec2;
  /** Where its seat starts at `d` mm from its back, in `at`'s `s`: 0 but where
   *  its seat and frame part — under a mitre with the wall's bench (the seat's
   *  start slants, the frames meet unmitred), or where its seat butts the wall
   *  bench's front and its frame runs on to that bench's frame. */
  seatStart?: (d: number) => number;
  /** The mitre line at its start where its frame runs through under a slatted
   *  mitre (`outer` on its back, `inner` on its front): its last stud lies on it. */
  mitre?: { outer: Vec2; inner: Vec2 };
  /** The wall bench whose front board its frame runs on under at its start
   *  (`PolyBenchBand.under`). */
  under?: string;
  /** How far it slides under the tier toward the partition (the overlap tuck). */
  tuck: number;
}

/**
 * Bench bands for the interior partition walls of a polygonal room. Each wall
 * face ('a' = toward decreasing distance along its host edge, 'b' = increasing)
 * carries a stack of tiers that step outward from the wall face and run the
 * wall's inward length. Mirrors the rectangular room's `innerWallTierBoxes`, but
 * in the polygon's local frame using the host edge's direction/normal. Reuses
 * the shared `InnerWall.benches` model so the two room types stay in step.
 */
export function polyInnerBenchBands(inst: ProductInstance): PolyInnerBenchBand[] {
  inst = wallView(inst);
  const edges = polyEdges(inst);
  if (!edges.length) return [];
  const wall = wallThicknessOf(inst);
  const out: PolyInnerBenchBand[] = [];
  const junctions = partitionJunctions(inst);
  (inst.innerWalls || []).forEach((iw, iwIndex) => {
    const e = edges[iw.edge ?? -1];
    if (!e) return;
    const sp = innerWallSpan(inst, iw); // benches start at the partition's own faces
    if (!sp) return;
    const len = Math.max(0, iw.length);
    if (len <= 1) return;
    // Local point at distance `along` on the host edge's inner face, moved inward
    // (along the edge normal) by `nd` — the bench runs the wall's inward length.
    // Both clamped to the host wall's inner face (corner to corner), so a bench
    // whose face runs toward a near corner stops at the wall there — its band, and
    // its slats, frame and trestles (`at`) with it.
    const aLo = (e.ia.x - e.a.x) * e.dir.x + (e.ia.y - e.a.y) * e.dir.y;
    const aHi = (e.ib.x - e.a.x) * e.dir.x + (e.ib.y - e.a.y) * e.dir.y;
    const P = (along: number, nd: number): Vec2 => add(innerAt(e, clamp(along, aLo, aHi), wall), mul(e.normal, nd));
    for (const face of ['a', 'b'] as const) {
      const sgn = face === 'a' ? -1 : 1;
      const J = junctions.find((j) => j.iwIndex === iwIndex && j.face === face);
      for (const t of partitionFaceTiers(iw, face)) {
        const { leg, index, depth } = t;
        // Face 'a' extends toward decreasing `along` from the partition's near
        // face; 'b' toward increasing `along` from its far one.
        const near = face === 'a' ? sp.s - t.cum : sp.t + t.cum;
        const far = near + sgn * depth;
        const lo = Math.min(near, far), hi = Math.max(near, far);
        if (hi - lo < 1) continue;
        // Where it meets the host wall's benches, it starts at the one it yields to
        // (`partitionTierEnd`), else at the wall itself; mitred, its seat's back
        // and front start apart and its frame where the frames meet.
        const cut = J ? partitionTierEnd(J, index) : null;
        const s0 = cut ? cut.frame : 0;
        if (len - s0 < 1) continue;
        const sNear = cut ? cut.back : 0, sFar = cut ? cut.front : 0;
        const sAt = (along: number): number => (along === near ? sNear : sFar);
        const poly = [P(lo, sAt(lo)), P(hi, sAt(hi)), P(hi, len), P(lo, len)];
        const sweep = [P(near, sNear), P(near, len), P(far, len), P(far, sFar)];
        const at = (s: number, d: number): Vec2 => add(outerAt(e, clamp(near + sgn * d, aLo, aHi)), mul(e.normal, wall + s0 + s));
        const band: PolyInnerBenchBand = { iwIndex, face, index, id: leg.id ?? '', leg, level: t.level, poly, sweep, lift: t.lift, len: len - s0, at, tuck: t.tuck };
        if (cut && (cut.mitre || Math.abs(sNear - s0) > EPS)) band.seatStart = (d) => sNear - s0 + ((sFar - sNear) * d) / (depth || 1);
        if (cut?.mitre && cut.wins) band.mitre = { outer: at(sNear - s0, 0), inner: at(sFar - s0, depth) };
        if (cut && !cut.mitre && !cut.wins && Math.abs(sNear - s0) > EPS && cut.other) band.under = cut.other;
        out.push(band);
      }
    }
  });
  return out;
}

/** A partition face's bench tiers, out from the face: each one's wall-side face
 *  (`cum`, mm from the partition face, its tuck under the tier behind applied), its
 *  depth, tuck, lift and level — what its band is laid from. */
function partitionFaceTiers(iw: InnerWall, face: 'a' | 'b'): Array<{ leg: BenchLeg; index: number; cum: number; depth: number; tuck: number; lift: number; level: BenchTier }> {
  const tiers = iw.benches?.[face] ?? [];
  const count = tiers.length;
  // Two profiled tiers meet where their profiles say (profileStep): the inner
  // one's back at the outer one's foot, the outer one raised onto it.
  const steps = tiers.map((leg, k) => {
    const up = tiers[k - 1]?.profile;
    return k > 0 && up && leg.profile ? profileStep(up, leg.profile) : null;
  });
  const lifts = tiers.map(() => 0);
  for (let k = count - 2; k >= 0; k--) lifts[k] = steps[k + 1] ? lifts[k + 1] + steps[k + 1]!.dy : 0;
  // Tiers accumulate out from the face; a slatted tier slides under the higher
  // slab behind it by its overlap, as a wall's do (`cumulativeTuck`).
  const nat: number[] = [];
  tiers.reduce((acc, leg, k) => { nat[k] = acc; return acc + Math.max(0, leg.depth); }, 0);
  const tuck = cumulativeTuck(count, {
    rank: (k) => autoTierRank(k, count),
    hasSlab: (k) => benchThickness(tiers[k]) != null,
    overlap: (k) => benchOverlap(tiers[k], k, tiers[k - 1]),
    maxOffset: (k) => nat[k],
    fixed: (k, prev) => (steps[k] ? prev + nat[k] - nat[k - 1] - steps[k]!.dx : null),
  });
  return tiers.map((leg, index) => ({
    leg, index, cum: nat[index] - tuck[index], depth: Math.max(0, leg.depth), tuck: tuck[index],
    lift: leg.profile ? lifts[index] : 0, level: RANK_LEVEL[autoTierRank(index, count)],
  }));
}

/** Where a partition face's benches meet the benches of its host wall, at the
 *  partition's foot (a polygonal room, wall view): the wall's tiers that end on
 *  that face (`station`, along the wall), wall-side first, and the partition's,
 *  each by its two faces — the wall's measured out from the wall (tuck applied),
 *  the partition's out from the partition. */
interface PartitionJunction {
  iwIndex: number;
  face: 'a' | 'b';
  edge: number;
  station: number;
  /** Each side's tiers: wall-side and front faces, and the frame's front. */
  wall: Array<{ id: string; back: number; front: number; frame: number }>;
  part: Array<{ id: string; back: number; front: number; frame: number }>;
  /** Tier `t` is swapped: the partition's bench runs through (under a mitre,
   *  its frame). */
  swapped: (t: number) => boolean;
  /** The two benches of tier `t` can mitre (`partitionMitreable`). */
  canMitre: (t: number) => boolean;
  /** Tier `t` mitres: set so, and the two benches can. */
  mitred: (t: number) => boolean;
}

/** Whether a wall bench and a partition's bench can mitre at the partition's
 *  foot: slatted, unprofiled, identical as two benches at a room corner must be,
 *  and laid on the same faces out from each side. */
function partitionMitreable(wall: Pick<BenchLeg, 'depth' | 'slats' | 'profile'> | undefined, leg: BenchLeg, w: { back: number; front: number }, q: { back: number; front: number }): boolean {
  if (!wall?.slats?.length || wall.profile || leg.profile) return false;
  if (Math.abs(w.back - q.back) > EPS || Math.abs(w.front - q.front) > EPS) return false;
  return signatureOf(wall as BenchLeg) === signatureOf(leg);
}

function partitionJunctions(inst: ProductInstance): PartitionJunction[] {
  inst = wallView(inst);
  const g = benchGeom(inst);
  if (!g) return [];
  const tuck = edgeTuckShifts(inst, g);
  const out: PartitionJunction[] = [];
  const pbOf = (id: string) => (inst.polyBenches || []).find((b) => b.id === id);
  (inst.innerWalls || []).forEach((iw, iwIndex) => {
    const edge = iw.edge;
    if (edge == null || !iw.benches) return;
    const sp = innerWallSpan(inst, iw);
    if (!sp) return;
    const list = g.byEdge.get(edge) ?? [];
    for (const face of ['a', 'b'] as const) {
      const station = face === 'a' ? sp.s : sp.t;
      const wall = list.map((b, k) => ({ b, k }))
        .filter(({ b }) => Math.abs((face === 'a' ? b.end : b.start) - station) <= 1)
        .map(({ b, k }) => {
          const sh = tuck.get(`${edge}:${k}`) ?? 0, back = g.cumOff(edge, k) - sh, pb = pbOf(b.id);
          return { id: b.id, back, front: g.innerOff(edge, k) - sh, frame: back + (pb ? frameDepthOf(pb) : b.depth) };
        })
        .sort((x, y) => x.back - y.back);
      const tiers = partitionFaceTiers(iw, face);
      const part = tiers.map((t) => ({ id: t.leg.id ?? '', back: t.cum, front: t.cum + t.depth, frame: t.cum + frameDepthOf(t.leg) }));
      if (!wall.length || !part.length) continue;
      const canMitre = (t: number): boolean => t >= 0 && t < wall.length && t < part.length
        && partitionMitreable(pbOf(wall[t].id), tiers[t].leg, wall[t], part[t]);
      out.push({
        iwIndex, face, edge, station, wall, part, canMitre,
        swapped: (t) => !!iw.benchJoinSwaps?.[face]?.includes(t),
        mitred: (t) => !!iw.benchJoinMitres?.[face]?.includes(t) && canMitre(t),
      });
    }
  });
  return out;
}

/** Where one bench stops at a partition's foot, mm out from the other's wall
 *  (the partition face for a wall bench, the wall for a partition's): its seat's
 *  wall-side face at `back`, its front at `front` — apart under a mitre — and its
 *  frame at `frame`; `wins` its frame runs through. */
interface PartitionEnd { back: number; front: number; frame: number; mitre: boolean; wins: boolean; other?: string }

/** How bench tier `t` of one side ends against the other side's tiers `other`:
 *  as two benches at a room corner, tier for tier the one that wins runs to the
 *  other's wall-side face and the other's seat butts its front, the other's
 *  frame running on under the winner's front board to the winner's frame; a tier
 *  with no partner butts the other side's outermost. Mitred, the seat runs from
 *  the other's wall-side face to its front, the frames as the winner / butt rule
 *  says. */
function junctionEnd(other: Array<{ id: string; back: number; front: number; frame: number }>, t: number, wins: boolean, mitre: boolean): PartitionEnd {
  const n = other.length;
  if (t >= n) { const f = other[n - 1].front; return { back: f, front: f, frame: f, mitre: false, wins: false }; }
  const o = other[t];
  if (mitre) return { back: o.back, front: o.front, frame: wins ? o.back : o.frame, mitre, wins };
  const at = wins ? o.back : o.front;
  return { back: at, front: at, frame: wins ? at : o.frame, mitre, wins, other: o.id };
}

/** Where wall tier `t` stops, out from the partition face. By default the
 *  wall's bench wins (it runs through). */
function wallTierEnd(J: PartitionJunction, t: number): PartitionEnd {
  return junctionEnd(J.part, t, !J.swapped(t), J.mitred(t));
}

/** Where partition tier `t` starts along the partition, out from the host
 *  wall: the twin of `wallTierEnd`. */
function partitionTierEnd(J: PartitionJunction, t: number): PartitionEnd {
  return junctionEnd(J.wall, t, J.swapped(t), J.mitred(t));
}

/** The joint where wall tier and partition tier `t` meet (local frame), the
 *  square both would cover — what the Bench tool's click on it swaps. */
function partitionJointPoly(inst: ProductInstance, J: PartitionJunction, t: number): Vec2[] {
  const e = polyEdges(inst)[J.edge];
  const wall = wallThicknessOf(inst);
  const sgn = J.face === 'a' ? -1 : 1;
  const a0 = J.station + sgn * J.part[t].back, a1 = J.station + sgn * J.part[t].front;
  const P = (along: number, nd: number): Vec2 => add(add(outerAt(e, along), mul(e.normal, wall)), mul(e.normal, nd));
  return [P(a0, J.wall[t].back), P(a1, J.wall[t].back), P(a1, J.wall[t].front), P(a0, J.wall[t].front)];
}

/** The joint of a wall bench and a partition's bench under `p` (local frame), or
 *  null: a tier both sides have, at the partition's foot. */
export function partitionBenchJointAt(inst: ProductInstance, p: Vec2): { iwIndex: number; face: 'a' | 'b'; tier: number } | null {
  const view = wallView(inst);
  for (const J of partitionJunctions(view)) {
    for (let t = 0; t < Math.min(J.wall.length, J.part.length); t++) {
      if (pointInPolygon(p, partitionJointPoly(view, J, t))) return { iwIndex: J.iwIndex, face: J.face, tier: t };
    }
  }
  return null;
}

/** The highlight of a wall / partition bench joint (local frame), or none. */
export function partitionBenchJointHighlight(inst: ProductInstance, iwIndex: number, face: 'a' | 'b', tier: number): Vec2[][] {
  const view = wallView(inst);
  const J = partitionJunctions(view).find((j) => j.iwIndex === iwIndex && j.face === face);
  return J && tier < Math.min(J.wall.length, J.part.length) ? [partitionJointPoly(view, J, tier)] : [];
}

/** The stored state of a wall / partition bench joint (`BenchJoinState`). */
export function partitionBenchJoinState(inst: ProductInstance, iwIndex: number, face: 'a' | 'b', tier: number): BenchJoinState {
  const iw = inst.innerWalls?.[iwIndex];
  const swapped = !!iw?.benchJoinSwaps?.[face]?.includes(tier), mitre = !!iw?.benchJoinMitres?.[face]?.includes(tier);
  return mitre ? (swapped ? 'mitreSwapped' : 'mitre') : swapped ? 'swapped' : 'default';
}

/** Whether the wall's and the partition's bench of a joint can mitre. */
export function partitionBenchCanMitre(inst: ProductInstance, iwIndex: number, face: 'a' | 'b', tier: number): boolean {
  const J = partitionJunctions(inst).find((j) => j.iwIndex === iwIndex && j.face === face);
  return !!J && J.canMitre(tier);
}

/** Store a wall / partition bench joint in state `s`. */
export function setPartitionBenchJoin(inst: ProductInstance, iwIndex: number, face: 'a' | 'b', tier: number, s: BenchJoinState): void {
  const iw = inst.innerWalls?.[iwIndex];
  if (!iw) return;
  const put = (key: 'benchJoinSwaps' | 'benchJoinMitres', on: boolean): void => {
    const sets = iw[key] ?? {};
    const list = (sets[face] ?? []).filter((t) => t !== tier);
    if (on) list.push(tier);
    if (list.length) sets[face] = list;
    else delete sets[face];
    if (sets.a || sets.b) iw[key] = sets;
    else delete iw[key];
  };
  put('benchJoinSwaps', joinSwapped(s));
  put('benchJoinMitres', joinMitred(s));
}

/** Step a wall / partition bench joint on, as a click with the Bench tool
 *  does — the wall's runs through, the partition's does, the two mitre on the
 *  wall's frame, on the partition's; the first two only where they can't mitre. */
export function togglePartitionBenchJoin(inst: ProductInstance, iwIndex: number, face: 'a' | 'b', tier: number): void {
  const s = partitionBenchJoinState(inst, iwIndex, face, tier);
  setPartitionBenchJoin(inst, iwIndex, face, tier, nextBenchJoin(s, partitionBenchCanMitre(inst, iwIndex, face, tier)));
}

/** Partition benches in the plan's paint order: on each face the tier furthest
 *  from the partition first, the one against it last — on top of the tiers it
 *  tucks over, as `benchDrawOrder` paints a wall's. A pick reads it backwards. */
export function innerBenchPaintOrder<T extends { iwIndex: number; face: 'a' | 'b'; index: number }>(bands: T[]): T[] {
  return [...bands].sort((a, b) => a.iwIndex - b.iwIndex || a.face.localeCompare(b.face) || b.index - a.index);
}

/** A partition bench's slat quads (local frame), the gaps left empty — each
 *  `[start, end]` on its back face, then on its front; [] with no slats. */
export function polyInnerBenchSlatQuads(b: PolyInnerBenchBand): Vec2[][] {
  if (!b.leg.slats?.length || b.leg.profile || b.len < 1) return [];
  const s0 = (d: number): number => b.seatStart?.(d) ?? 0;
  return benchSlatSpans(b.leg).map((s) => [b.at(s0(s.off), s.off), b.at(b.len, s.off), b.at(b.len, s.off + s.width), b.at(s0(s.off + s.width), s.off + s.width)]);
}

/** Cross product z-component of 2D vectors. */
const cross = (u: Vec2, v: Vec2): number => u.x * v.y - u.y * v.x;

/**
 * Inward span (mm) available to a partition on edge `edge` at distance `pos`:
 * the distance from the host edge's inner face along its normal to the first
 * crossing of the opposite inner wall. Lets a partition snap across the room to
 * merge with the far wall. Zero when no crossing is found.
 */
export function polyInnerWallSpan(inst: ProductInstance, edge: number, pos: number): number {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return 0;
  const wall = wallThicknessOf(inst);
  const origin = add(outerAt(e, clamp(pos, 0, e.len)), mul(e.normal, wall));
  const inner = polyInner(inst);
  let best = Infinity;
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i], b = inner[(i + 1) % inner.length];
    const seg = sub(b, a);
    const denom = cross(e.normal, seg);
    if (Math.abs(denom) < 1e-9) continue;
    const diff = sub(a, origin);
    const t = cross(diff, seg) / denom;
    const s = cross(diff, e.normal) / denom;
    if (t > 1 && s >= -1e-6 && s <= 1 + 1e-6 && t < best) best = t;
  }
  return best === Infinity ? 0 : best;
}

/**
 * Stretches of `edge` a bench may occupy: the whole edge, minus the footprint of
 * every interior partition rooted on it. A bench must MEET a partition but never
 * overlap it, so each free span runs from one obstruction to the next.
 */
export function benchFreeSpans(inst: ProductInstance, edge: number): Array<[number, number]> {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return [];
  // Each partition on this edge blocks its own thickness, centred on iw.pos.
  const blocks = (inst.innerWalls || [])
    .filter((iw) => (iw.edge ?? -1) === edge)
    .map((iw) => {
      const sp = innerWallSpan(inst, iw);
      return (sp ? [sp.s, sp.t] : [0, 0]) as [number, number];
    })
    .sort((a, b) => a[0] - b[0]);
  const spans: Array<[number, number]> = [];
  let cursor = 0;
  for (const [b0, b1] of blocks) {
    if (b0 - cursor > EPS) spans.push([cursor, b0]);
    cursor = Math.max(cursor, b1);
  }
  if (e.len - cursor > EPS) spans.push([cursor, e.len]);
  return spans;
}

/** Where each partition stands on its host wall, by id: its two faces (`s`, `t`,
 *  mm along the wall, as `innerWallSpan`) — the ends of the benches either side. */
export type PartitionFaces = Map<string, { edge: number; s: number; t: number }>;

export function partitionFaces(inst: ProductInstance): PartitionFaces {
  const out: PartitionFaces = new Map();
  for (const iw of inst.innerWalls ?? []) {
    const sp = iw.id != null && iw.edge != null ? innerWallSpan(inst, iw) : null;
    if (sp) out.set(iw.id!, { edge: iw.edge!, s: sp.s, t: sp.t });
  }
  return out;
}

/** After partitions moved or changed thickness (`before`, `partitionFaces` as they
 *  stood), the wall benches that ended on one of their faces end on it again — a
 *  bench a partition split goes with it, one shortened short of it stays put.
 *  `ends` overrides a bench's ends as they stood (a resize scaled them since).
 *  True when any moved. */
export function benchesFollowPartitions(inst: ProductInstance, before: PartitionFaces, ends?: Map<string, [number | undefined, number | undefined]>): boolean {
  const now = partitionFaces(inst);
  let changed = false;
  for (const b of inst.polyBenches ?? []) {
    const [s0, e0] = ends?.get(b.id) ?? [b.start, b.end];
    for (const [id, was] of before) {
      const is = now.get(id);
      if (!is || was.edge !== b.edge || is.edge !== b.edge) continue;
      if (e0 != null && Math.abs(e0 - was.s) <= 1 && b.end !== is.s) { b.end = is.s; changed = true; }
      if (s0 != null && Math.abs(s0 - was.t) <= 1 && b.start !== is.t) { b.start = is.t; changed = true; }
    }
  }
  return changed;
}

/** The free span of `edge` containing `along` (null when that point is inside a
 *  partition). Used to bound a newly added bench to the side that was clicked. */
export function benchSpanAt(inst: ProductInstance, edge: number, along: number): [number, number] | null {
  for (const sp of benchFreeSpans(inst, edge)) {
    if (along >= sp[0] - EPS && along <= sp[1] + EPS) return sp;
  }
  return null;
}

/** Whether a bench already occupies (overlaps) `span` on `edge` — the test that
 *  replaces "one bench per edge" now that each side of a partition holds its own. */
export function benchOccupies(inst: ProductInstance, edge: number, span: [number, number]): boolean {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return false;
  return (inst.polyBenches || []).some((b) => {
    if (b.edge !== edge) return false;
    const s = clamp(b.start ?? 0, 0, e.len), t2 = clamp(b.end ?? e.len, 0, e.len);
    return s < span[1] - 1 && span[0] < t2 - 1;
  });
}

/**
 * Re-fit an edge's benches to the stretches of wall actually available, so a
 * bench always MEETS an interior partition and never overlaps it.
 *
 * One rule covers both directions:
 *  - a partition added through a bench leaves that bench overlapping two free
 *    spans, so it is re-emitted once per span (SPLIT), each piece inheriting the
 *    original's depth, slats, height and colour;
 *  - a partition removed leaves the two benches either side both overlapping the
 *    single reunited span, so one bench is emitted covering it (MERGE) — taking
 *    the first piece's settings, as agreed.
 *
 * Stacked tiers survive because tiers overlap each other's span: the number of
 * benches emitted for a span is the deepest stack that reaches into it.
 * Returns true when anything changed.
 */
type PolyBench = NonNullable<ProductInstance['polyBenches']>[number];

/**
 * Re-index the bench list after a vertex is inserted at distance `splitAt` along
 * edge `edge` (of outer length `edgeLen`). Later edges shift up by one; a bench
 * on the split edge is copied onto BOTH halves with its span clipped, so the two
 * new walls keep the configuration the one wall had. A copy that covers its whole
 * half loses `start`/`end` and stays full-span, so it keeps growing with the wall.
 */
export function splitBenchesAtVertex(
  benches: PolyBench[], edge: number, splitAt: number, edgeLen: number, newId: (n: number) => string,
): PolyBench[] {
  const EPS = 0.5;
  const half = (b: PolyBench, lo: number, hi: number, e: number): PolyBench | null => {
    const s = Math.max(b.start ?? 0, lo), t = Math.min(b.end ?? edgeLen, hi);
    if (t - s < 1) return null;
    const full = s <= lo + EPS && t >= hi - EPS;
    return { ...b, edge: e, ...(full ? { start: undefined, end: undefined } : { start: s - lo, end: t - lo }) };
  };
  const out: PolyBench[] = [];
  let seq = 0;
  for (const b of benches) {
    if (b.edge !== edge) { out.push(b.edge > edge ? { ...b, edge: b.edge + 1 } : b); continue; }
    const first = half(b, 0, splitAt, edge);
    const second = half(b, splitAt, edgeLen, edge + 1);
    if (first) out.push(first);
    if (second) out.push({ ...second, id: first ? newId(seq++) : b.id });
  }
  return out;
}

export function reconcileBenchesWithPartitions(room: ProductInstance): boolean {
  // Read through the wall view, but WRITE to the room: a view is a copy.
  const inst = wallView(room);
  const benches = inst.polyBenches;
  if (!benches || !benches.length) return false;
  const edges = polyEdges(inst);
  const SEPS = 1;
  const out: NonNullable<ProductInstance['polyBenches']> = [];
  let changed = false;
  let seq = 0;

  const byEdge = new Map<number, typeof benches>();
  for (const b of benches) {
    if (b.edge < 0 || b.edge >= edges.length) { out.push(b); continue; }
    const list = byEdge.get(b.edge) ?? [];
    list.push(b);
    byEdge.set(b.edge, list);
  }

  for (const [edge, list] of byEdge) {
    const len = edges[edge].len;
    const spanOf = (b: (typeof list)[number]): [number, number] =>
      [clamp(b.start ?? 0, 0, len), clamp(b.end ?? len, 0, len)];
    // Stack position of each bench: how many earlier benches share its stretch.
    const tierOf = list.map((b, i) => {
      const [s, e] = spanOf(b);
      let t = 0;
      for (let k = 0; k < i; k++) {
        const [s2, e2] = spanOf(list[k]);
        if (s2 < e - SEPS && s < e2 - SEPS) t++;
      }
      return t;
    });
    for (const sp of benchFreeSpans(inst, edge)) {
      // Every tier that reaches into this span, in tier order; the first bench at
      // each tier supplies the settings, and the tier's benches there their extent,
      // clipped to the span — never stretched to fill it: a bench shortened short
      // of a partition or a corner stays short (`benchesFollowPartitions` keeps the
      // ones that end on a partition on its face), and the two pieces of a bench a
      // partition split go back together once it is gone.
      const perTier = new Map<number, { src: (typeof list)[number]; lo: number; hi: number }>();
      list.forEach((b, i) => {
        const [s, e] = spanOf(b);
        if (!(s < sp[1] - SEPS && sp[0] < e - SEPS)) return;
        const t = perTier.get(tierOf[i]);
        if (t) { t.lo = Math.min(t.lo, s); t.hi = Math.max(t.hi, e); } else perTier.set(tierOf[i], { src: b, lo: s, hi: e });
      });
      for (const tier of [...perTier.keys()].sort((a, b) => a - b)) {
        const { src, lo, hi } = perTier.get(tier)!;
        const p0 = Math.max(sp[0], lo), p1 = Math.min(sp[1], hi);
        const full = p0 <= SEPS && p1 >= len - SEPS;
        const next = { ...src, ...(full ? { start: undefined, end: undefined } : { start: p0, end: p1 }) };
        const [os, oe] = spanOf(src);
        if (Math.abs(os - p0) > SEPS || Math.abs(oe - p1) > SEPS) {
          changed = true;
          // A split piece needs its own id; the first keeps the original's, and
          // its strips light both.
          if (out.some((o) => o.id === src.id)) {
            next.id = `pb-${src.id}-${seq++}`;
            lightsOnHostSplit(room, src.id, next.id, (id) => `${id}-${next.id}`);
          }
        }
        out.push(next);
      }
    }
    if (out.filter((o) => o.edge === edge).length !== list.length) changed = true;
  }

  if (changed) room.polyBenches = out;
  return changed;
}

/**
 * The footprint(s) a bench the click would add on `edge` takes — the green add
 * preview. A span-less tier (or one clipped to `span`) is appended to a clone and
 * reconciled exactly as the placement path does, so a tier a partition splits
 * previews as the same separate pieces the click will create, never a single
 * band the click won't actually make. `like` is the bench the click adds (a
 * steam room's carries its profile, which sets its mitre and tuck), or its depth;
 * the footprint is where it will be DRAWN, tuck and all.
 */
export function benchTierPreview(
  inst: ProductInstance, edge: number, like: number | Omit<PolyBench, 'id' | 'edge'>, span?: [number, number] | null,
): Vec2[][] {
  const bench = { ...(typeof like === 'number' ? { depth: like } : like), id: '__preview__', edge, ...(span ? { start: span[0], end: span[1] } : {}) };
  const clone = { ...inst, polyBenches: [...(inst.polyBenches || []), bench] } as ProductInstance;
  reconcileBenchesWithPartitions(clone); // split the tier the way the click will
  const ids = new Set((clone.polyBenches || [])
    .filter((b) => b.id === '__preview__' || b.id.startsWith('pb-__preview__'))
    .map((b) => b.id));
  return polyBenchBandsDrawn(clone).filter((b) => ids.has(b.id)).map((b) => b.poly);
}

/** Outer length of every polygon edge, in edge order. */
export function polyEdgeLengths(inst: ProductInstance): number[] {
  return polyEdges(inst).map((e) => e.len);
}

/**
 * Keep edge-anchored features in proportion when a wall changes length — e.g.
 * while a room vertex is dragged. Everything positioned by a distance ALONG an
 * edge (glass panes, openings, interior partition positions, part-span benches)
 * is scaled by that edge's length ratio, so a pane that sat mid-wall at a third
 * of its width stays mid-wall at a third of the new width.
 *
 * `oldLens` are the edge lengths captured BEFORE the polygon was mutated; edges
 * whose length is unchanged (or that collapsed to nothing) are left alone.
 * Returns how the ring's vertices were renumbered (a collapsed figure takes its
 * jambs with it), for `followVertices` (anchors.ts); null when nothing changed.
 */
export function rescaleEdgeFeatures(inst: ProductInstance, oldLens: number[]): VertexRemap | null {
  if (!isPolyRoom(inst)) return null;
  let remap = identityRemap;
  const newLens = polyEdgeLengths(inst);
  let changed = false;
  const n0 = inst.polygon!.length;
  const walls = wallRuns(inst).length;
  let firstMoved = -1;
  for (let w = 0; w < walls; w++) {
    const run = wallRuns(inst)[w];
    // Any segment of the wall that moved — a corner drag also slides the jambs a
    // little, even on a wall whose overall length held.
    if (!run.segs.some((i) => oldLens[i] == null || Math.abs(oldLens[i] - newLens[i]) > 1e-6)) continue;
    const before = run.segs.reduce((a, i) => a + (oldLens[i] ?? 0), 0), after = run.len;
    // Guard a degenerate wall: scaling by a ratio from ~0 would fling features.
    if (before < 1 || after < 1) continue;
    // The wall's chain as it stood BEFORE, re-solved at its new length: pinned
    // figures and doors (held by default) keep their length, everything free —
    // panes and openings included — scales in proportion, equal groups as units.
    // Then the wall is re-laid, which puts every jamb back where the chain says.
    const spans = wallChain(inst, w, { segLens: oldLens });
    if (spans.some((sp) => sp.kind !== 'solid')) {
      remap = composeRemap(remap, writeWallChain(inst, w, spans, solveWallSpans(spans, after)));
      changed = true;
      if (firstMoved < 0) firstMoved = w;
      // A figure collapsed and the ring lost a jamb: the old lengths no longer
      // line up with the segments, so leave the remaining walls to the next pass.
      if (inst.polygon!.length !== n0) return remap;
    }
    const r = after / before;
    if (Math.abs(r - 1) < 1e-9) continue;
    // Benches and partitions are hosted by WALL and keep their relative station —
    // but a bench end on a partition's face stays on it (the partition keeps its
    // thickness: its faces don't scale with its centre).
    const faces = partitionFaces(inst);
    const ends = new Map((inst.polyBenches || []).filter((b) => b.edge === w).map((b) => [b.id, [b.start, b.end] as [number | undefined, number | undefined]]));
    for (const b of inst.polyBenches || []) {
      if (b.edge !== w) continue;
      if (typeof b.start === 'number') b.start *= r;
      if (typeof b.end === 'number') b.end *= r;
      changed = true;
    }
    for (const iw of inst.innerWalls || []) {
      if ((iw.edge ?? -1) !== w) continue;
      iw.pos *= r;
      changed = true;
    }
    benchesFollowPartitions(inst, faces, ends);
    for (const s of inst.benchSupports || []) {
      if (s.edge !== w) continue;
      s.at *= r;
      changed = true;
    }
    for (const br of inst.backrests || []) {
      if (!('wall' in br.host) || br.host.wall !== w) continue;
      if (typeof br.start === 'number') br.start *= r;
      if (typeof br.end === 'number') br.end *= r;
      changed = true;
    }
    // A sculpted bench's ends on this wall keep their relative station.
    for (const sb of inst.sculptedBenches || []) {
      for (const end of sculptStations(sb)) {
        if (!('wall' in end.host) || end.host.wall !== w) continue;
        end.s *= r;
        changed = true;
      }
    }
  }
  // Walls scaled by different ratios break an equal group that spans them: the
  // first wall that moved sets the length, the others fit around it.
  if (firstMoved >= 0) remap = composeRemap(remap, propagateEqual(inst, firstMoved));
  return changed ? remap : null;
}

/**
 * Re-index EVERY edge-hosted feature after a vertex was inserted, splitting edge
 * `edge` at `splitAt` (its old length was `edgeLen`). Splitting shifts every later
 * edge up by one, so anything still naming the old index ends up on the wrong wall
 * — a partition that does lands with a `pos` past the end of its new (shorter)
 * wall, where both its faces clamp together and it renders zero-thick.
 *
 * Per feature kind:
 *  - spans (benches, glass, openings) are clipped onto both halves and re-based;
 *  - a heater wall spans its whole edge, so it is copied onto both halves;
 *  - point-hosted things (partitions, doors) go to the half that contains them;
 *  - the ceiling (wall-numbered): its direction `wall` stays with the half
 *    holding the old midpoint, and both halves keep a split wall's `setbacks`
 *    entry (so the flat edge does not move while the new corner is in line).
 *
 * This is the ONE place the rule lives: a new edge-hosted feature that is not
 * handled here silently jumps walls the first time a neighbour is split.
 */
export function insertVertexEdgeFeatures(
  inst: ProductInstance, edge: number, splitAt: number, edgeLen: number, newId: (kind: string) => string,
  wall: { wall: number; at: number; len: number } = { wall: edge, at: splitAt, len: edgeLen },
): void {
  const later = (e: number | undefined): number | undefined => (e != null && e > edge ? e + 1 : e);
  const secondHalf = edgeLen - splitAt;
  // Benches, their corner joins, heater walls and partitions are numbered by WALL,
  // so the new corner splits wall `wall.wall` at station `wall.at` for them. On a
  // room without jambs that is the very same edge and station.
  const W = wall.wall, laterW = (e: number): number => (e > W ? e + 1 : e);

  if (inst.polyBenches) {
    const made = new Set<string>();
    inst.polyBenches = splitBenchesAtVertex(inst.polyBenches, W, wall.at, wall.len, () => { const id = newId('pb'); made.add(id); return id; });
    // A bench split in two keeps its strips on both halves (the second half
    // follows the first in the list).
    inst.polyBenches.forEach((b, k, all) => { if (made.has(b.id) && k > 0) lightsOnHostSplit(inst, all[k - 1].id, b.id, () => newId('lt')); });
  }
  if (inst.polyBenchJoins) {
    inst.polyBenchJoins = inst.polyBenchJoins.map((j) => (j.corner > W ? { ...j, corner: j.corner + 1 } : j));
  }
  // A wall bench's support is a station along its wall, as a bench's ends are;
  // a rounded corner's is numbered by wall, as a join is.
  for (const s of inst.benchSupports || []) {
    if (s.corner != null && s.corner > W) s.corner += 1;
    if (s.edge == null) continue;
    if (s.edge > W) s.edge += 1;
    else if (s.edge === W && s.at > wall.at) { s.edge = W + 1; s.at -= wall.at; }
  }
  if (inst.heaterWalls) {
    const split = inst.heaterWalls.find((h) => h.edge === W);
    inst.heaterWalls = inst.heaterWalls.map((h) => ({ ...h, edge: laterW(h.edge) }));
    if (split) inst.heaterWalls.push({ ...split, id: newId('hw'), edge: W + 1 });
  }
  const halves: Array<[string, string]> = [];
  // A wall's backrest is a span along it: clipped onto both halves (the second
  // a new backrest), the whole wall's onto both; a later wall's shifts up.
  if (inst.backrests) {
    const out: NonNullable<ProductInstance['backrests']> = [];
    for (const br of inst.backrests) {
      const h = br.host;
      if (!('wall' in h) || h.wall < W) { out.push(br); continue; }
      if (h.wall > W) { out.push({ ...br, host: { wall: h.wall + 1 } }); continue; }
      const lo = br.start ?? -Infinity, hi = br.end ?? Infinity;
      if (lo < wall.at - 1) out.push({ ...br, ...(br.end != null ? { end: Math.min(hi, wall.at) } : {}) });
      if (hi > wall.at + 1) {
        const rest: Backrest = { ...br, id: lo < wall.at - 1 ? newId('br') : br.id, host: { wall: W + 1 } };
        if (br.start != null) rest.start = Math.max(0, lo - wall.at); else delete rest.start;
        if (br.end != null) rest.end = hi - wall.at; else delete rest.end;
        out.push(rest);
        // Its strips light both halves.
        if (rest.id !== br.id) halves.push([br.id, rest.id]);
      }
    }
    inst.backrests = out;
  }
  // A sculpted bench's end is a station along its wall: past the new corner it
  // moves onto the second half; a later wall's shifts up. Its run now walks the
  // new (straight) corner too.
  for (const sb of inst.sculptedBenches || []) {
    for (const end of sculptStations(sb)) {
      const h = end.host;
      if (!('wall' in h)) continue;
      if (h.wall > W) end.host = { wall: h.wall + 1 };
      else if (h.wall === W && end.s > wall.at) { end.host = { wall: W + 1 }; end.s -= wall.at; }
    }
  }
  for (const [from, to] of halves) lightsOnHostSplit(inst, from, to, () => newId('lt'));
  // A light along the split wall (a cove, a channel laid along it) runs along both halves.
  lightsOnWallSplit(inst, W);
  // The ceiling's direction wall stays with the half holding its old midpoint;
  // both halves keep a split wall's setback, so the flat edge does not move.
  const c = inst.ceiling;
  if (c) {
    if (c.wall != null) {
      if (c.wall > W) c.wall += 1;
      else if (c.wall === W && wall.at < wall.len / 2) c.wall = W + 1;
    }
    if (c.setbacks && W < c.setbacks.length) c.setbacks.splice(W + 1, 0, c.setbacks[W]);
  }
  // Spans on the split edge: clip to each half, re-base the second, drop slivers.
  const splitSpans = <T extends { edge?: number; start: number; end: number }>(arr: T[] | undefined): T[] | undefined => {
    if (!arr) return arr;
    const out: T[] = [];
    for (const s of arr) {
      if (s.edge !== edge) { out.push({ ...s, edge: later(s.edge) }); continue; }
      const lo = Math.min(s.start, s.end), hi = Math.max(s.start, s.end);
      const a: Array<[number, number, number]> = [
        [Math.max(lo, 0), Math.min(hi, splitAt), edge],
        [Math.max(lo, splitAt) - splitAt, Math.min(hi, edgeLen) - splitAt, edge + 1],
      ];
      for (const [s0, e0, e1] of a) if (e0 - s0 > 1) out.push({ ...s, edge: e1, start: s0, end: e0 });
    }
    return out;
  };
  inst.glass = splitSpans(inst.glass);
  inst.openings = splitSpans(inst.openings);
  // Partitions are a station along the wall: they belong to the half holding them.
  for (const iw of inst.innerWalls || []) {
    if (iw.edge == null) continue;
    if (iw.edge > W) { iw.edge += 1; continue; }
    if (iw.edge !== W) continue;
    const h = innerWallThickness(inst, iw) / 2;
    if (iw.pos <= wall.at) iw.pos = clamp(iw.pos, h, Math.max(h, wall.at - h));
    else { iw.edge = W + 1; iw.pos = clamp(iw.pos - wall.at, h, Math.max(h, wall.len - wall.at - h)); }
  }
  // A door's `offset` is measured from its edge's midpoint, so moving it to a half
  // means re-centring it on that half's length.
  for (const d of inst.doors || []) {
    if (d.innerWallId != null) continue; // hosted on a partition, not an edge
    const e = d.edge ?? 0;
    if (e > edge) { d.edge = e + 1; continue; }
    if (e !== edge) continue;
    const mid = edgeLen / 2 + (d.offset || 0);
    if (mid <= splitAt) { d.edge = edge; d.offset = mid - splitAt / 2; }
    else { d.edge = edge + 1; d.offset = (mid - splitAt) - secondHalf / 2; }
  }
}

/**
 * The mirror of {@link insertVertexEdgeFeatures}: vertex `v` was removed, so edge
 * `v` went with it and every later edge shifted down by one. A feature hosted on
 * the removed edge goes with it — the wall it lived on is gone. The ceiling's
 * removed wall instead MERGES into the previous one, which keeps its own
 * direction `wall` and `setbacks` entry (wall 0 removed: the last wall's).
 */
export function removeVertexEdgeFeatures(inst: ProductInstance, v: number, wall: number | null = v): void {
  const n = inst.polygon?.length ?? 0;
  const walls = wallRuns(inst).length;
  // Wall-numbered features (benches, joins, heater walls, partitions) only move
  // when a CORNER goes — `wall` is the wall that started at it; a jamb (null)
  // leaves every wall where it was.
  const keepW = <T extends { edge?: number }>(arr: T[] | undefined): T[] | undefined => {
    if (!arr || wall == null) return arr;
    return arr
      .filter((f) => f.edge == null || f.edge !== wall)
      .map((f) => (f.edge != null && f.edge > wall ? { ...f, edge: f.edge - 1 } : f))
      .filter((f) => f.edge == null || f.edge < walls);
  };
  // An absent `edge` means "not hosted on an edge" (a door on a partition), NOT
  // edge 0 — coercing it would delete such a door whenever vertex 0 went.
  const keep = <T extends { edge?: number }>(arr: T[] | undefined): T[] | undefined => {
    if (!arr) return arr;
    return arr
      .filter((f) => f.edge == null || f.edge !== v)
      .map((f) => (f.edge != null && f.edge > v ? { ...f, edge: f.edge - 1 } : f))
      .filter((f) => f.edge == null || f.edge < n);
  };
  inst.polyBenches = keepW(inst.polyBenches);
  if (inst.polyBenchJoins && wall != null) {
    inst.polyBenchJoins = inst.polyBenchJoins
      .filter((j) => j.corner !== wall)
      .map((j) => (j.corner > wall ? { ...j, corner: j.corner - 1 } : j))
      .filter((j) => j.corner < walls);
  }
  const goneHeaters = new Set(wall == null ? [] : (inst.heaterWalls || []).filter((h) => h.edge === wall).map((h) => h.id));
  inst.heaterWalls = keepW(inst.heaterWalls);
  if (inst.heaterWalls && !inst.heaterWalls.length) delete inst.heaterWalls;
  // The removed wall merges into the previous one, which keeps its own ceiling
  // direction and setback.
  const c = inst.ceiling;
  if (c && wall != null) {
    if (c.wall != null) c.wall = c.wall > wall ? c.wall - 1 : c.wall === wall ? (wall > 0 ? wall - 1 : walls - 1) : c.wall;
    if (c.setbacks && wall < c.setbacks.length) c.setbacks.splice(wall, 1);
  }
  inst.glass = keep(inst.glass);
  inst.openings = keep(inst.openings);
  const goneWalls = new Set(wall == null ? [] : (inst.innerWalls || []).filter((iw) => iw.edge === wall).map((iw) => iw.id));
  inst.innerWalls = keepW(inst.innerWalls);
  // A pane glazed into a partition that just went with the removed wall goes too.
  if (inst.glass) inst.glass = inst.glass.filter((g) => g.innerWallId == null || !goneWalls.has(g.innerWallId));
  // A wall support follows its wall; a partition's goes with the partition; a
  // rounded corner's goes with its corner, a later one's shifts down.
  inst.benchSupports = keepW(inst.benchSupports)?.filter((s) => s.innerWallId == null || !goneWalls.has(s.innerWallId))
    .flatMap((s) => (s.corner == null || wall == null ? [s] : s.corner === wall ? [] : s.corner > wall ? [{ ...s, corner: s.corner - 1 }] : [s]))
    .filter((s) => s.corner == null || s.corner < walls);
  // A backrest goes with its wall, heater wall or partition; a later wall's shifts down.
  if (inst.backrests && wall != null) {
    inst.backrests = inst.backrests.flatMap((br): Backrest[] => {
      const h = br.host;
      if ('wall' in h) return h.wall === wall || h.wall - (h.wall > wall ? 1 : 0) >= walls ? [] : [h.wall > wall ? { ...br, host: { wall: h.wall - 1 } } : br];
      if ('heaterWall' in h) return goneHeaters.has(h.heaterWall) ? [] : [br];
      return goneWalls.has(h.partition) ? [] : [br];
    });
    if (!inst.backrests.length) delete inst.backrests;
  }
  // A sculpted bench goes with a wall or a partition one of its ends is on; an
  // end on a later wall shifts down.
  if (inst.sculptedBenches && wall != null) {
    inst.sculptedBenches = inst.sculptedBenches.filter((sb) => [sb.start, sb.end].every((end) => {
      const h = end.host;
      if ('partition' in h) return !goneWalls.has(h.partition);
      return h.wall !== wall && h.wall - (h.wall > wall ? 1 : 0) < walls;
    }));
    for (const sb of inst.sculptedBenches) {
      // A trestle on the removed wall or partition goes; the bench stays.
      if (sb.trestles) {
        sb.trestles = sb.trestles.filter((t) => ('partition' in t.host ? !goneWalls.has(t.host.partition) : t.host.wall !== wall));
        if (!sb.trestles.length) delete sb.trestles;
      }
      for (const end of sculptStations(sb)) if ('wall' in end.host && end.host.wall > wall) end.host = { wall: end.host.wall - 1 };
    }
    if (!inst.sculptedBenches.length) delete inst.sculptedBenches;
  }
  if (inst.benchSupports && !inst.benchSupports.length) delete inst.benchSupports;
  // A light along walls (a cove, a channel laid along them): the removed one
  // leaves it, a later one shifts down.
  if (wall != null) lightsOnWallRemoved(inst, wall, walls);
  // Doors: one hosted on a partition survives unless that partition just went with
  // the removed wall; an edge-hosted one follows the edge rule like everything else.
  inst.doors = keep((inst.doors || []).filter((d) => d.innerWallId == null || !goneWalls.has(d.innerWallId))
    .map((d) => (d.innerWallId != null ? { ...d, edge: undefined } : d)));
}

/** Index of the polygon interior wall hit by a local point, or -1 (topmost first). */
export function polyInnerWallHit(inst: ProductInstance, p: Vec2): number {
  inst = wallView(inst);
  const arr = inst.innerWalls || [];
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i].edge == null) continue;
    const band = polyInnerWallBand(inst, arr[i]);
    if (band.length >= 4 && pointInPolygon(p, band)) return i;
  }
  return -1;
}

/**
 * Project a local point onto edge `edge`: `along` is the distance from the
 * edge's first vertex (clamped to the edge), `depth` is the inward distance past
 * the edge's inner wall face (negative while still within the wall). Null when
 * the edge index is out of range.
 */
export function polyInnerWallMetrics(inst: ProductInstance, edge: number, p: Vec2): { along: number; depth: number } | null {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return null;
  const ap = sub(p, e.a);
  const along = clamp(ap.x * e.dir.x + ap.y * e.dir.y, 0, e.len);
  const depth = ap.x * e.normal.x + ap.y * e.normal.y - wallThicknessOf(inst);
  return { along, depth };
}

/**
 * How the mitred corners shift an edge's INNER face relative to its outer one:
 * the inner face starts `m0` further along the edge direction and ends `m1`
 * further, so a point at outer-distance `d` sits `d - m0` along the inner face,
 * whose whole length is `len + m1 - m0`. This is the conversion the interior
 * clear-span dimensions need — they are measured corner to mitred corner, not
 * along the outer edge.
 */
export function innerFaceMitres(inst: ProductInstance, edge: number): { m0: number; m1: number; innerLen: number } | null {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return null;
  const wall = wallThicknessOf(inst);
  const base = (p: Vec2, at: Vec2): number => {
    const d = sub(p, add(at, mul(e.normal, wall)));
    return d.x * e.dir.x + d.y * e.dir.y;
  };
  const m0 = base(e.ia, e.a), m1 = base(e.ib, e.b);
  return { m0, m1, innerLen: e.len + m1 - m0 };
}

/**
 * Where the interior partitions hosted on `edge` interrupt that wall's interior
 * clear span, in order along the edge. `s`/`t` are the partition's two faces
 * measured along the edge; `a`/`b` are the same faces ON the wall's inner face,
 * which is the line the interior dimension runs along — taken from the band
 * itself, so the dimension can never disagree with the drawn partition.
 */
export function innerWallDimSplits(
  inst: ProductInstance, edge: number,
): Array<{ wallId: string; s: number; t: number; a: Vec2; b: Vec2 }> {
  inst = wallView(inst);
  const e = polyEdges(inst)[edge];
  if (!e) return [];
  const out: Array<{ wallId: string; s: number; t: number; a: Vec2; b: Vec2 }> = [];
  for (const iw of inst.innerWalls || []) {
    if (iw.edge !== edge || !iw.id) continue;
    const band = polyInnerWallBand(inst, iw);
    if (band.length < 4) continue;
    const sp = innerWallSpan(inst, iw);
    if (!sp) continue;
    out.push({ wallId: iw.id, s: sp.s, t: sp.t, a: band[0], b: band[1] });
  }
  return out.sort((p, q) => p.s - q.s);
}

/** One figure of a wall's outer dimension chain: a solid run, or the opening a
 *  door or glazed pane cuts in it. `s`/`t` are distances along the outer edge. */
export interface WallDimSpan {
  kind: 'solid' | 'glass' | 'door' | 'opening';
  s: number;
  t: number;
  /** Pinned by the user typing this figure — automatic redistribution skips it. */
  locked: boolean;
  /** A glass pane in an `equalGroup` — kept equal to its group by the solver, and
   *  drawn with an "=" badge. */
  equal?: boolean;
  /** The pane or door this span IS (feature spans only). */
  feature?: GlassSegment | SaunaDoor;
  /** For a solid run: the features flanking it, if any. The run's lock lives on
   *  one of them (`prev.lockedAfter` / `next.lockedBefore`) since a run is not
   *  itself a stored object. */
  prev?: GlassSegment | SaunaDoor;
  next?: GlassSegment | SaunaDoor;
}

const SPAN_MIN = 1; // below this a run is a rounding artefact, not a figure
/** Smallest a door / glazed pane figure may be driven to, typed or dragged. */
export const DOOR_MIN = 300;
/** Solid wall a door always keeps on EACH side (mm) — its frame needs something
 *  to land on, so moving a door never takes either distance below this. */
export const DOOR_SIDE_MIN = 79;
export const GLASS_MIN = 30;

/**
 * The outer dimension chain of one wall: `solid | feature | solid | …` over
 * every door and glazed pane hosted on that edge, in order along it. This is the
 * ONE resolver for how a wall's outer dimension is broken up — the drawn
 * figures, the double-click hit-test and the resize redistribution all read it,
 * so none of them can disagree about where a figure starts or what it measures.
 *
 * `len` overrides the edge length, for solving against a length the polygon does
 * not have yet (a wall mid-resize, where the spans still describe the old one).
 */
export function wallDimSpans(inst: ProductInstance, edge: number, len?: number): WallDimSpan[] {
  const e = polyEdges(inst)[edge];
  if (!e) return [];
  const L = Math.max(0, len ?? e.len);
  const feats: ChainFeat[] = [];
  for (const g of inst.glass || []) {
    if (g.edge !== edge || g.innerWallId != null) continue;
    feats.push({ kind: 'glass', s: Math.min(g.start, g.end), t: Math.max(g.start, g.end), feature: g });
  }
  // Openings are wall features just like panes (start/end along the edge), so they
  // join the SAME chain — a wall dimensions solid | glass | opening | door | solid.
  for (const o of inst.openings || []) {
    if (o.edge !== edge || o.innerWallId != null) continue;
    feats.push({ kind: 'opening', s: Math.min(o.start, o.end), t: Math.max(o.start, o.end), feature: o });
  }
  for (const d of roomDoors(inst)) {
    if (d.innerWallId != null || (d.edge ?? 0) !== edge) continue;
    const mid = L / 2 + (d.offset || 0), half = d.width / 2;
    feats.push({ kind: 'door', s: mid - half, t: mid + half, feature: d });
  }
  return chainFrom(feats, L);
}

/** A figure-making feature at stations along a run (one edge, or a whole wall). */
type ChainFeat = { kind: 'glass' | 'door' | 'opening'; s: number; t: number; feature: GlassSegment | SaunaDoor };

/** The chain `solid | feature | solid | …` over features at stations along a run
 *  of length `L` — the one rule for how a run is broken into figures, shared by
 *  the per-segment chain and the whole-wall one. */
function chainFrom(input: ChainFeat[], L: number): WallDimSpan[] {
  const feats = input
    .map((f) => ({ ...f, s: clamp(f.s, 0, L), t: clamp(f.t, 0, L) }))
    .filter((f) => f.t - f.s > SPAN_MIN)
    .sort((a, b) => a.s - b.s);
  if (!feats.length) return L > SPAN_MIN ? [{ kind: 'solid', s: 0, t: L, locked: false }] : [];
  const out: WallDimSpan[] = [];
  let at = 0;
  for (let i = 0; i < feats.length; i++) {
    const f = feats[i];
    const prev = i > 0 ? feats[i - 1].feature : undefined;
    const s = Math.max(at, f.s), t = Math.max(s, f.t);
    if (s - at > SPAN_MIN) {
      out.push({ kind: 'solid', s: at, t: s, locked: !!prev?.lockedAfter || !!f.feature.lockedBefore, prev, next: f.feature });
    }
    out.push({ kind: f.kind, s, t, locked: !!f.feature.lockedWidth, equal: (f.kind === 'glass' || f.kind === 'opening') && (f.feature as GlassSegment).equalGroup != null, feature: f.feature });
    at = t;
  }
  const last = feats[feats.length - 1].feature;
  if (L - at > SPAN_MIN) out.push({ kind: 'solid', s: at, t: L, locked: !!last.lockedAfter, prev: last });
  return out;
}

/**
 * The outer dimension chain of WALL `w` (corner to corner), across all of its
 * segments: `solid | glass | solid | door | …` exactly as the wall read before its
 * panes owned segments. Built from the room's OWN panes and doors (not view
 * copies), so pinning a figure writes straight onto the feature.
 *
 * `segLens` lays the wall out at other segment lengths than the current ones — the
 * lengths from BEFORE a reshape, so the old chain can be re-solved at the new wall
 * length. `len` overrides the wall's length for a single-segment wall (a wall
 * mid-resize), exactly like `wallDimSpans`' own `len`.
 */
export function wallChain(inst: ProductInstance, w: number, opts: { segLens?: number[]; len?: number } = {}): WallDimSpan[] {
  const run = wallRuns(inst)[w];
  if (!run) return [];
  if (run.segs.length === 1 && !opts.segLens) return wallDimSpans(inst, run.segs[0], opts.len);
  const lens = opts.segLens ?? polyEdgeLengths(inst);
  const off = new Map<number, number>();
  let L = 0;
  for (const i of run.segs) { off.set(i, L); L += lens[i] ?? 0; }
  const feats: ChainFeat[] = [];
  const along = (kind: 'glass' | 'opening', arr: GlassSegment[] | undefined): void => {
    for (const g of arr || []) {
      if (g.innerWallId != null || g.edge == null || !off.has(g.edge)) continue;
      const o = off.get(g.edge)!, sl = lens[g.edge] ?? 0;
      feats.push({ kind, s: o + clamp(Math.min(g.start, g.end), 0, sl), t: o + clamp(Math.max(g.start, g.end), 0, sl), feature: g });
    }
  };
  along('glass', inst.glass);
  along('opening', inst.openings);
  for (const d of roomDoors(inst)) {
    const e = d.edge ?? 0;
    if (d.innerWallId != null || !off.has(e)) continue;
    const mid = off.get(e)! + (lens[e] ?? 0) / 2 + (d.offset || 0), half = d.width / 2;
    feats.push({ kind: 'door', s: mid - half, t: mid + half, feature: d });
  }
  return chainFrom(feats, L);
}

/**
 * Lay a solved chain (`lens`, one per span of `spans`) back onto wall `w`: every
 * pane/opening takes its station, every door its width and centre, and the wall
 * is re-laid so each pane/opening owns its segment again (`layoutWall`). The
 * lengths must sum to the wall's CURRENT length.
 */
export function writeWallChain(inst: ProductInstance, w: number, spans: WallDimSpan[], lens: number[]): VertexRemap {
  const run = wallRuns(inst)[w];
  if (!run) return identityRemap;
  const segLens = polyEdgeLengths(inst);
  const off = new Map<number, number>();
  run.segs.forEach((i, k) => off.set(i, run.starts[k]));
  const feats: WallFeature[] = [];
  const placed = new Set<GlassSegment>();
  let at = 0;
  spans.forEach((sp, i) => {
    const width = Math.max(0, lens[i] ?? sp.t - sp.s);
    if (sp.kind === 'glass' || sp.kind === 'opening') {
      const g = sp.feature as GlassSegment;
      feats.push({ kind: sp.kind, seg: g, s: at, t: at + width });
      placed.add(g);
    } else if (sp.kind === 'door') {
      const d = sp.feature as SaunaDoor, e = d.edge ?? 0;
      d.width = width;
      // Expressed in the CURRENT segmentation; layoutWall re-homes the door onto
      // whichever segment its centre ends up in.
      d.offset = at + width / 2 - ((off.get(e) ?? 0) + (segLens[e] ?? 0) / 2);
    }
    at += width;
  });
  // A sliver the chain dropped still lives on this wall: keep it where it is.
  for (const f of wallFeatures(inst, w)) if (!placed.has(f.seg)) feats.push(f);
  return layoutWall(inst, w, feats);
}

/** The pinned length of wall `w`, or null: an edge-length lock on a wall that is
 *  one segment, else the distance between its two corners. */
export function wallLengthPin(inst: ProductInstance, w: number): number | null {
  const run = wallRuns(inst)[w];
  if (!run) return null;
  if (run.segs.length === 1) {
    const pin = inst.polygonEdgeLengths?.[run.segs[0]];
    if (pin != null) return pin;
  }
  const rel = (inst.polygonRelations ?? []).find((r) => r.type === 'distancePP'
    && ((r.verts[0] === run.a && r.verts[1] === run.b) || (r.verts[0] === run.b && r.verts[1] === run.a)));
  return rel && 'value' in rel ? rel.value : null;
}

/** Pin (a number) or release (null) wall `w`'s length, whichever way it is stored. */
export function setWallLengthPin(inst: ProductInstance, w: number, val: number | null): void {
  const run = wallRuns(inst)[w];
  if (!run) return;
  const n = inst.polygon?.length ?? 0;
  const isOurs = (r: ShapeRelation): boolean => r.type === 'distancePP'
    && ((r.verts[0] === run.a && r.verts[1] === run.b) || (r.verts[0] === run.b && r.verts[1] === run.a));
  const rels = (inst.polygonRelations ?? []).filter((r) => !isOurs(r));
  let lens = inst.polygonEdgeLengths?.slice();
  if (lens) for (const i of run.segs) lens[i] = null;
  if (val != null && run.segs.length === 1) {
    lens ??= new Array(n).fill(null);
    lens[run.segs[0]] = val;
  } else if (val != null) rels.push({ type: 'distancePP', verts: [run.a, run.b], value: val });
  inst.polygonEdgeLengths = lens && lens.some((x) => x != null) ? lens : undefined;
  inst.polygonRelations = rels.length ? rels : undefined;
}

/**
 * Re-solve wall `w`'s chain — with figure `index` typed to `val`, or just refitted
 * to the wall — and lay it back down; then keep every equal group equal across
 * the room's OTHER walls (`propagateEqual`). The typed-figure rule is
 * `solveWallSpans`' (the solid runs absorb first), unchanged by the split.
 */
export function solveWall(inst: ProductInstance, w: number, index = -1, val?: number): VertexRemap {
  const run = wallRuns(inst)[w];
  if (!run) return identityRemap;
  const spans = wallChain(inst, w);
  const remap = writeWallChain(inst, w, spans, solveWallSpans(spans, run.len, index, val));
  return composeRemap(remap, propagateEqual(inst, w));
}

/**
 * Equal groups may span walls: after wall `from` was re-laid, every OTHER wall
 * holding members of a group seen on `from` gives them that wall's member length
 * (held, like a pin) and re-solves its own free figures around them.
 */
export function propagateEqual(inst: ProductInstance, from: number): VertexRemap {
  const lenOf = new Map<string, number>();
  for (const sp of wallChain(inst, from)) {
    const g = sp.equal ? (sp.feature as GlassSegment).equalGroup : undefined;
    if (g && !lenOf.has(g)) lenOf.set(g, sp.t - sp.s);
  }
  let remap = identityRemap;
  if (!lenOf.size) return remap;
  const walls = wallRuns(inst).length;
  for (let w = 0; w < walls; w++) {
    if (w === from) continue;
    const spans = wallChain(inst, w);
    const held = spans.map((sp) => {
      const g = sp.equal ? (sp.feature as GlassSegment).equalGroup : undefined;
      return g && lenOf.has(g) ? { ...sp, locked: true, equal: false, t: sp.s + lenOf.get(g)! } : sp;
    });
    if (held.every((sp, i) => sp === spans[i])) continue;
    remap = composeRemap(remap, writeWallChain(inst, w, spans, solveWallSpans(held, wallRuns(inst)[w].len)));
  }
  return remap;
}

/**
 * Make every member of equal group `group` (panes and openings of this room, on
 * any walls) the same length: their mean. Each wall holding members then fits its
 * own free figures around them, keeping its length.
 */
export function equaliseGroup(inst: ProductInstance, group: string): VertexRemap {
  const members: number[] = [];
  const walls = wallRuns(inst).length;
  for (let w = 0; w < walls; w++) {
    for (const sp of wallChain(inst, w)) if (sp.equal && (sp.feature as GlassSegment).equalGroup === group) members.push(sp.t - sp.s);
  }
  if (!members.length) return identityRemap;
  const target = members.reduce((a, b) => a + b, 0) / members.length;
  let remap = identityRemap;
  for (let w = 0; w < walls; w++) {
    const spans = wallChain(inst, w);
    const held = spans.map((sp) => (sp.equal && (sp.feature as GlassSegment).equalGroup === group
      ? { ...sp, locked: true, equal: false, t: sp.s + target } : sp));
    if (held.every((sp, i) => sp === spans[i])) continue;
    remap = composeRemap(remap, writeWallChain(inst, w, spans, solveWallSpans(held, wallRuns(inst)[w].len)));
  }
  return remap;
}

/**
 * Resolve a wall's dimension chain to a new total, honouring the locks.
 *
 * `fixed` is the index of a figure the user just typed (`typedLen`); it is held
 * as firmly as a lock. The slack goes to the SOLID runs first — that is what
 * makes typing a solid figure *move* the panes and doors rather than stretch
 * them — and only widens to the features, then to everything, when the solid
 * runs alone cannot absorb it.
 */
export function solveWallSpans(spans: WallDimSpan[], total: number, fixed = -1, typedLen?: number): number[] {
  const lens = spans.map((s) => s.t - s.s);
  if (fixed >= 0 && fixed < lens.length && typedLen != null) lens[fixed] = Math.max(0, typedLen);
  const distribute = (free: number[]): boolean => {
    if (!free.length) return false;
    const freeSet = new Set(free);
    let held = 0;
    for (let i = 0; i < lens.length; i++) if (!freeSet.has(i)) held += lens[i];
    const rem = total - held;
    if (rem < -1e-6) return false;
    // Distribute over UNITS, not individual spans: glass panes sharing an
    // `equalGroup` form one unit whose share is split EVENLY among its members, so
    // the group stays equal length; everything else is a singleton unit that scales
    // proportionally exactly as before. With no equal groups this is identical to
    // plain proportional distribution.
    const groups = new Map<string, number[]>();
    const units: number[][] = [];
    for (const i of free) {
      const g = (spans[i].kind === 'glass' || spans[i].kind === 'opening') ? (spans[i].feature as GlassSegment | undefined)?.equalGroup : undefined;
      if (g) { let u = groups.get(g); if (!u) { u = []; groups.set(g, u); units.push(u); } u.push(i); }
      else units.push([i]);
    }
    const cur = free.reduce((a, i) => a + lens[i], 0);
    for (const u of units) {
      const uCur = u.reduce((a, i) => a + lens[i], 0);
      const share = cur > 1e-6 ? (uCur * rem) / cur : rem / units.length;
      const each = share / u.length; // singleton → the whole share; equal group → split evenly
      for (const i of u) lens[i] = each;
    }
    return true;
  };
  const idx = spans.map((_, i) => i);
  // A door keeps its width through any automatic redistribution — it is "locked by
  // default", so resizing the room (or retyping a neighbouring figure) slides the
  // door along its wall but never resizes it. Only typing the door figure itself
  // (`fixed`) changes its width; a glass pane still scales with the wall.
  const held = (i: number): boolean => spans[i].locked || spans[i].kind === 'door';
  const tiers = fixed >= 0
    ? [idx.filter((i) => i !== fixed && spans[i].kind === 'solid' && !held(i)),
       idx.filter((i) => i !== fixed && !held(i)),
       idx.filter((i) => i !== fixed)]
    : [idx.filter((i) => !held(i)), idx];
  for (const t of tiers) if (distribute(t)) return lens;
  // Even the typed figure alone overruns the wall: fall back to plain scaling.
  const sum = lens.reduce((a, l) => a + l, 0) || 1;
  return lens.map((l) => (l * total) / sum);
}

/** The smallest length wall `edge` can be resized to WITHOUT breaking a locked
 *  size constraint: every held figure (a pinned run / pane, or a door — locked by
 *  default) keeps its exact length, and every unlocked figure keeps only its own
 *  minimum. A bare wall (a single solid span) returns ~0, so it never constrains a
 *  resize; a wall carrying locks or a door does. `len` overrides the edge length so
 *  the current spans can be measured before the polygon is scaled. */
export function wallSpansMinLen(inst: ProductInstance, w: number, len?: number): number {
  const spans = wallChain(inst, w, { len });
  const heldMin = (sp: WallDimSpan): number => {
    if (sp.locked || sp.kind === 'door') return Math.max(0, sp.t - sp.s); // preserved exactly
    return sp.kind === 'glass' || sp.kind === 'opening' ? GLASS_MIN : SPAN_MIN;
  };
  return spans.reduce((a, sp) => a + heldMin(sp), 0);
}

/**
 * The length range WALL `w` may take without breaking a size constraint on it:
 * the floor is `wallSpansMinLen` (every held figure kept, the free ones at their
 * own minimum) and a wall with NO free figure to absorb a change is FIXED at its
 * current length. A wall carrying nothing but plain unpinned solid runs is
 * unconstrained (`0 … ∞`), so reshaping a bare room stays as free as it ever was.
 *
 * This is the per-wall rule `roomResizeConstraints` maps onto the width/depth
 * axes; a reshape that moves corners freely reads it per wall instead.
 */
export function wallLengthLimits(inst: ProductInstance, w: number): { min: number; max: number } {
  const run = wallRuns(inst)[w];
  if (!run) return { min: 0, max: Infinity };
  const spans = wallChain(inst, w);
  if (!spans.some((s) => s.locked || s.kind !== 'solid')) return { min: 0, max: Infinity };
  if (!spans.some((s) => !(s.locked || s.kind === 'door'))) return { min: run.len, max: run.len };
  return { min: wallSpansMinLen(inst, w), max: Infinity };
}

/**
 * The first WALL a reshape would break, or null when every wall still fits the
 * figures on it. `next` is the candidate polygon in the room's local frame, so a
 * corner drag is tested BEFORE it is applied and refused outright — the room
 * never shows a wall its door, panes and pinned figures cannot be laid out in.
 */
export function roomReshapeBreak(inst: ProductInstance, next: Vec2[], tol = 0.5): number | null {
  if (!isPolyRoom(inst) || !inst.polygon || next.length !== inst.polygon.length) return null;
  const runs = wallRuns(inst);
  for (let w = 0; w < runs.length; w++) {
    const { min, max } = wallLengthLimits(inst, w);
    if (min <= 0 && max === Infinity) continue;
    const L = len2(sub(next[runs[w].b], next[runs[w].a])); // corner to corner, as wallRuns measures it
    if (L < min - tol || L > max + tol) return w;
  }
  return null;
}

/**
 * True when a candidate reshape still leaves SOME corner where it was.
 *
 * Dragging a corner is meant to resize the room, not carry it. With locked walls
 * the solver reshapes the whole ring, and a pull it cannot satisfy locally
 * degenerates into sliding every vertex at once — the room walks off under the
 * cursor instead of growing. `prev` and `next` are both in the room's local frame
 * (compare BEFORE recentring, which shifts every local point by construction).
 */
export function reshapeKeepsAnchor(prev: Vec2[], next: Vec2[], tol = 0.5): boolean {
  if (!next.length || prev.length !== next.length) return true;
  return next.some((p, i) => len2(sub(p, prev[i])) <= tol);
}

/** The {width, depth} range a poly-room can be resized to without breaking any
 *  wall's dimensional constraint — a floor AND a ceiling, so a resize can neither
 *  shrink past a lock NOR grow past one. Each wall maps to the axis it runs along
 *  (horizontal → width, vertical → depth, diagonal → dominant axis):
 *  - its FLOOR is `wallSpansMinLen` (every held figure kept, unlocked ones at their
 *    minimum), so the room can't shrink a locked figure below its pinned length;
 *  - a wall whose length can't change without breaking a lock is FIXED (floor =
 *    ceiling = its current length): its edge length is pinned, or every figure on
 *    it is held (no run/pane free to absorb the change), so growing the room would
 *    otherwise scale the pinned figures up. A wall with a free figure grows freely
 *    (ceiling = ∞) — the free figure absorbs it and the locks stay put. */
export function roomResizeConstraints(inst: ProductInstance): { minW: number; maxW: number; minD: number; maxD: number } {
  const INF = Infinity;
  if (!isPolyRoom(inst) || !(inst.width > 0) || !(inst.depth > 0)) return { minW: 1, maxW: INF, minD: 1, maxD: INF };
  const edges = polyEdges(wallView(inst)); // one per WALL: a jamb is not a new wall
  let minW = 1, minD = 1, maxW = INF, maxD = INF;
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    if (!e || e.len < 1) continue;
    const spans = wallChain(inst, i);
    const edgeLocked = wallLengthPin(inst, i) != null;
    const anyFree = spans.some((s) => !(s.locked || s.kind === 'door'));
    const fixed = edgeLocked || !anyFree;
    const lo = (fixed ? e.len : wallSpansMinLen(inst, i)) / e.len; // scale floor for this wall
    const horiz = Math.abs(e.dir.x) >= Math.abs(e.dir.y);
    if (horiz) {
      minW = Math.max(minW, lo * inst.width);
      if (fixed) maxW = Math.min(maxW, inst.width);
    } else {
      minD = Math.max(minD, lo * inst.depth);
      if (fixed) maxD = Math.min(maxD, inst.depth);
    }
  }
  // Round the floor up and the ceiling down, but never let rounding invert them.
  const mnW = Math.ceil(minW), mnD = Math.ceil(minD);
  return {
    minW: mnW, maxW: maxW === INF ? INF : Math.max(mnW, Math.floor(maxW)),
    minD: mnD, maxD: maxD === INF ? INF : Math.max(mnD, Math.floor(maxD)),
  };
}

/** Write a solved dimension chain back onto the wall's doors and panes: each
 *  feature lands at its cumulative station, the solid runs are the gaps. */
export function writeWallSpans(inst: ProductInstance, edge: number, spans: WallDimSpan[], lens: number[], len?: number): void {
  const e = polyEdges(inst)[edge];
  if (!e) return;
  const L = Math.max(0, len ?? e.len);
  let at = 0;
  for (let i = 0; i < spans.length; i++) {
    const sp = spans[i], w = Math.max(0, lens[i] ?? sp.t - sp.s);
    if (sp.kind === 'glass' || sp.kind === 'opening') {
      const g = sp.feature as GlassSegment;
      g.start = at; g.end = at + w;
    } else if (sp.kind === 'door') {
      const d = sp.feature as SaunaDoor;
      d.width = w;
      d.offset = at + w / 2 - L / 2;
    }
    at += w;
  }
}

/** The min / max total length a group of chain spans (one side of a door) can be
 *  compressed / stretched to: a held figure (locked, or a door) keeps its length,
 *  a free run/pane may shrink to its own minimum or grow without bound. */
function sideMinMax(group: WallDimSpan[]): { min: number; max: number } {
  let min = 0, cur = 0, anyFree = false;
  for (const sp of group) {
    const l = sp.t - sp.s;
    cur += l;
    const held = sp.locked || sp.kind === 'door';
    if (held) min += l; else { min += sp.kind === 'glass' || sp.kind === 'opening' ? GLASS_MIN : SPAN_MIN; anyFree = true; }
  }
  return { min, max: anyFree ? Infinity : cur };
}

/**
 * The range (mid-station along the edge, 0…len) an outer-wall door may be DRAGGED
 * to. Moving the door PUSHES the features on the side it moves toward, compressing
 * them proportionally, and stretches the other side — so it does NOT stop at a
 * pane, it resizes it. The door clamps only where a side can no longer absorb the
 * change: each side must stay within `[min, max]` of its features (a held/locked
 * figure keeps its length, a free run/pane may shrink to its minimum). A side made
 * entirely of locked figures pins the door on that side. Null for a partition door.
 */
export function doorDragMidRange(inst: ProductInstance, door: SaunaDoor): { lo: number; hi: number } | null {
  if (door.innerWallId != null) return null;
  const edge = door.edge ?? 0;
  const e = polyEdges(inst)[edge];
  if (!e) return null;
  const len = e.len, hwd = door.width / 2;
  // Both sides keep DOOR_SIDE_MIN of wall — unless the wall is too short to honour
  // it on both at once, where only the features' own minima can apply.
  const side = 2 * DOOR_SIDE_MIN + door.width <= len ? DOOR_SIDE_MIN : 0;
  const spans = wallDimSpans(inst, edge);
  const di = spans.findIndex((s) => s.feature === door);
  if (di < 0) return { lo: hwd + side, hi: len - hwd - side };
  const L = sideMinMax(spans.slice(0, di)), R = sideMinMax(spans.slice(di + 1));
  const lo = Math.max(hwd + Math.max(L.min, side), len - hwd - R.max);
  const hi = Math.min(hwd + L.max, len - hwd - Math.max(R.min, side));
  return hi < lo ? { lo, hi: lo } : { lo, hi };
}

/**
 * Apply a door drag to target mid-station `m`: clamp it to `doorDragMidRange`, then
 * re-solve each side to fit (holding locked runs/panes and keeping equal groups
 * equal, via `solveWallSpans`) and write the features + door offset back. Returns
 * false for a partition door, so the caller keeps the plain clamp.
 */
export function dragDoorAlong(inst: ProductInstance, door: SaunaDoor, m: number): boolean {
  const range = doorDragMidRange(inst, door);
  if (!range) return false;
  const edge = door.edge ?? 0;
  const e = polyEdges(inst)[edge];
  if (!e) return false;
  const len = e.len, hwd = door.width / 2;
  const spans = wallDimSpans(inst, edge);
  const di = spans.findIndex((s) => s.feature === door);
  if (di < 0) return false;
  const mid = Math.max(range.lo, Math.min(range.hi, m));
  const leftLens = solveWallSpans(spans.slice(0, di), mid - hwd);
  const rightLens = solveWallSpans(spans.slice(di + 1), len - mid - hwd);
  writeWallSpans(inst, edge, spans, [...leftLens, door.width, ...rightLens], len);
  return true;
}

/**
 * The joints of a wall's dimension chain: where two consecutive figures meet,
 * as a station along the edge plus the index of the figure on its left. These
 * are exactly the places a drag can move — a pane's jamb against the solid run
 * beside it, a door's against its own.
 */
export function wallSpanJoints(spans: WallDimSpan[]): Array<{ index: number; at: number }> {
  const out: Array<{ index: number; at: number }> = [];
  for (let i = 0; i + 1 < spans.length; i++) out.push({ index: i, at: spans[i].t });
  return out;
}

/**
 * Move the joint between figures `index` and `index + 1` to station `at`: the two
 * figures either side are resized and NOTHING else moves, so both far ends stay
 * put and the wall's total length is unchanged. Each figure is floored at its own
 * minimum — a door at `DOOR_MIN`, a pane at `GLASS_MIN`, a solid run just clear of
 * the threshold `wallDimSpans` would drop it at (so it can't silently vanish and
 * shift the chain under the drag). If the wall is too short to hold both minima the
 * joint doesn't move. Returns the lengths to hand to `writeWallSpans`.
 */
export function moveWallSpanJoint(spans: WallDimSpan[], index: number, at: number): number[] {
  const lens = spans.map((sp) => sp.t - sp.s);
  const a = spans[index], b = spans[index + 1];
  if (!a || !b) return lens;
  const minOf = (sp: WallDimSpan): number => (sp.kind === 'door' ? DOOR_MIN : sp.kind === 'glass' || sp.kind === 'opening' ? GLASS_MIN : 2 * SPAN_MIN);
  const lo = a.s + minOf(a), hi = b.t - minOf(b);
  if (hi < lo) return lens; // no room for both minima — leave the joint where it is
  const d = Math.max(lo, Math.min(hi, at));
  lens[index] = d - a.s;
  lens[index + 1] = b.t - d;
  return lens;
}

/** Pin the figure the user just typed. A feature span pins its own width; a solid
 *  run has no object of its own, so it pins onto the feature that follows it (or
 *  the one before, for the run past the last feature). */
export function lockWallSpan(span: WallDimSpan): void {
  if (span.kind !== 'solid') {
    // Pinning a pane's width and the equal-length constraint are mutually exclusive:
    // a pinned width is fixed, an equal pane is free (kept equal to its group).
    if (span.kind === 'glass' && span.feature) delete (span.feature as GlassSegment).equalGroup;
    if (span.feature) span.feature.lockedWidth = true;
    return;
  }
  if (span.next) span.next.lockedBefore = true;
  else if (span.prev) span.prev.lockedAfter = true;
}

/** Release a pinned figure. A solid run is read as `prev.lockedAfter ||
 *  next.lockedBefore`, so both sides have to be cleared to actually unpin it. */
export function unlockWallSpan(span: WallDimSpan): void {
  if (span.kind !== 'solid') { if (span.feature) span.feature.lockedWidth = undefined; return; }
  if (span.prev) span.prev.lockedAfter = undefined;
  if (span.next) span.next.lockedBefore = undefined;
}

/** Glass + door spans on an edge (each [lo,hi] along the edge). */
function edgeGlassSpans(inst: ProductInstance, edge: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const g of inst.glass || []) if (g.edge === edge) out.push([Math.min(g.start, g.end), Math.max(g.start, g.end)]);
  for (const d of outerDoorSpans(inst)) if (d.edge === edge) out.push([d.start, d.end]);
  return out;
}

/**
 * Local-frame polygon of a glass pane on a polygon edge — a thin strip down the
 * middle of the wall thickness. Where the pane reaches a corner whose adjacent
 * wall is also glazed, its end is mitred (both panes' edges meet on the corner
 * bisector) so the glass lines join cleanly instead of overlapping.
 */
export function polyGlassPolys(inst: ProductInstance, seg: GlassSegment): Vec2[][] {
  const edges = polyEdges(inst);
  const n = edges.length;
  const i = seg.edge ?? -1;
  const e = edges[i];
  if (!e) return [];
  const wall = wallThicknessOf(inst);
  const gh = GLASS_THICKNESS / 2;
  const off = wall / 2; // glass runs down the middle of the wall thickness
  const s = clamp(Math.min(seg.start, seg.end), 0, e.len);
  const t = clamp(Math.max(seg.start, seg.end), 0, e.len);
  const sqLo = (d: number): Vec2 => add(outerAt(e, d), mul(e.normal, off - gh));
  const sqHi = (d: number): Vec2 => add(outerAt(e, d), mul(e.normal, off + gh));
  // A point on edge k's centreline-offset line, shifted in by `delta` from the wall.
  const offLine = (k: number, delta: number): { p: Vec2; d: Vec2 } => ({ p: add(edges[k].a, mul(edges[k].normal, delta)), d: edges[k].dir });
  const miter = (k: number, delta: number): Vec2 => {
    const a = offLine(i, delta), b = offLine(k, delta);
    return lineIntersect(a.p, a.d, b.p, b.d);
  };
  const reaches = (k: number, atEnd: boolean): boolean =>
    edgeGlassSpans(inst, k).some(([a, b]) => (atEnd ? b >= edges[k].len - 1 : a <= 1));
  const prev = (i - 1 + n) % n, next = (i + 1) % n;
  // A jamb is straight: two panes meeting there simply butt (the mitre of two
  // parallel offset lines would be a midpoint, not the jamb).
  const miterStart = s <= 1 && !isJoint(inst, i) && reaches(prev, true);
  const miterEnd = t >= e.len - 1 && !isJoint(inst, next) && reaches(next, false);
  const loS = miterStart ? miter(prev, off - gh) : sqLo(s);
  const hiS = miterStart ? miter(prev, off + gh) : sqHi(s);
  const loE = miterEnd ? miter(next, off - gh) : sqLo(t);
  const hiE = miterEnd ? miter(next, off + gh) : sqHi(t);
  return [[loS, loE, hiE, hiS]];
}

/** Every span cut out of a partition's fill: its doors and its glazed panes. */
export function innerWallGaps(inst: ProductInstance, iw: InnerWall): Array<[number, number]> {
  inst = wallView(inst);
  return [
    ...innerWallDoorSpans(inst, iw.id).map((d) => [d.start, d.end] as [number, number]),
    ...innerWallGlassSpans(inst, iw.id).map((g) => [g.start, g.end] as [number, number]),
  ];
}

/**
 * Local-frame outline of a pane glazed into an interior partition: a 10 mm strip
 * down the MIDDLE of the partition's thickness, spanning `[start,end]` along its
 * inward length. The room-wall version mitres into its neighbours' panes; a
 * partition has no such neighbour, so both ends are square.
 */
export function polyInnerWallGlassPoly(inst: ProductInstance, seg: GlassSegment): Vec2[] {
  inst = wallView(inst);
  const iw = (inst.innerWalls || []).find((w) => w.id === seg.innerWallId);
  if (!iw) return [];
  const e = polyEdges(inst)[iw.edge ?? -1];
  const sp = innerWallSpan(inst, iw);
  if (!e || !sp) return [];
  const wall = wallThicknessOf(inst);
  const gh = GLASS_THICKNESS / 2;
  const mid = innerAt(e, sp.mid, wall); // the partition's centreline at its base
  const across = { x: e.dir.x, y: e.dir.y }; // across the partition's thickness
  const len = Math.max(0, iw.length);
  const a = clamp(Math.min(seg.start, seg.end), 0, len), b = clamp(Math.max(seg.start, seg.end), 0, len);
  if (b - a <= 0) return [];
  const at = (d: number, off: number): Vec2 => add(add(mid, mul(e.normal, d)), mul(across, off));
  return [at(a, -gh), at(b, -gh), at(b, gh), at(a, gh)];
}

/** Local-frame band of an opening on a polygon edge (full wall thickness). */
export function polyOpeningBand(inst: ProductInstance, seg: GlassSegment): Vec2[] {
  const edges = polyEdges(inst);
  const e = edges[seg.edge ?? -1];
  if (!e) return [];
  const wall = wallThicknessOf(inst);
  const s = clamp(Math.min(seg.start, seg.end), 0, e.len);
  const t = clamp(Math.max(seg.start, seg.end), 0, e.len);
  return [outerAt(e, s), outerAt(e, t), add(outerAt(e, t), mul(e.normal, wall)), add(outerAt(e, s), mul(e.normal, wall))];
}

/** The plan band an opening takes out of its wall: its whole thickness, or a
 *  part's pocket (`GlassSegment.pocket`) only the depths it takes — what a
 *  section cuts as the hole and the plan dashes. */
export function openingHoleBand(inst: ProductInstance, seg: GlassSegment): Vec2[] {
  const e = polyEdges(inst)[seg.edge ?? -1];
  if (!e || !seg.pocket || seg.innerWallId != null) return polyOpeningBand(inst, seg);
  const wall = wallThicknessOf(inst);
  const s = clamp(Math.min(seg.start, seg.end), 0, e.len), t = clamp(Math.max(seg.start, seg.end), 0, e.len);
  const d0 = clamp(seg.pocket.from, 0, wall), d1 = clamp(seg.pocket.to, 0, wall);
  return [add(outerAt(e, s), mul(e.normal, d0)), add(outerAt(e, t), mul(e.normal, d0)), add(outerAt(e, t), mul(e.normal, d1)), add(outerAt(e, s), mul(e.normal, d1))];
}

/** What a part's pocket leaves of the wall over it (`GlassSegment.pocket`):
 *  the bands of the wall's thickness it does not take, along its span (room
 *  local) — what 3D builds behind it, over the hole's height. None for a hole
 *  right through. */
export function pocketBacks(inst: ProductInstance, seg: GlassSegment): Vec2[][] {
  const e = polyEdges(inst)[seg.edge ?? -1];
  if (!e || !seg.pocket) return [];
  const wall = wallThicknessOf(inst);
  const s = clamp(Math.min(seg.start, seg.end), 0, e.len), t = clamp(Math.max(seg.start, seg.end), 0, e.len);
  const band = (d0: number, d1: number): Vec2[] => [add(outerAt(e, s), mul(e.normal, d0)), add(outerAt(e, t), mul(e.normal, d0)), add(outerAt(e, t), mul(e.normal, d1)), add(outerAt(e, s), mul(e.normal, d1))];
  const out: Vec2[][] = [];
  if (seg.pocket.from > 0.5) out.push(band(0, Math.min(wall, seg.pocket.from)));
  if (seg.pocket.to < wall - 0.5) out.push(band(Math.max(0, seg.pocket.to), wall));
  return out;
}

/**
 * Nearest wall edge to a local point, with the projected distance along it.
 * Used by the glass/opening tools and snapping. Returns null beyond `threshold`.
 */
export function polyWallHit(inst: ProductInstance, p: Vec2, threshold: number): { edge: number; pos: number; dist: number } | null {
  const edges = polyEdges(inst);
  let best: { edge: number; pos: number; dist: number } | null = null;
  let bestD = threshold;
  edges.forEach((e, i) => {
    const ap = sub(p, e.a);
    const t = clamp(ap.x * e.dir.x + ap.y * e.dir.y, 0, e.len);
    const proj = outerAt(e, t);
    const d = len2(sub(p, proj)); // distance to the clamped edge segment
    if (d < bestD) { bestD = d; best = { edge: i, pos: t, dist: d }; }
  });
  return best;
}

/** World-frame outer vertices (for hit-testing / bounds), given a transform. */
export function polyVertices(inst: ProductInstance): Vec2[] {
  return isPolyRoom(inst) ? toCCW(inst.polygon!) : [];
}

export interface PolyBenchBand {
  id: string;
  /** Wall edge this band sits on. */
  edge: number;
  /** Stacking tier WITHIN THE BAND'S OWN STACK: 0 is the outermost (against the
   *  wall), 1 inward. An edge holding side-by-side stacks (one per side of a
   *  partition) has several tier-0 bands. */
  tier: number;
  /** The band's span along its edge (clamped store values), so corner logic can
   *  tell whether it actually reaches an edge end. */
  start: number;
  end: number;
  /** Level name (high/medium/low), for the default per-tier fill colour. Mirrors
   *  the rectangular room's autoTierFor. */
  level: BenchTier;
  /** Filled local-frame polygon of the bench band (mitred at shared corners). */
  poly: Vec2[];
  /** The inner (room-facing) edge, for the raised-seat lip line. */
  lip: [Vec2, Vec2];
  /** The quad its frame is laid from (`[lo, ro, ri, li]`, as `poly`), where it
   *  differs from the seat: at a slatted mitre the two frames still meet
   *  unmitred, one running through; a seat that butts the winner's front has its
   *  frame run on under the winner's front board to the winner's frame. */
  frame?: Vec2[];
  /** The mitre lines at the ends where its frame runs through under a slatted
   *  mitre (`end` 0 its start, 1 its end; `outer` on the band's wall-side face,
   *  `inner` on its front, unslid as `poly`): its last stud lies on it. */
  mitres?: Array<{ end: 0 | 1; outer: Vec2; inner: Vec2 }>;
  /** The ends where its frame runs on past its seat under another bench's
   *  front board (`bench`, a wall bench's or a partition tier's id): where that
   *  board hangs below the frame the frame stops with the seat instead. */
  under?: Array<{ end: 0 | 1; bench: string }>;
}

/** How deep a slatted bench's frame reaches (mm from its wall-side face): to
 *  its front board's back face, the whole depth with no front board — as
 *  `frontBoard` reads it. */
function frameDepthOf(b: { depth: number; slats?: number[]; gaps?: number[] }): number {
  const D = b.depth > 0 ? b.depth : 1;
  const spans = benchSlatSpans(b);
  return spans.length >= 2 ? Math.min(D, spans[spans.length - 1].off) : D;
}

const RANK_LEVEL: BenchTier[] = ['low', 'medium', 'high'];

/**
 * Split a bench band into its individual slat quads (local frame), leaving the
 * gaps empty. Slats run parallel to the wall across the band depth; `shift`
 * slides the whole set wall-ward (toward the outer edge) so a lower bench's
 * wall-side band tucks under the one above it (overlap). Points are found by
 * interpolating along the band's two mitred side edges, so slats respect the
 * corner cuts. Returns [] when the band carries no slats.
 */
export function polyBenchSlatQuads(band: PolyBenchBand, slats: number[], gaps: number[], depth: number, shift: number): Vec2[][] {
  if (!slats.length || band.poly.length < 4) return [];
  const at = bandAt(band.poly, depth, shift);
  return benchSlatSpans({ slats, gaps }).map((s) => [at(0, s.off), at(1, s.off), at(1, s.off + s.width), at(0, s.off + s.width)]);
}

/** A wall band's slat frame: the point at fraction `u` along quad `[lo, ro, ri,
 *  li]` (as `PolyBenchBand.poly`, unslid) and `d` mm from its wall-side face as
 *  DRAWN — the quad slid `shift` wall-ward square to its wall, as
 *  `shiftBandWallward` slides it, so a slanted end (a mitre) slides with it. */
export function bandAt(poly: Vec2[], depth: number, shift: number): (u: number, d: number) => Vec2 {
  const [lo, ro, ri, li] = poly;
  const D = depth > 0 ? depth : 1;
  const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  const ux = ro.x - lo.x, uy = ro.y - lo.y, ul = Math.hypot(ux, uy) || 1;
  let nx = -uy / ul, ny = ux / ul;
  if (nx * (li.x - lo.x) + ny * (li.y - lo.y) < 0) { nx = -nx; ny = -ny; }
  return (u, d) => {
    const p = lerp(lerp(lo, ro, u), lerp(li, ri, u), d / D);
    return { x: p.x - nx * shift, y: p.y - ny * shift };
  };
}

/**
 * Level rank of tier `k` within a stack of `count` tiers on one edge: high (2),
 * medium (1), low (0). Delegates to the rectangular room's tier logic
 * ({@link autoTierRank}) so both room types join corners by the identical
 * "a lower bench yields to a higher one" rule.
 */
function benchLevelRank(k: number, count: number): number {
  return autoTierRank(k, count);
}

/** A bench joint's states, in the order a click with the Bench tool steps
 *  through them: the default winner runs through, the other one does, the two
 *  mitre with the default winner's frame running through underneath, or the
 *  other's. Only two identical slatted benches can mitre (`…CanMitre`). */
export type BenchJoinState = 'default' | 'swapped' | 'mitre' | 'mitreSwapped';

const JOIN_CYCLE: BenchJoinState[] = ['default', 'swapped', 'mitre', 'mitreSwapped'];

/** The state a click on a joint in `s` moves it to: the next of the four, or
 *  — where the two benches cannot mitre — the other winner. */
export function nextBenchJoin(s: BenchJoinState, canMitre: boolean): BenchJoinState {
  if (canMitre) return JOIN_CYCLE[(JOIN_CYCLE.indexOf(s) + 1) % JOIN_CYCLE.length];
  return s === 'swapped' || s === 'mitreSwapped' ? 'default' : 'swapped';
}

/** Whether a joint in state `s` has the default winner's frame (and, unmitred,
 *  its seat) swapped for the other's. */
const joinSwapped = (s: BenchJoinState): boolean => s === 'swapped' || s === 'mitreSwapped';
const joinMitred = (s: BenchJoinState): boolean => s === 'mitre' || s === 'mitreSwapped';

/** The stored state of the corner join at vertex `corner`, tier `tier`. */
export function polyBenchJoinState(inst: ProductInstance, corner: number, tier: number): BenchJoinState {
  const j = (inst.polyBenchJoins || []).find((x) => x.corner === corner && x.tier === tier);
  if (!j) return 'default';
  return j.mitre === 'default' ? 'mitre' : j.mitre === 'swapped' ? 'mitreSwapped' : 'swapped';
}

/** Whether the corner join at vertex `corner`, tier `tier`, has been manually
 *  swapped (the default winner/loser of that junction is flipped — under a
 *  mitre, whose frame runs through). */
export function polyBenchJoinSwapped(inst: ProductInstance, corner: number, tier: number): boolean {
  return joinSwapped(polyBenchJoinState(inst, corner, tier));
}

/** Store the corner join at vertex `corner`, tier `tier`, in state `s`. */
export function setPolyBenchJoin(inst: ProductInstance, corner: number, tier: number, s: BenchJoinState): void {
  const list = (inst.polyBenchJoins || []).filter((j) => !(j.corner === corner && j.tier === tier));
  if (s === 'swapped') list.push({ corner, tier });
  else if (s !== 'default') list.push({ corner, tier, mitre: s === 'mitre' ? 'default' : 'swapped' });
  if (list.length) inst.polyBenchJoins = list;
  else delete inst.polyBenchJoins;
}

/** Step one polygon bench corner join on, as a click with the Bench tool does:
 *  A wins → B wins → mitred on A's frame → mitred on B's, where the two can
 *  mitre; else A wins ↔ B wins. */
export function togglePolyBenchJoin(inst: ProductInstance, corner: number, tier: number): void {
  setPolyBenchJoin(inst, corner, tier, nextBenchJoin(polyBenchJoinState(inst, corner, tier), polyBenchCanMitre(inst, corner, tier)));
}

/** Where a room's bench stands in a joint, by the key the joint is stored under:
 *  a room corner it reaches (`polyBenchJoins`: the corner, its place among the
 *  benches of its wall reaching that corner), or a partition's foot
 *  (`benchJoinSwaps` / `benchJoinMitres`: the partition, the face, its place
 *  among that side's tiers there). */
export type BenchJoinSlot = { corner: number; tier: number } | { iwIndex: number; face: 'a' | 'b'; tier: number };

/** Every joint key bench `id` stands at: a partition's bench at its face's tier
 *  (whether a wall bench meets it there now or not); a wall bench at each corner
 *  it reaches and at each partition face it ends on. Read before the bench
 *  leaves, for `dropBenchJoinSlots`. */
export function benchJoinSlots(inst: ProductInstance, id: string): BenchJoinSlot[] {
  const out: BenchJoinSlot[] = [];
  (inst.innerWalls ?? []).forEach((iw, iwIndex) => {
    for (const face of ['a', 'b'] as const) {
      const tier = (iw.benches?.[face] ?? []).findIndex((t) => t.id === id);
      if (tier >= 0) out.push({ iwIndex, face, tier });
    }
  });
  if (out.length) return out;
  const view = wallView(inst);
  const g = benchGeom(view);
  if (!g) return out;
  for (const [i, list] of g.byEdge) {
    const k = list.findIndex((b) => b.id === id);
    if (k < 0) continue;
    const tS = g.cornerList(i, false).indexOf(k), tE = g.cornerList(i, true).indexOf(k);
    if (tS >= 0) out.push({ corner: i, tier: tS });
    if (tE >= 0) out.push({ corner: (i + 1) % g.n, tier: tE });
  }
  for (const J of partitionJunctions(view)) {
    const tier = J.wall.findIndex((w) => w.id === id);
    if (tier >= 0) out.push({ iwIndex: J.iwIndex, face: J.face, tier });
  }
  return out;
}

/** Re-key the joints a removed bench stood at (`benchJoinSlots`, read before it
 *  left): at each, its own entry goes and every higher tier's moves down one with
 *  the bench it was set on — so a swap or mitre never comes back on whichever
 *  bench later takes that place. */
export function dropBenchJoinSlots(inst: ProductInstance, slots: readonly BenchJoinSlot[]): void {
  const down = (t: number, gone: number): number[] => (t === gone ? [] : [t > gone ? t - 1 : t]);
  for (const s of slots) {
    if ('corner' in s) {
      const list = (inst.polyBenchJoins ?? []).flatMap((j) => (j.corner !== s.corner ? [j] : down(j.tier, s.tier).map((tier) => ({ ...j, tier }))));
      if (list.length) inst.polyBenchJoins = list;
      else delete inst.polyBenchJoins;
      continue;
    }
    const iw = inst.innerWalls?.[s.iwIndex];
    if (!iw) continue;
    for (const key of ['benchJoinSwaps', 'benchJoinMitres'] as const) {
      const sets = iw[key];
      const list = sets?.[s.face]?.flatMap((t) => down(t, s.tier));
      if (!sets || !list) continue;
      if (list.length) sets[s.face] = list;
      else delete sets[s.face];
      if (!sets.a && !sets.b) delete iw[key];
    }
  }
}

interface BenchGeom {
  edges: PolyEdge[];
  n: number;
  byEdge: Map<number, Array<{ id: string; depth: number; start: number; end: number }>>;
  /** Tiers stacked with entry `j`; the edge's total when `j` is omitted. */
  tierCount: (i: number, j?: number) => number;
  cumOff: (i: number, j: number) => number;
  /** Entry indices sharing a stack with entry `j` on edge `i`. */
  stackAt: (i: number, j: number) => number[];
  /** Position of entry `j` within its own stack (0 = against the wall). NOT the
   *  raw entry index — an edge holding side-by-side stacks (one per side of a
   *  partition) has several tier-0 entries. */
  tierIndexOf: (i: number, j: number) => number;
  /** Inner-face offset of entry `j`: its own outer offset plus its OWN depth —
   *  never derived from the next entry, which may belong to a different stack. */
  innerOff: (i: number, j: number) => number;
  /** Entry indices on edge `i` whose span reaches the edge's start (atEnd=false)
   *  or end (atEnd=true) corner, in stack order — the stack cross-corner mitres
   *  and corner pieces must match against, since a bench on the far side of a
   *  partition never reaches the corner at all. */
  cornerList: (i: number, atEnd: boolean) => number[];
}

/** Shared setup for the bench geometry: edges + tiers grouped/stacked per edge. */
function benchGeom(inst: ProductInstance): BenchGeom | null {
  inst = wallView(inst);
  const benches = inst.polyBenches;
  if (!benches || !benches.length) return null;
  const edges = polyBenchEdges(inst);
  const n = edges.length;
  if (!n) return null;
  const byEdge = new Map<number, Array<{ id: string; depth: number; start: number; end: number }>>();
  for (const b of benches) {
    if (b.edge < 0 || b.edge >= n) continue;
    const list = byEdge.get(b.edge) || [];
    const len = edges[b.edge].len;
    list.push({ id: b.id, depth: b.depth, start: clamp(b.start ?? 0, 0, len), end: clamp(b.end ?? len, 0, len) });
    byEdge.set(b.edge, list);
  }
  // Benches on one edge only STACK when they cover the same stretch of wall.
  // Side-by-side benches — e.g. either side of an interior partition — are
  // independent stacks, each starting at the wall, so neither is pushed inward by
  // the other. Stacks are the connected components of the span-overlap relation,
  // so a tier whose dragged ends overlap only part of the stack still belongs.
  const SEPS = 1;
  const overlaps = (a: { start: number; end: number }, b: { start: number; end: number }): boolean =>
    a.start < b.end - SEPS && b.start < a.end - SEPS;
  const compCache = new Map<number, number[]>(); // edge → component id per entry
  const compsOf = (i: number): number[] => {
    let comp = compCache.get(i);
    if (comp) return comp;
    const list = byEdge.get(i) ?? [];
    comp = list.map((_, idx) => idx);
    const find = (x: number): number => (comp![x] === x ? x : (comp![x] = find(comp![x])));
    for (let a = 0; a < list.length; a++) {
      for (let b = a + 1; b < list.length; b++) if (overlaps(list[a], list[b])) comp[find(a)] = find(b);
    }
    for (let x = 0; x < comp.length; x++) comp[x] = find(x);
    compCache.set(i, comp);
    return comp;
  };
  /** Indices on edge `i` that share a stack with entry `j` (including `j`). */
  const stackAt = (i: number, j: number): number[] => {
    const list = byEdge.get(i);
    if (!list || !list[j]) return [];
    const comp = compsOf(i);
    const out: number[] = [];
    for (let k = 0; k < list.length; k++) if (comp[k] === comp[j]) out.push(k);
    return out;
  };
  /** Tiers stacked with entry `j` (the whole edge's count when `j` is omitted —
   *  used only where a representative depth is needed). */
  const tierCount = (i: number, j?: number): number =>
    j == null ? (byEdge.get(i)?.length ?? 0) : stackAt(i, j).length;
  const tierIndexOf = (i: number, j: number): number => Math.max(0, stackAt(i, j).indexOf(j));
  /** Wall-ward offset of tier `j` on edge `i`: the depths of the tiers BELOW it in
   *  its own stack. Entries in other stacks (a different stretch of the same wall)
   *  contribute nothing. */
  const cumOff = (i: number, j: number): number => {
    const list = byEdge.get(i);
    if (!list) return 0;
    const jj = list[j] ? j : list.length - 1; // legacy "one past end" fallback
    let o = 0;
    for (const k of stackAt(i, jj)) if (k < j) o += list[k].depth;
    return o;
  };
  const innerOff = (i: number, j: number): number => cumOff(i, j) + (byEdge.get(i)?.[j]?.depth ?? 0);
  const CEPS = 1;
  const cornerList = (i: number, atEnd: boolean): number[] => {
    const list = byEdge.get(i) ?? [];
    const out: number[] = [];
    for (let k = 0; k < list.length; k++) {
      if (atEnd ? list[k].end >= edges[i].len - CEPS : list[k].start <= CEPS) out.push(k);
    }
    return out;
  };
  return { edges, n, byEdge, tierCount, cumOff, stackAt, tierIndexOf, innerOff, cornerList };
}

/** The bench a room's bench tucks under — the tier toward its wall or
 *  partition face in its stack —, or null for the one against it. */
export function benchTuckedUnder(inst: ProductInstance, benchId: string): { depth: number; slats?: number[]; gaps?: number[] } | null {
  for (const iw of inst.innerWalls ?? []) for (const face of ['a', 'b'] as const) {
    const tiers = iw.benches?.[face] ?? [];
    const k = tiers.findIndex((t) => t.id === benchId);
    if (k >= 0) return k > 0 ? tiers[k - 1] : null;
  }
  const g = benchGeom(inst);
  if (!g) return null;
  for (const [i, list] of g.byEdge) {
    const j = list.findIndex((b) => b.id === benchId);
    if (j < 0) continue;
    const stack = g.stackAt(i, j), t = stack.indexOf(j);
    return t > 0 ? inst.polyBenches?.find((p) => p.id === list[stack[t - 1]].id) ?? null : null;
  }
  return null;
}

/**
 * Wall-ward tuck offset (mm) per `${edge}:${tier}`: a lower bench runs UNDER the
 * one toward the wall by its overlap, but only under a strictly-higher bench with
 * a thickness, and never past the wall (clamped to `cumOff`). Mirrors the
 * rectangular room's benchStackShifts so corner joins can meet tucked edges.
 */
function edgeTuckShifts(inst: ProductInstance, g: BenchGeom): Map<string, number> {
  const out = new Map<string, number>();
  const pbOf = (id: string) => inst.polyBenches?.find((p) => p.id === id);
  for (const [i, list] of g.byEdge) {
    // Tuck runs per STACK: side-by-side stacks (either side of a partition) don't
    // slide under each other. Keys stay `${edge}:${entryIndex}`.
    const done = new Set<number>();
    for (let j = 0; j < list.length; j++) {
      if (done.has(j)) continue;
      const stack = g.stackAt(i, j);
      stack.forEach((k) => done.add(k));
      const shifts = cumulativeTuck(stack.length, {
        rank: (t) => benchLevelRank(t, stack.length),
        hasSlab: (t) => { const h = pbOf(list[stack[t]].id); return !!h && benchThickness(h) != null; },
        overlap: (t) => benchOverlap(pbOf(list[stack[t]].id) ?? {}, t, pbOf(list[stack[t - 1]].id)),
        maxOffset: (t) => g.cumOff(i, stack[t]),
        fixed: (t, prev) => {
          const up = pbOf(list[stack[t - 1]].id)?.profile, lo = pbOf(list[stack[t]].id)?.profile;
          if (!up || !lo) return null;
          // The lower tier's back sits where the higher one's foot is (profileStep).
          return prev + g.cumOff(i, stack[t]) - g.cumOff(i, stack[t - 1]) - profileStep(up, lo).dx;
        },
      });
      shifts.forEach((s, t) => out.set(`${i}:${stack[t]}`, s));
    }
  }
  return out;
}

/**
 * How far each profiled wall bench rises (mm), by id: a stack of profiled tiers
 * steps up from the innermost, which stands on the floor — each higher one's
 * innermost lowest corner on the outermost highest corner of the one in front
 * (`profileStep`), its foot filled to the floor (`profileFilled`). A tier in
 * front of no profiled tier does not rise; one with no profile is absent.
 */
export function polyBenchLifts(inst: ProductInstance): Map<string, number> {
  inst = wallView(inst);
  const out = new Map<string, number>();
  const g = benchGeom(inst);
  if (!g) return out;
  const pbOf = (id: string) => inst.polyBenches?.find((p) => p.id === id);
  for (const [i, list] of g.byEdge) {
    const done = new Set<number>();
    for (let j = 0; j < list.length; j++) {
      if (done.has(j)) continue;
      const stack = g.stackAt(i, j);
      stack.forEach((k) => done.add(k));
      out.set(list[stack[stack.length - 1]].id, 0);
      for (let t = stack.length - 2; t >= 0; t--) {
        const up = pbOf(list[stack[t]].id)?.profile, lo = pbOf(list[stack[t + 1]].id)?.profile;
        out.set(list[stack[t]].id, up && lo ? (out.get(list[stack[t + 1]].id) ?? 0) + profileStep(up, lo).dy : 0);
      }
    }
  }
  for (const id of [...out.keys()]) if (!pbOf(id)?.profile) out.delete(id);
  return out;
}

/**
 * Bench bands for a polygonal room. Each edge can carry a stack of tiers, listed
 * in `polyBenches` outer→inner; tier 0 sits against the wall, tier 1 inward of it.
 * A tier is a full-edge band whose faces are parallel to the wall at the stack's
 * cumulative inward offsets.
 *
 * Where two tiers of the same level meet at a corner one bench *wins* — it spans
 * through the corner — and the other is shortened and mitred to butt against the
 * winner's inner face, so the two never intersect. The default winner is the
 * higher-level bench, or (same level) the one on the longer wall; a stored
 * `polyBenchJoins` override swaps it. A tier whose neighbour has no matching tier
 * gets a square end.
 */
/** Where one face of a ring stops being straight at a rounded corner, as a
 *  station from the corner vertex: the shared tangent station while the face
 *  still curves, else its own sharp mitre. */
export function faceStation(c: RoomCorner, inset: number): number {
  return faceRadius(c, inset) > RADIUS_EPS ? c.station : stationOf(inset, c.theta);
}

/**
 * Where a rounded corner piece hands over to the straight benches — kept as
 * close to the corner as the geometry allows. The piece has to contain whatever
 * is not straight on EITHER of its faces, and nothing more:
 *  - a big radius curves both faces, so the join lands on the tangent points and
 *    the piece is a pure sector;
 *  - a smaller one leaves the inner face sharp, so the join lands where the two
 *    benches' inner faces cross and the piece is their overlap.
 */
function cornerPieceStation(c: RoomCorner, insetOuter: number, insetInner: number): number {
  return Math.max(faceStation(c, insetOuter), faceStation(c, insetInner));
}

/** Everything that has to agree before two tiers can share a rounded corner
 *  piece: depth, seat height, slab thickness, slat composition, colour and the
 *  skirting as BUILT (`skirtingOf`) — boards kept while it is switched off, or
 *  the default ones a bench with none takes, do not part two benches. */
function benchSignature(inst: ProductInstance, id: string): string {
  const b = (inst.polyBenches || []).find((x) => x.id === id);
  return b ? signatureOf(b) : `?${id}`;
}

/** `benchSignature` of any bench — a wall's or a partition's tier. */
function signatureOf(b: Pick<BenchLeg, 'depth' | 'height' | 'thickness' | 'color' | 'slats' | 'gaps' | 'slatThickness' | 'overlap' | 'profile' | 'skirting' | 'skirtingBoards'>): string {
  return JSON.stringify([b.depth, b.height ?? null, b.thickness ?? null, b.color ?? null,
    b.slats ?? null, b.gaps ?? null, b.slatThickness ?? null, b.overlap ?? null, b.profile ?? null, skirtingOf(b)]);
}

/** Whether two identical slatted benches meeting at vertex `v` on corner tier
 *  `t` can mitre: both reach the corner, everything a rounded corner piece asks
 *  of them agrees, they tuck alike, the corner is sharp (a rounded one joins
 *  them with its piece) and not straight. A profiled pair mitres anyway. */
function cornerMitreable(inst: ProductInstance, g: BenchGeom, v: number, t: number): boolean {
  if (t < 0) return false;
  const p = (v - 1 + g.n) % g.n, i = v;
  const pIdx = g.cornerList(p, true)[t], iIdx = g.cornerList(i, false)[t];
  if (pIdx == null || iIdx == null) return false;
  const b = (inst.polyBenches || []).find((x) => x.id === g.byEdge.get(i)?.[iIdx]?.id);
  if (!b?.slats?.length || b.profile) return false;
  if (!tiersMatchAtCorner(inst, g, i, iIdx, p, pIdx)) return false;
  const tuck = edgeTuckShifts(inst, g);
  const shift = tuck.get(`${i}:${iIdx}`) ?? 0;
  if (Math.abs((tuck.get(`${p}:${pIdx}`) ?? 0) - shift) > EPS) return false;
  if (Math.abs(cross(g.edges[i].dir, g.edges[p].dir)) < 1e-9) return false;
  const c = roomCorners(inst)[v], wallT = wallThicknessOf(inst);
  return !(c && cornerPieceStation(c, wallT + g.cumOff(i, iIdx) - shift, wallT + g.innerOff(i, iIdx) - shift) > 0);
}

/** Whether the two benches meeting at vertex `corner` on tier `tier` can mitre
 *  — what lets the Bench tool's click step on to the two mitred states. */
export function polyBenchCanMitre(inst: ProductInstance, corner: number, tier: number): boolean {
  inst = wallView(inst);
  const g = benchGeom(inst);
  return !!g && cornerMitreable(inst, g, corner, tier);
}

/**
 * Whether entry `aIdx` on edge `a` and entry `bIdx` on edge `b` (the two benches
 * meeting at a corner, matched by their position within each side's
 * corner-reaching stack) are identical — the test that decides between a rounded
 * corner piece (matching) and a plain void (not). They must also sit at the same
 * depth band, which they do whenever every tier below them matches too.
 */
function tiersMatchAtCorner(inst: ProductInstance, g: BenchGeom, a: number, aIdx: number, b: number, bIdx: number): boolean {
  const la = g.byEdge.get(a)?.[aIdx], lb = g.byEdge.get(b)?.[bIdx];
  if (!la || !lb) return false;
  if (Math.abs(g.cumOff(a, aIdx) - g.cumOff(b, bIdx)) > EPS) return false; // different depth band
  return benchSignature(inst, la.id) === benchSignature(inst, lb.id);
}

export function polyBenchBands(inst: ProductInstance): PolyBenchBand[] {
  inst = wallView(inst);
  const g = benchGeom(inst);
  if (!g) return [];
  const { edges, n, byEdge, cumOff, innerOff, tierIndexOf, tierCount, cornerList } = g;
  const corners = roomCorners(inst);
  // Overlap tuck per (edge, tier): a bench tucked toward the wall recedes, so a
  // corner-facing neighbour must run in to meet its TUCKED face, not the natural
  // one — the corner joins use tucked bounding boxes.
  const tuck = edgeTuckShifts(inst, g);
  const shiftOf = (edge: number, k: number): number => tuck.get(`${edge}:${k}`) ?? 0;
  // Face line at cumulative offset `off` on edge i (a line parallel to the wall).
  const faceP = (i: number, off: number): Vec2 => add(edges[i].ia, mul(edges[i].normal, off));

  // Which edge wins corner-tier `t` at the corner shared by edges i and adj
  // (spans through the corner); the loser is cut back to butt against it. Higher
  // level wins; on a tie the longer wall wins, then the lower edge index. A
  // stored swap flips it. `cntI`/`cntAdj` are each side's corner-stack sizes.
  const winnerAt = (i: number, adj: number, t: number, cntI: number, cntAdj: number, corner: number): number => {
    const li = benchLevelRank(t, cntI);
    const lj = benchLevelRank(t, cntAdj);
    let win: number;
    if (li !== lj) win = li > lj ? i : adj;
    else if (Math.abs(edges[i].len - edges[adj].len) > EPS) win = edges[i].len > edges[adj].len ? i : adj;
    else win = Math.min(i, adj);
    if (polyBenchJoinSwapped(inst, corner, t)) win = win === i ? adj : i;
    return win;
  };

  // Cut edge i's band faces (parallel to edge i at `oOff`/`iOff`) by a line
  // parallel to edge `adj` at cumulative offset `boundOff` — `boundOff = 0` is the
  // adjacent wall's inner face (a mitre against the wall). Both corner points lie
  // on that one bounding line, so the end face is a single clean segment.
  const cutAt = (i: number, adj: number, oOff: number, iOff: number, boundOff: number): { outer: Vec2; inner: Vec2 } => {
    const foP = faceP(i, oOff), fiP = faceP(i, iOff);
    const bound = faceP(adj, boundOff);
    return {
      outer: lineIntersect(foP, edges[i].dir, bound, edges[adj].dir),
      inner: lineIntersect(fiP, edges[i].dir, bound, edges[adj].dir),
    };
  };

  const wallT = wallThicknessOf(inst);
  // Square end face at distance `d` along edge i, spanning offsets o0..o1.
  const sq = (i: number, d: number, off: number): Vec2 =>
    add(add(edges[i].a, mul(edges[i].dir, d)), mul(edges[i].normal, wallT + off));
  const CEPS = 1;

  // Offset (from the adjacent wall's inner face) at which my corner-tier `t` is
  // bounded at a shared corner I reach: the neighbour bench face it meets when a
  // matching bench reaches the corner, else 0 — mitre against the wall itself.
  // Tiers are matched by position within each side's CORNER-reaching stack, so a
  // bench on the far side of a partition on either edge never takes part.
  const boundOffsetAt = (i: number, adj: number, t: number, cntI: number, corner: number, atEnd: boolean): number => {
    const adjList = cornerList(adj, atEnd);
    const aT = adjList.length;
    if (aT === 0) return 0; // neighbour edge has no bench reaching this corner → meet the wall
    if (t < aT) {
      // Same tier: winner spans to the loser's (tucked) wall face, loser butts the
      // winner's (tucked) inner face. Subtract the neighbour tier's tuck so the
      // faces meet after the band is slid wall-ward.
      const adjIdx = adjList[t];
      const win = winnerAt(i, adj, t, cntI, aT, corner);
      return (win === i ? cumOff(adj, adjIdx) : innerOff(adj, adjIdx)) - shiftOf(adj, adjIdx);
    }
    // No matching neighbour tier here: butt against the neighbour's innermost
    // corner-reaching bench, at its tucked inner face.
    const last = adjList[aT - 1];
    return innerOff(adj, last) - shiftOf(adj, last);
  };
  // Where the loser's FRAME stops at a corner where its seat butts the winner's
  // front: on under the winner's front board to the winner's frame front (the
  // board's notch is cut in `roomBenchSolids`); null where it stops with its seat.
  const frameBoundAt = (i: number, adj: number, t: number, cntI: number, corner: number, atEnd: boolean): { bound: number; bench: string } | null => {
    const adjList = cornerList(adj, atEnd);
    if (t < 0 || t >= adjList.length) return null;
    const adjIdx = adjList[t];
    if (winnerAt(i, adj, t, cntI, adjList.length, corner) === i) return null;
    const adjBench = pbOf(byEdge.get(adj)![adjIdx].id);
    if (!adjBench) return null;
    const F = frameDepthOf(adjBench);
    return F < adjBench.depth - EPS ? { bound: cumOff(adj, adjIdx) + F - shiftOf(adj, adjIdx), bench: adjBench.id } : null;
  };

  // A rounded corner replaces the mitre: the bench is square-cut at a station
  // measured from the corner vertex. Matching tiers stop `depth` beyond where
  // their own ring stops curving, leaving room for the corner piece that joins
  // them; mismatched tiers stop exactly where their ring stops curving, leaving
  // the curve void. A ring the radius no longer reaches is sharp, so it falls
  // through to the ordinary mitre (null) and behaves exactly as before.
  const roundedCut = (i: number, adj: number, k: number, t: number, vertex: number, atStart: boolean): number | null => {
    const c = corners[vertex];
    if (!c) return null;
    // A tier tucked under the one toward the wall sits `shift` further out, so
    // its corner geometry has to be measured there too — otherwise the piece and
    // the bench it joins are computed at different depths and drift apart.
    const shift = shiftOf(i, k);
    const insetOuter = wallT + cumOff(i, k) - shift;
    const rOuter = faceRadius(c, insetOuter);
    const arcEnd = faceStation(c, insetOuter);
    const adjIdx = cornerList(adj, !atStart)[t]; // the neighbour bench at my corner tier
    const matched = adjIdx != null && tiersMatchAtCorner(inst, g, i, k, adj, adjIdx)
      && Math.abs(shiftOf(adj, adjIdx) - shift) <= EPS; // both must tuck the same, or they can't meet
    const S = matched
      ? cornerPieceStation(c, insetOuter, wallT + innerOff(i, k) - shift)
      : (rOuter > RADIUS_EPS ? arcEnd : null);
    if (S == null || !(S > 0)) return null;
    return atStart ? Math.min(S, edges[i].len) : Math.max(0, edges[i].len - S);
  };

  // Two identical PROFILED benches meeting at a sharp corner mitre — each cut on
  // the line from where their wall-side faces meet to where their fronts do — so
  // the profile turns the corner, backrest and all. (One winning the corner would
  // leave the other's backrest ending over its seat.) Two identical SLATTED ones
  // mitre where the joint is set to (`polyBenchJoins` `mitre`), their frames
  // meeting as the winner / butt rule says, the winner's to the other's wall-side
  // face, the other's to the winner's FRAME front. `atStart`: the corner at the
  // start of edge i, shared with `adj`'s end.
  const pbOf = (id: string) => (inst.polyBenches || []).find((b) => b.id === id);
  const mitreAt = (i: number, k: number, adj: number, t: number, atStart: boolean, oOff: number, iOff: number):
    { seat: { outer: Vec2; inner: Vec2 }; frame?: { outer: Vec2; inner: Vec2 }; wins?: boolean } | null => {
    if (t < 0) return null;
    const me = byEdge.get(i)?.[k];
    if (!me) return null;
    const corner = atStart ? i : (i + 1) % n;
    const profiled = !!pbOf(me.id)?.profile;
    if (!profiled && !(joinMitred(polyBenchJoinState(inst, corner, t)) && cornerMitreable(inst, g, corner, t))) return null;
    const adjIdx = cornerList(adj, atStart)[t];
    if (adjIdx == null || !tiersMatchAtCorner(inst, g, i, k, adj, adjIdx)) return null;
    if (Math.abs(shiftOf(adj, adjIdx) - shiftOf(i, k)) > EPS) return null;
    if (Math.abs(cross(edges[i].dir, edges[adj].dir)) < 1e-9) return null; // in line: nothing to mitre
    // Both are drawn slid wall-ward by their (equal) tuck, each along its own
    // normal: the mitre is cut where the SLID faces meet, and handed back unslid.
    const s = shiftOf(i, k);
    const meet = (offI: number, offA: number): Vec2 =>
      add(lineIntersect(faceP(i, offI - s), edges[i].dir, faceP(adj, offA - s), edges[adj].dir), mul(edges[i].normal, s));
    const seat = { outer: meet(cumOff(i, k), cumOff(adj, adjIdx)), inner: meet(innerOff(i, k), innerOff(adj, adjIdx)) };
    if (profiled) return { seat };
    const cnt = cornerList(i, !atStart).length;
    const wins = winnerAt(i, adj, t, cnt, cornerList(adj, atStart).length, corner) === i;
    const adjBench = pbOf(byEdge.get(adj)![adjIdx].id)!;
    const bound = cumOff(adj, adjIdx) + (wins ? 0 : frameDepthOf(adjBench)) - shiftOf(adj, adjIdx);
    return { seat, frame: cutAt(i, adj, oOff, iOff, bound), wins };
  };

  // A wall bench ending on a partition's face meets that face's benches there:
  // its seat's wall-side and front faces end `back` / `front` mm out from the
  // face (the same under a square join, apart under a mitre), its frame `frame`.
  const junctions = partitionJunctions(inst);
  const partitionCut = (i: number, id: string, side: 'a' | 'b'): PartitionEnd | null => {
    const J = junctions.find((j) => j.edge === i && j.face === side && j.wall.some((w) => w.id === id));
    return J ? wallTierEnd(J, J.wall.findIndex((w) => w.id === id)) : null;
  };
  const out: PolyBenchBand[] = [];
  for (const [i, list] of byEdge) {
    const e = edges[i];
    const prev = (i - 1 + n) % n;
    const next = (i + 1) % n;
    for (let k = 0; k < list.length; k++) {
      const { start, end } = list[k];
      // Its end on a partition's face 'a', its start on a face 'b': cut back to
      // where that partition's benches let it run (`wallTierEnd`).
      const pA = partitionCut(i, list[k].id, 'a'), pB = partitionCut(i, list[k].id, 'b');
      const oOff = cumOff(i, k), iOff = innerOff(i, k);
      const tS = cornerList(i, false).indexOf(k); // my tier within the start-corner stack
      const tE = cornerList(i, true).indexOf(k);  // … and within the end-corner stack
      const sqPair = (d: number): { outer: Vec2; inner: Vec2 } => ({ outer: sq(i, d, oOff), inner: sq(i, d, iOff) });
      const sqApart = (dOuter: number, dInner: number): { outer: Vec2; inner: Vec2 } => ({ outer: sq(i, dOuter, oOff), inner: sq(i, dInner, iOff) });
      const cutL = start > CEPS ? null : roundedCut(i, prev, k, tS, i, true);
      const cutR = end < e.len - CEPS ? null : roundedCut(i, next, k, tE, (i + 1) % n, false);
      // Each end either square-cuts mid-edge (span stops short of the corner), or
      // — reaching the corner — mitres: against the meeting neighbour bench when
      // one is there, otherwise against the neighbouring wall so no corner is left
      // open. A seat and its frame part only under a slatted mitre.
      type End = { seat: { outer: Vec2; inner: Vec2 }; frame?: { outer: Vec2; inner: Vec2 }; wins?: boolean; under?: string };
      const atPartition = (cut: PartitionEnd | null, at: number, sgn: number): End => !cut ? { seat: sqPair(at) }
        : { seat: sqApart(at + sgn * cut.back, at + sgn * cut.front), ...(cut.mitre || Math.abs(cut.frame - cut.back) > EPS ? { frame: sqPair(at + sgn * cut.frame), wins: cut.wins, ...(!cut.mitre && cut.other ? { under: cut.other } : {}) } : {}) };
      const butt = (adj: number, t: number, cnt: number, corner: number, atEnd: boolean): End => {
        const seat = cutAt(i, adj, oOff, iOff, boundOffsetAt(i, adj, t, cnt, corner, atEnd));
        const fb = frameBoundAt(i, adj, t, cnt, corner, atEnd);
        return fb == null ? { seat } : { seat, frame: cutAt(i, adj, oOff, iOff, fb.bound), wins: false, under: fb.bench };
      };
      const left: End = start > CEPS ? atPartition(pB, start, 1)
        : cutL != null ? { seat: sqPair(cutL) }
          : mitreAt(i, k, prev, tS, true, oOff, iOff) ?? butt(prev, tS, cornerList(i, false).length, i, true);
      const right: End = end < e.len - CEPS ? atPartition(pA, end, -1)
        : cutR != null ? { seat: sqPair(cutR) }
          : mitreAt(i, k, next, tE, false, oOff, iOff) ?? butt(next, tE, cornerList(i, true).length, (i + 1) % n, false);
      const poly = [left.seat.outer, right.seat.outer, right.seat.inner, left.seat.inner];
      const tier = tierIndexOf(i, k);
      const level = RANK_LEVEL[benchLevelRank(tier, tierCount(i, k))];
      const band: PolyBenchBand = { id: list[k].id, edge: i, tier, start, end, level, poly, lip: [right.seat.inner, left.seat.inner] };
      if (left.frame || right.frame) {
        const lf = left.frame ?? left.seat, rf = right.frame ?? right.seat;
        band.frame = [lf.outer, rf.outer, rf.inner, lf.inner];
        const mitres = ([[left, 0], [right, 1]] as const).filter(([x]) => x.frame && x.wins).map(([x, end]) => ({ end, ...x.seat }));
        if (mitres.length) band.mitres = mitres;
        const under = ([[left, 0], [right, 1]] as const).filter(([x]) => x.under).map(([x, end]) => ({ end, bench: x.under! }));
        if (under.length) band.under = under;
      }
      out.push(band);
    }
  }
  return out;
}

/** A derived bench piece filling a rounded corner between two identical tiers.
 *  Never stored: it appears whenever the two neighbours match and vanishes the
 *  moment either is deleted or edited apart. */
export interface PolyBenchCornerBand {
  /** Stable within a render, derived from the corner and tier. */
  id: string;
  /** Polygon vertex the piece wraps; -1 on a bench's rounded end. */
  vertex: number;
  /** On a bench's rounded end (`roundStart` / `roundEnd`): the end, and a key
   *  every tier rounded round the same wall point shares. */
  roundEnd?: { bench: string; side: 'start' | 'end'; key: string };
  tier: number;
  level: BenchTier;
  /** Filled local-frame polygon, arcs already expanded. */
  poly: Vec2[];
  /** Arc centre — shared by both faces; drives concentric / radial slats. */
  center: Vec2;
  /** Face radii of the ring here; <= 0 means that face is a sharp mitre. */
  rOuter: number;
  rInner: number;
  /** Seat centreline length (mm) through the piece, for the seating total. */
  seatLength: number;
  /** Inherited from the two neighbours (identical by construction). */
  color?: string;
  /** Id of one of the two source benches, for its slat composition. */
  sourceId: string;
  /** Seat height / slab thickness of the tier this piece joins — identical on
   *  both neighbours by construction. Section and 3D must build the piece from
   *  these, or it stands at the default height instead of the bench's. */
  height?: number;
  thickness?: number;
  slatThickness?: number[];
  /** A skirting under its front board, as the two neighbours'. */
  skirting?: boolean;
  /** The piece's face at `d` mm into its depth — 0 is the wall-side face.
   *  Used for concentric slat bands and for ray-casting the radial ones. */
  facePath: (d: number) => Vec2[];
}

/**
 * The rounded corner bench pieces of a room. One per (corner, tier) where the
 * corner is curved and the tier-k benches on both adjacent walls are identical;
 * a mismatched or missing tier leaves that ring void instead. The piece runs
 * from `depth` beyond one wall's arc, around the curve, to `depth` beyond the
 * other's — the extent `polyBenchBands` cuts the straight benches back to, so
 * the three meet exactly.
 */
export function polyBenchCornerBands(inst: ProductInstance): PolyBenchCornerBand[] {
  inst = wallView(inst);
  const corners = roomCornerPieces(inst);
  if (!inst.polyBenches?.some((b) => b.roundStart || b.roundEnd)) return corners;
  return [...corners, ...polyBenchRoundEnds(inst).filter((r) => r.rounded).map((r) => r.piece)];
}

/** One end of a wall bench that can be rounded (the Round bench end tool): free —
 *  no bench, partition or corner piece carries on from it — and leaving the wall
 *  at an angle the sweep can turn. `piece` is its rounded end, built whether it
 *  is rounded or not (the tool previews it); `rounded` its stored flag. */
export interface BenchRoundEnd {
  bench: string;
  side: 'start' | 'end';
  edge: number;
  tier: number;
  rounded: boolean;
  piece: PolyBenchCornerBand;
}

/** Arc steps per radian of a rounded end: every face the same count, so a
 *  profile sweeps and its slats pair point for point. */
const ROUND_STEPS_PER_RAD = 96 / Math.PI;

/**
 * Every end of a room's wall benches that can be rounded, and its rounded piece.
 * The piece pivots on the wall: on the line of the bench's end edge (slid by its
 * tuck), where it meets the wall's inner face. Every depth of the bench sweeps
 * round that point at its own distance from it — the slats become concentric
 * arcs, a profile is revolved — from the end edge on, away from the bench, until
 * it lies along the wall beyond the point: a quarter turn on a straight wall,
 * less where the wall turns into the room there. The piece is ADDED beyond the
 * end: the bench keeps its length.
 */
export function polyBenchRoundEnds(inst: ProductInstance): BenchRoundEnd[] {
  inst = wallView(inst);
  const pbs = inst.polyBenches;
  if (!pbs?.length) return [];
  const edges = polyBenchEdges(inst);
  const n = edges.length;
  const bands = polyBenchBands(inst);
  const tuck = polyBenchTuckShifts(inst);
  const slid = new Map(bands.map((b) => [b.id, shiftBandWallward(inst, b, tuck.get(b.id) ?? 0)]));
  const blockers: Array<{ id: string; poly: Vec2[] }> = [
    ...bands.map((b) => ({ id: b.id, poly: slid.get(b.id)! })),
    ...roomCornerPieces(inst).map((c) => ({ id: c.id, poly: c.poly })),
    ...polyInnerBenchBands(inst).map((b) => ({ id: b.id, poly: b.poly })),
    ...partitionClips(inst).map((poly) => ({ id: '', poly })),
  ];
  const inner = polyInnerCarved(inst);
  const out: BenchRoundEnd[] = [];
  for (const b of bands) {
    const pb = pbs.find((x) => x.id === b.id);
    const e = edges[b.edge];
    const q = slid.get(b.id)!;
    if (!pb || !e || q.length !== 4) continue;
    for (const side of ['start', 'end'] as const) {
      const A = side === 'start' ? q[0] : q[1], B = side === 'start' ? q[3] : q[2];
      const beyond = side === 'start' ? mul(e.dir, -1) : e.dir;
      const probe = add(mul(add(A, B), 0.5), mul(beyond, 5));
      if (blockers.some((o) => o.id !== b.id && o.poly.length >= 3 && pointInPolygon(probe, o.poly))) continue;
      const AB = sub(B, A), ab = len2(AB);
      if (!(ab > EPS)) continue;
      const u0 = mul(AB, 1 / ab);
      const un = u0.x * e.normal.x + u0.y * e.normal.y;
      if (un < 0.1) continue; // an end along the wall: nothing to turn
      const t = -((A.x - e.ia.x) * e.normal.x + (A.y - e.ia.y) * e.normal.y) / un;
      const P = add(A, mul(u0, t));
      // The wall beyond the pivot: its own, or the next wall's past its corner.
      const st = (P.x - e.ia.x) * e.dir.x + (P.y - e.ia.y) * e.dir.y;
      const L = (e.ib.x - e.ia.x) * e.dir.x + (e.ib.y - e.ia.y) * e.dir.y;
      const w = side === 'end' && st >= L - 1 ? edges[(b.edge + 1) % n].dir
        : side === 'start' && st <= 1 ? mul(edges[(b.edge - 1 + n) % n].dir, -1) : beyond;
      const th = Math.atan2(cross(u0, w), u0.x * w.x + u0.y * w.y);
      // It turns away from the bench, and by enough to be a round.
      if (Math.abs(th) < (5 * Math.PI) / 180 || Math.sign(th) !== Math.sign(cross(u0, beyond))) continue;
      const r0 = len2(sub(A, P)), r1 = len2(sub(B, P));
      const D = pb.depth > 0 ? pb.depth : r1 - r0;
      const r = (d: number): number => r0 + ((r1 - r0) * d) / (D || 1);
      const a0 = Math.atan2(u0.y, u0.x);
      const steps = Math.max(4, Math.ceil(Math.abs(th) * ROUND_STEPS_PER_RAD));
      const at = (rd: number, a: number): Vec2 => ({ x: P.x + rd * Math.cos(a), y: P.y + rd * Math.sin(a) });
      // How far a face sweeps: to the wall beyond, or — where another wall comes
      // first — to that wall, as the plan clips the seats to the room.
      const sweepOf = (rd: number): number => {
        if (rd < 1 || inner.length < 3) return th;
        const out = (f: number): boolean => !pointInPolygon(at(rd, a0 + th * f), inner);
        const N = steps * 4;
        for (let k = 1; k < N; k++) {
          if (!out(k / N)) continue;
          let lo = (k - 1) / N, hi = k / N;
          for (let i = 0; i < 30; i++) { const m = (lo + hi) / 2; if (out(m)) hi = m; else lo = m; }
          return th * lo;
        }
        return th;
      };
      const facePath = (d: number): Vec2[] => {
        const rd = Math.max(0, r(d));
        const sw = sweepOf(rd);
        const pts: Vec2[] = [];
        for (let k = 0; k <= steps; k++) pts.push(at(rd, a0 + (sw * k) / steps));
        return pts;
      };
      const ring = dedupeRing([...facePath(0), ...facePath(D).reverse()]);
      const key = `${b.edge}:${side}:${Math.round(P.x)},${Math.round(P.y)}`;
      out.push({
        bench: b.id,
        side,
        edge: b.edge,
        tier: b.tier,
        rounded: !!(side === 'start' ? pb.roundStart : pb.roundEnd),
        piece: {
          id: `pbr-${b.id}-${side}`,
          vertex: -1,
          roundEnd: { bench: b.id, side, key },
          tier: b.tier,
          level: b.level,
          poly: ring,
          center: P,
          rOuter: r0,
          rInner: r1,
          seatLength: Math.abs(th) * r(D / 2),
          color: pb.color,
          sourceId: b.id,
          height: pb.height,
          thickness: pb.thickness,
          slatThickness: pb.slatThickness,
          skirting: pb.skirting,
          facePath,
        },
      });
    }
  }
  return out;
}

/** A ring with its repeated consecutive points dropped (a face of radius 0
 *  is one point). */
function dedupeRing(pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (!q || Math.abs(p.x - q.x) > 1e-6 || Math.abs(p.y - q.y) > 1e-6) out.push(p);
  }
  while (out.length > 1 && Math.abs(out[0].x - out[out.length - 1].x) <= 1e-6 && Math.abs(out[0].y - out[out.length - 1].y) <= 1e-6) out.pop();
  return out;
}

/** The rounded corner pieces at the room's own curved corners. */
function roomCornerPieces(inst: ProductInstance): PolyBenchCornerBand[] {
  const g = benchGeom(inst);
  if (!g) return [];
  const { edges, n, byEdge, cumOff, innerOff, cornerList } = g;
  const corners = roomCorners(inst);
  const wallT = wallThicknessOf(inst);
  // A tucked tier slides wall-ward; its corner piece has to slide with it.
  const tuck = edgeTuckShifts(inst, g);
  const shiftOf = (edge: number, k: number): number => tuck.get(`${edge}:${k}`) ?? 0;
  const CEPS = 1;
  const at = (i: number, d: number, off: number): Vec2 =>
    add(add(edges[i].a, mul(edges[i].dir, d)), mul(edges[i].normal, wallT + off));
  const out: PolyBenchCornerBand[] = [];

  for (let v = 0; v < n; v++) {
    const c = corners[v];
    if (!c) continue;
    const p = (v - 1 + n) % n, i = v;            // edges meeting at this corner
    const lp = byEdge.get(p), li = byEdge.get(i);
    if (!lp || !li) continue;
    // Tiers are matched by position within each side's CORNER-reaching stack —
    // benches beyond a partition (which never reach the corner) don't take part.
    const pList = cornerList(p, true), iList = cornerList(i, false);
    for (let t = 0; t < Math.min(pList.length, iList.length); t++) {
      const pIdx = pList[t], iIdx = iList[t];
      if (!tiersMatchAtCorner(inst, g, i, iIdx, p, pIdx)) continue;
      const shift = shiftOf(i, iIdx);
      if (Math.abs(shiftOf(p, pIdx) - shift) > EPS) continue; // they tuck differently: no piece can join them
      const src = (inst.polyBenches || []).find((b) => b.id === li[iIdx].id);
      const oOff = cumOff(i, iIdx) - shift, iOff = innerOff(i, iIdx) - shift;
      const S = cornerPieceStation(c, wallT + oOff, wallT + iOff);
      // The piece must fit inside what both benches actually span.
      if (S > li[iIdx].end - CEPS || edges[p].len - S < lp[pIdx].start - CEPS) continue;
      // One face of the piece: straight in from `S` on edge p, around the arc (or
      // through the mitre when the radius no longer reaches this face), out to
      // `S` on edge i.
      const facePath = (off: number): Vec2[] => {
        const r = faceRadius(c, wallT + off);
        const a0 = at(p, edges[p].len - S, off);
        const a1 = at(i, S, off);
        if (r > RADIUS_EPS) {
          const e0 = at(p, edges[p].len - c.station, off);
          const e1 = at(i, c.station, off);
          return [a0, ...arcPoints(c.center, e0, e1, r), a1];
        }
        return [a0, mitreAt(at(p, 0, off), edges[p].dir, at(i, 0, off), edges[i].dir), a1];
      };
      const outer = facePath(oOff), inner = facePath(iOff);
      const mid = facePath((oOff + iOff) / 2);
      let seat = 0;
      for (let m = 1; m < mid.length; m++) seat += len2(sub(mid[m], mid[m - 1]));
      out.push({
        id: `pbc-${v}-${t}`,
        vertex: v,
        tier: t,
        level: RANK_LEVEL[benchLevelRank(t, Math.max(iList.length, pList.length))],
        poly: [...outer, ...inner.reverse()],
        center: c.center,
        rOuter: faceRadius(c, wallT + oOff),
        rInner: faceRadius(c, wallT + iOff),
        seatLength: seat,
        color: src?.color,
        sourceId: li[iIdx].id,
        height: src?.height,
        thickness: src?.thickness,
        slatThickness: src?.slatThickness,
        skirting: src?.skirting,
        facePath: (d: number) => facePath(oOff + d),
      });
    }
  }
  return out;
}

export type BenchDrawItem =
  | { kind: 'band'; tier: number; band: PolyBenchBand }
  | { kind: 'corner'; tier: number; corner: PolyBenchCornerBand };

const BENCH_DRAW_KIND: Record<BenchDrawItem['kind'], number> = { band: 0, corner: 1 };

/**
 * Plan draw order for a room's bench pieces: inner tiers first, the wall tier
 * last, so a higher bench paints over the lower one's tucked band. Rounded
 * corner pieces are interleaved by their OWN tier rather than drawn after every
 * band — a tucked lower piece belongs under the higher bench it tucks beneath,
 * exactly like the straight band it continues.
 */
export function benchDrawOrder(inst: ProductInstance): BenchDrawItem[] {
  inst = wallView(inst);
  const items: BenchDrawItem[] = [
    ...polyBenchBands(inst).map((band): BenchDrawItem => ({ kind: 'band', tier: band.tier, band })),
    ...polyBenchCornerBands(inst).map((corner): BenchDrawItem => ({ kind: 'corner', tier: corner.tier, corner })),
  ];
  return items.sort((a, b) => b.tier - a.tier || BENCH_DRAW_KIND[a.kind] - BENCH_DRAW_KIND[b.kind]);
}

/**
 * The even radial slat layout for a corner piece: `n` identical wedges separated
 * by `n + 1` identical gaps, measured along an arc of radius `rRef` (the piece's
 * widest, so no wedge anywhere exceeds `maxW`).
 *
 * The straight bench's own composition only sets the BOUNDS — its widest slat is
 * the cap and its widest gap the spacing — because repeating a varied pattern
 * around an arc cannot come out even: the pattern ends on a slat, so every repeat
 * butts the next one gaplessly and the widths land wherever the arc leaves them.
 * `n` is the fewest wedges that keeps each one within the cap, which is also the
 * widest they can be.
 */
export function radialCornerSlatLayout(
  arcLen: number, maxW: number, gap: number,
): { n: number; width: number; gap: number } | null {
  if (!(arcLen > 0) || !(maxW > 0)) return null;
  const g = Math.max(0, Math.min(gap, arcLen / 4)); // a gap can never eat the piece
  const n = Math.max(1, Math.ceil((arcLen - g) / (maxW + g)));
  const width = (arcLen - (n + 1) * g) / n;
  // `n` is already the fewest wedges that fit the cap, so `width` is the widest an
  // even split allows; falling under the floor means the piece is too small to
  // slat at all, not that a different `n` would do better.
  return width >= MIN_RADIAL_SLAT ? { n, width, gap: g } : null;
}

/** Below this a radial wedge stops reading as a slat, so the piece is filled plain. */
const MIN_RADIAL_SLAT = 10;

/**
 * Slat quads inside a rounded corner piece. Concentric (the default) continues
 * the straight bench's slats as arc bands at the same depths, so the pattern
 * reads through the curve unbroken. Radial fans them out from the arc centre:
 * evenly spaced identical wedges (see {@link radialCornerSlatLayout}) met against
 * the piece's two faces, so they cover the straight shoulders as well as the arc.
 * Empty when the bench has no slats, or the piece is too small to slat at all —
 * either way it is drawn as a plain filled piece.
 */
export function polyBenchCornerSlatQuads(inst: ProductInstance, c: PolyBenchCornerBand): Vec2[][] {
  inst = wallView(inst);
  const src = (inst.polyBenches || []).find((b) => b.id === c.sourceId);
  const spans = benchSlatSpans({ slats: src?.slats, gaps: src?.gaps });
  if (!spans.length) return [];
  const radial = inst.polygonCornerSlats?.[c.vertex] === 'radial';
  const depth = Math.abs(c.rOuter - c.rInner); // the ring's depth, whichever way it curves
  if (!radial) {
    return spans
      .map((s) => dedupeRing([...c.facePath(s.off), ...[...c.facePath(s.off + s.width)].reverse()]))
      .filter((q) => q.length >= 3);
  }
  const outer = c.facePath(0), inner = c.facePath(Math.max(0, depth));
  const ang = (p: Vec2): number => Math.atan2(p.y - c.center.y, p.x - c.center.x);
  let sweep = ang(outer[outer.length - 1]) - ang(outer[0]);
  while (sweep <= -Math.PI) sweep += 2 * Math.PI;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  // Size the wedges on the piece's widest arc, so `maxW` caps them everywhere:
  // the outer face at a convex corner, the inner one at a reflex corner.
  const widest = c.rInner > c.rOuter ? inner : outer;
  const rRef = Math.max(...widest.map((p) => len2(sub(p, c.center))));
  const lay = radialCornerSlatLayout(
    Math.abs(sweep) * rRef,
    Math.max(...(src?.slats ?? []).map((w) => Math.max(0, w))),
    Math.max(0, ...(src?.gaps ?? []).map((g) => Math.max(0, g))),
  );
  if (!lay) return [];
  const perMm = sweep / (Math.abs(sweep) * rRef); // signed radians per mm of arc
  const a0 = ang(outer[0]);
  const hit = (path: Vec2[], a: number): Vec2 => rayHitPolyline(c.center, a, path) ?? path[0];
  const out: Vec2[][] = [];
  for (let i = 0; i < lay.n; i++) {
    const s = lay.gap + i * (lay.width + lay.gap);
    const b0 = a0 + perMm * s, b1 = a0 + perMm * (s + lay.width);
    out.push([hit(outer, b0), hit(outer, b1), hit(inner, b1), hit(inner, b0)]);
  }
  return out;
}

/** Where the ray from `o` at angle `a` first meets a polyline, or null. */
function rayHitPolyline(o: Vec2, a: number, path: Vec2[]): Vec2 | null {
  const d = { x: Math.cos(a), y: Math.sin(a) };
  for (let i = 1; i < path.length; i++) {
    const p = path[i - 1], q = path[i];
    const s = sub(q, p);
    const den = d.x * s.y - d.y * s.x;
    if (Math.abs(den) < 1e-9) continue;
    const diff = sub(p, o);
    const t = (diff.x * s.y - diff.y * s.x) / den;
    const u = (diff.x * d.y - diff.y * d.x) / den;
    if (t >= -1e-6 && u >= -1e-6 && u <= 1 + 1e-6) return { x: o.x + d.x * t, y: o.y + d.y * t };
  }
  return null;
}

/**
 * Cumulative wall-ward tuck (mm) per bench band id: a lower tier on an edge runs
 * UNDER the higher one (toward the wall) by its overlap, but only when that higher
 * bench has a `thickness` set (a void beneath its slab). Mirrors the rectangular
 * room's benchStackShifts so plan, section and 3D agree.
 */
export function polyBenchTuckShifts(inst: ProductInstance): Map<string, number> {
  inst = wallView(inst);
  const g = benchGeom(inst);
  const out = new Map<string, number>();
  if (!g) return out;
  const shifts = edgeTuckShifts(inst, g); // keyed by `${edge}:${tier}`
  for (const [i, list] of g.byEdge) {
    for (let k = 0; k < list.length; k++) out.set(list[k].id, shifts.get(`${i}:${k}`) ?? 0);
  }
  return out;
}

/** Translate a bench band's local-frame polygon wall-ward (toward its edge's wall)
 *  by `shift` mm — the overlap tuck, for the section and 3D views. */
export function shiftBandWallward(inst: ProductInstance, band: PolyBenchBand, shift: number): Vec2[] {
  inst = wallView(inst);
  if (shift <= 0) return band.poly;
  const e = polyBenchEdges(inst)[band.edge];
  if (!e) return band.poly;
  return band.poly.map((p) => ({ x: p.x - e.normal.x * shift, y: p.y - e.normal.y * shift }));
}

/**
 * Per-bench seat length (mm) for a polygonal room: for each bench band the
 * shorter of its two long sides — the wall-side edge (start-outer→end-outer) and
 * the room-side edge (start-inner→end-inner) — which differ when the ends are
 * mitred at a corner. Tiny slivers are dropped.
 */
export function polyBenchSeatLengths(inst: ProductInstance): number[] {
  inst = wallView(inst);
  const out: number[] = [];
  for (const b of polyBenchBands(inst)) {
    const [so, eo, ei, si] = b.poly; // start-outer, end-outer, end-inner, start-inner
    const outer = Math.hypot(eo.x - so.x, eo.y - so.y);
    const inner = Math.hypot(ei.x - si.x, ei.y - si.y);
    const len = Math.min(outer, inner);
    if (len > 1) out.push(len);
  }
  // A rounded corner piece is real, sittable bench: it contributes its seat
  // centreline through the curve, joining the two straight runs it was cut into.
  for (const c of polyBenchCornerBands(inst)) if (c.seatLength > 1) out.push(c.seatLength);
  return out;
}

/** Total seating length (mm) of a polygonal room's benches: the sum of each
 *  bench's shorter long side. */
export function polyBenchSeatingLength(inst: ProductInstance): number {
  inst = wallView(inst);
  return polyBenchSeatLengths(inst).reduce((s, l) => s + l, 0);
}

/** Capacity of a polygonal room: one person per 60 cm of each bench run. */
export function polyBenchCapacity(inst: ProductInstance): number {
  inst = wallView(inst);
  return polyBenchSeatLengths(inst).reduce((c, l) => c + Math.floor(l / 600), 0);
}

/** Editable handles of a selected polygon-room bench (local frame): the two end
 *  faces, the inner face, and the four corners. */
export interface BenchHandle {
  /** What dragging this handle edits. */
  kind: 'start' | 'end' | 'inner' | 'corner-start-outer' | 'corner-start-inner' | 'corner-end-outer' | 'corner-end-inner';
  p: Vec2;
}

export function polyBenchHandles(inst: ProductInstance, benchId: string): BenchHandle[] {
  inst = wallView(inst);
  const band = polyBenchBands(inst).find((b) => b.id === benchId);
  if (!band) return [];
  // The bench is drawn slid wall-ward by its overlap tuck, so the handles must
  // follow that shift to sit on the drawn bench, not the un-tucked band.
  const shift = polyBenchTuckShifts(inst).get(benchId) ?? 0;
  const [so, eo, ei, si] = shiftBandWallward(inst, band, shift); // start-outer, end-outer, end-inner, start-inner
  const mid = (a: Vec2, b: Vec2): Vec2 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  return [
    { kind: 'start', p: mid(so, si) },
    { kind: 'end', p: mid(eo, ei) },
    { kind: 'inner', p: mid(si, ei) },
    { kind: 'corner-start-outer', p: so },
    { kind: 'corner-start-inner', p: si },
    { kind: 'corner-end-outer', p: eo },
    { kind: 'corner-end-inner', p: ei },
  ];
}

/** Guide vertices for bench editing in a polygonal room (local frame): the inner
 *  room corners plus every other bench's corners. */
export function polyBenchGuideVerts(inst: ProductInstance, excludeBenchId: string): Vec2[] {
  inst = wallView(inst);
  const out: Vec2[] = polyInner(inst).map((p) => ({ x: p.x, y: p.y }));
  for (const b of polyBenchBands(inst)) {
    if (b.id === excludeBenchId) continue;
    for (const p of b.poly) out.push({ x: p.x, y: p.y });
  }
  return out;
}

/** The edit frame of a polygon bench: its edge direction/normal and stack offsets,
 *  for translating handle drags into span/depth updates. */
export function polyBenchFrame(inst: ProductInstance, benchId: string): { origin: Vec2; dir: Vec2; normal: Vec2; oOff: number; iOff: number; len: number; shift: number } | null {
  inst = wallView(inst);
  const g = benchGeom(inst);
  if (!g) return null;
  for (const [i, list] of g.byEdge) {
    const k = list.findIndex((t) => t.id === benchId);
    if (k < 0) continue;
    const e = g.edges[i];
    // The overlap tuck slides the drawn band (and its handles) wall-ward, so the
    // depth drag adds it back to read the true depth from the tucked handle.
    const shift = edgeTuckShifts(inst, g).get(`${i}:${k}`) ?? 0;
    return { origin: e.a, dir: e.dir, normal: e.normal, oOff: g.cumOff(i, k), iOff: g.innerOff(i, k), len: e.len, shift };
  }
  return null;
}

/** How far back along a bench (mm) a corner-join highlight/hit region reaches. */
const BENCH_JOINT_REACH = 320;

export interface PolyBenchJoint {
  /** Shared vertex index: the corner between edges corner-1 and corner. */
  corner: number;
  /** Tier level (index) at which the two benches meet. */
  tier: number;
}

/** The end-cap quad of a bench band near shared vertex `vertexM` (the region
 *  highlighted at a corner join), reaching `BENCH_JOINT_REACH` mm back. */
function benchEndCap(band: PolyBenchBand, vertexM: number, edges: PolyEdge[]): Vec2[] {
  const dir = edges[band.edge].dir;
  const isLeft = vertexM === band.edge; // left end sits at the band's own start vertex
  const cOuter = isLeft ? band.poly[0] : band.poly[1];
  const cInner = isLeft ? band.poly[3] : band.poly[2];
  const s = isLeft ? BENCH_JOINT_REACH : -BENCH_JOINT_REACH;
  const capOuter = add(cOuter, mul(dir, s));
  const capInner = add(cInner, mul(dir, s));
  return [cOuter, capOuter, capInner, cInner];
}

/**
 * The bench corner join under a local point, or null. A join exists where two
 * benches on adjacent edges meet at the same tier level; the point must fall in
 * the near-corner end region of the hovered bench (the region that pulses green),
 * so clicking the highlight always swaps.
 */
export function polyBenchJointAt(inst: ProductInstance, p: Vec2): PolyBenchJoint | null {
  inst = wallView(inst);
  const pick = polyBenchPick(inst, p);
  if (!pick) return null;
  const edges = polyBenchEdges(inst);
  const n = edges.length;
  const band = pick.band;
  const i = band.edge, k = band.tier;
  const bands = polyBenchBands(inst);
  const CEPS = 1;
  // A join needs BOTH benches to actually reach the shared corner — a bench held
  // back by a partition (or a dragged end) never joins there.
  const hasAdj = (adj: number, atEndAdj: boolean): boolean =>
    bands.some((b) => b.edge === adj && b.tier === k && (atEndAdj ? b.end >= edges[adj].len - CEPS : b.start <= CEPS));
  const leftV = i, rightV = (i + 1) % n;
  const leftAdj = (i - 1 + n) % n, rightAdj = (i + 1) % n;
  if (band.start <= CEPS && hasAdj(leftAdj, true) && pointInPolygon(p, benchEndCap(band, leftV, edges))) return { corner: leftV, tier: k };
  if (band.end >= edges[i].len - CEPS && hasAdj(rightAdj, false) && pointInPolygon(p, benchEndCap(band, rightV, edges))) return { corner: rightV, tier: k };
  return null;
}

/** Local-frame highlight polygons (both benches' ends) for a corner join. */
export function polyBenchJointHighlight(inst: ProductInstance, corner: number, tier: number): Vec2[][] {
  inst = wallView(inst);
  const edges = polyBenchEdges(inst);
  const n = edges.length;
  if (!n) return [];
  const eA = (corner - 1 + n) % n, eB = corner;
  const bands = polyBenchBandsDrawn(inst);
  const CEPS = 1;
  const polys: Vec2[][] = [];
  const a = bands.find((b) => b.edge === eA && b.tier === tier && b.end >= edges[eA].len - CEPS);
  const b = bands.find((b) => b.edge === eB && b.tier === tier && b.start <= CEPS);
  if (a) polys.push(benchEndCap(a, corner, edges));
  if (b) polys.push(benchEndCap(b, corner, edges));
  return polys;
}

/** Default bench depth (mm) for a polygonal room, by category. */
export function polyBenchDepth(inst: ProductInstance): number {
  inst = wallView(inst);
  return inst.category === 'steamroom' ? 500 : 600;
}

/** The bench band id under a local point, or null (topmost first). */
export function polyBenchHit(inst: ProductInstance, p: Vec2): string | null {
  inst = wallView(inst);
  return polyBenchPick(inst, p)?.band.id ?? null;
}

export interface PolyBenchPick {
  /** The hit band. */
  band: PolyBenchBand;
  /** Total tiers stacked on the band's edge. */
  tiers: number;
  /** True when the click is nearer the band's inner lip than its outer face. */
  innerHalf: boolean;
}

/** A room's wall benches as the plan DRAWS them: each band (`poly`, `lip`) slid
 *  wall-ward by its tuck. What a hover outlines, a press picks and a joint
 *  pulses on — the stored band is where the bench would stand untucked. */
export function polyBenchBandsDrawn(inst: ProductInstance): PolyBenchBand[] {
  inst = wallView(inst);
  const tuck = polyBenchTuckShifts(inst);
  return polyBenchBands(inst).map((b) => {
    const s = tuck.get(b.id) ?? 0;
    if (!(s > 0)) return b;
    const [l0, l1] = shiftBandWallward(inst, { ...b, poly: b.lip }, s);
    return { ...b, poly: shiftBandWallward(inst, b, s), lip: [l0, l1] };
  });
}

/**
 * The bench band under a local point with stacking context, or null: the one
 * DRAWN there on top — `benchDrawOrder` paints the wall tier last, so a tier's
 * tucked back, under the tier behind it, picks that one. Its `band` is the
 * drawn one (`polyBenchBandsDrawn`).
 */
export function polyBenchPick(inst: ProductInstance, p: Vec2): PolyBenchPick | null {
  inst = wallView(inst);
  const bands = polyBenchBandsDrawn(inst);
  let hit: PolyBenchBand | null = null;
  for (let i = bands.length - 1; i >= 0; i--) {
    if ((!hit || bands[i].tier < hit.tier) && pointInPolygon(p, bands[i].poly)) hit = bands[i];
  }
  if (!hit) return null;
  const tiers = bands.reduce((c, b) => (b.edge === hit!.edge ? c + 1 : c), 0);
  const dOuter = distToSeg(p, hit.poly[0], hit.poly[1]);
  const dInner = distToSeg(p, hit.poly[2], hit.poly[3]);
  return { band: hit, tiers, innerHalf: dInner <= dOuter };
}

/** Distance from point p to segment a→b. */
function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const ab = sub(b, a);
  const denom = ab.x * ab.x + ab.y * ab.y || 1;
  const t = clamp(((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / denom, 0, 1);
  return len2(sub(p, { x: a.x + t * ab.x, y: a.y + t * ab.y }));
}

